import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_ARTWORK = '/default-artwork.svg';

// Try station art next, then the default image; never leave the box empty
const handleArtError = (e, fallbackArt) => {
  const img = e.currentTarget;
  if (fallbackArt && img.dataset.triedFallback !== '1') {
    img.dataset.triedFallback = '1';
    if (img.getAttribute('src') !== fallbackArt) {
      img.src = fallbackArt;
      return;
    }
  }
  if (!img.src.endsWith(DEFAULT_ARTWORK)) img.src = DEFAULT_ARTWORK;
};


// Auth Context
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', response.data.access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, role) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, role });
    localStorage.setItem('token', response.data.access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, refetchUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Protected Route
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (requireAdmin && user.role !== 'admin') {
    return <Navigate to="/" />;
  }

  return children;
};

// How It Works Page
const HowItWorks = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="how-it-works-page">
      <div className="hero-banner">
        <div className="animated-bg">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        <div className="hero-content">
          <h1 className="brand-title">Jukebox</h1>
          <p className="brand-tagline">Discover Music. Support Artists. Build Your Collection.</p>
          
          {!user && (
            <div className="hero-buttons">
              <button className="cta-btn" onClick={() => navigate('/login')}>Get Started</button>
              <button className="secondary-btn" onClick={() => navigate('/songs')}>Browse Songs</button>
            </div>
          )}
        </div>
      </div>

      {/* How It Works Section */}
      <div className="features-section">
        <h2>How It Works</h2>
        <div className="feature-grid">
          <div className="feature-card">
            <div className="feature-icon">🎵</div>
            <h3>Listen to Radio</h3>
            <p>Listen to and discover new independent music on AMS Radio</p>
          </div>
          
          <div className="feature-arrow">→</div>
          
          <div className="feature-card">
            <div className="feature-icon">🎫</div>
            <h3>Buy Tokens & Stars</h3>
            <p>Purchase tokens to collect songs and stars to "show love" for songs you like and want to bookmark for later</p>
          </div>
          
          <div className="feature-arrow">→</div>
          
          <div className="feature-card">
            <div className="feature-icon">📻</div>
            <h3>Build Your Jukebox</h3>
            <p>Own, download, and enjoy your favorite tracks forever</p>
          </div>
        </div>
      </div>
    </div>
  );
};


// Channel Page (single channel - Mother Ship)
const ChannelPage = () => {
  const navigate = useNavigate();
  const { channelId } = useParams();
  const { user, refetchUser } = useAuth();
  const [nowPlaying, setNowPlaying] = useState(null);
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'star' | 'token', songId, song }
  
  // Feedback survey state
  const [listeningMinutes, setListeningMinutes] = useState(0);
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackData, setFeedbackData] = useState({ rating: null, comment: '', favoriteGenre: '' });
  const [lastFeedbackTime, setLastFeedbackTime] = useState(0);

  // Channel configuration (single channel)
  const channelConfigs = {
    'the-mother-ship': {
      name: '🚀 Mother Ship',
      subtitle: 'Independent Music • 24/7',
      live365Embed: 'https://live365.com/embeds/v1/player/a15527?s=md&m=dark&c=mp3',
      live365Stream: 'https://streaming.live365.com/a15527',
      live365RecentlyPlayed: null, // Uses AzuraCast API instead
      apiChannel: 'the-mother-ship',
      requiresLogin: false,
      hasSchedule: true,
      hasJukebox: true,
      calendarUrl: 'https://calendar.google.com/calendar/embed?src=ab288f7492ab7080e742185ef50767c20dd4f99109233ca89604ec300d37f8ae%40group.calendar.google.com&ctz=America%2FLos_Angeles&mode=AGENDA&showTitle=0&showNav=0&showDate=0&showPrint=0&showTabs=0&showCalendars=0&showTz=0&height=400',
      poweredBy: 'Live365'
    },
    // Keep old route working for backwards compatibility
    'ams-radio': {
      name: '🚀 Mother Ship',
      subtitle: 'Independent Music • 24/7',
      live365Embed: 'https://live365.com/embeds/v1/player/a15527?s=md&m=dark&c=mp3',
      live365Stream: 'https://streaming.live365.com/a15527',
      live365RecentlyPlayed: null, // Uses AzuraCast API instead
      apiChannel: 'the-mother-ship',
      requiresLogin: false,
      hasSchedule: true,
      hasJukebox: true,
      calendarUrl: 'https://calendar.google.com/calendar/embed?src=ab288f7492ab7080e742185ef50767c20dd4f99109233ca89604ec300d37f8ae%40group.calendar.google.com&ctz=America%2FLos_Angeles&mode=AGENDA&showTitle=0&showNav=0&showDate=0&showPrint=0&showTabs=0&showCalendars=0&showTz=0&height=400',
      poweredBy: 'Live365'
    }
  };

  const currentChannel = channelConfigs[channelId] || channelConfigs['the-mother-ship'];

  const fetchNowPlaying = async () => {
    // Skip API call if channel doesn't have AzuraCast API
    if (!currentChannel.apiChannel) return;
    
    try {
      const response = await axios.get(`${API}/azurecast/now-playing?channel=${currentChannel.apiChannel}`);
      if (response.data.success) {
        setNowPlaying(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch now playing');
    }
  };

  const fetchRecentlyPlayed = async () => {
    // Skip API call if channel doesn't have AzuraCast API
    if (!currentChannel.apiChannel) return;
    
    try {
      const response = await axios.get(`${API}/azurecast/recently-played?channel=${currentChannel.apiChannel}`);
      if (response.data.success) {
        setRecentlyPlayed(response.data.recently_played);
      }
    } catch (error) {
      console.error('Failed to fetch recently played');
    }
  };

  // Protect channels that require login
  useEffect(() => {
    if (currentChannel.requiresLogin && !user) {
      toast.error('Please log in to access this channel');
      navigate('/login');
    }
  }, [currentChannel.requiresLogin, user, navigate]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, [channelId]);

  useEffect(() => {
    // Poll now playing and recently played from Azurecast every 10 seconds
    // Only if channel has API and (doesn't require login OR user is logged in)
    if (!currentChannel.apiChannel) return;
    if (currentChannel.requiresLogin && !user) return;
    
    fetchNowPlaying();
    fetchRecentlyPlayed();
    const interval = setInterval(() => {
      fetchNowPlaying();
      fetchRecentlyPlayed();
    }, 10000);
    return () => clearInterval(interval);
  }, [channelId, user]);

  // Listening heartbeat for loyalty points tracking (Phase 0)
  useEffect(() => {
    let heartbeatInterval = null;
    
    if (isPlaying && user) {
      // Send heartbeat every 60 seconds while playing
      heartbeatInterval = setInterval(async () => {
        try {
          await axios.post(`${API}/user/track-activity`, {
            activity_type: 'listening_minutes',
            value: 1,  // 1 minute
            metadata: { channel: channelId }
          });
        } catch (error) {
          // Silently fail - don't interrupt listening experience
          console.debug('Heartbeat tracking failed:', error);
        }
      }, 60000); // Every 60 seconds
    }
    
    return () => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    };
  }, [isPlaying, user, channelId]);

  // Feedback prompt timer - show prompt every 20 minutes of listening
  useEffect(() => {
    let minuteTimer = null;
    
    if (isPlaying && user) {
      minuteTimer = setInterval(() => {
        setListeningMinutes(prev => {
          const newMinutes = prev + 1;
          // Check if 20 minutes have passed since last feedback prompt
          if (newMinutes - lastFeedbackTime >= 20 && !showFeedbackPrompt && !showFeedbackModal) {
            setShowFeedbackPrompt(true);
          }
          return newMinutes;
        });
      }, 60000); // Every 60 seconds
    }
    
    return () => {
      if (minuteTimer) {
        clearInterval(minuteTimer);
      }
    };
  }, [isPlaying, user, lastFeedbackTime, showFeedbackPrompt, showFeedbackModal]);

  const handleFeedbackSubmit = async () => {
    if (!feedbackData.rating) {
      toast.error('Please select a rating');
      return;
    }
    
    try {
      // Track feedback for loyalty points
      await axios.post(`${API}/user/track-activity`, {
        activity_type: 'feedback_submitted',
        value: 1,
        metadata: {
          channel: channelId,
          song_title: nowPlaying?.title || 'Unknown',
          song_artist: nowPlaying?.artist || 'Unknown',
          rating: feedbackData.rating,
          comment: feedbackData.comment,
          favorite_genre: feedbackData.favoriteGenre
        }
      });
      
      toast.success('Thanks for your feedback! Points earned! 🎉');
      setShowFeedbackModal(false);
      setFeedbackData({ rating: null, comment: '', favoriteGenre: '' });
      setLastFeedbackTime(listeningMinutes);
      refetchUser();
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  const dismissFeedbackPrompt = () => {
    setShowFeedbackPrompt(false);
    setLastFeedbackTime(listeningMinutes); // Reset timer so it doesn't immediately pop up again
  };

  // If channel requires login and user isn't logged in, don't render
  if (currentChannel.requiresLogin && !user) {
    return null;
  }

  const togglePlay = () => {
    if (audioRef) {
      if (isPlaying) {
        audioRef.pause();
        setIsPlaying(false);
      } else {
        audioRef.play();
        setIsPlaying(true);
      }
    }
  };

  const handleAddSong = (songId, song) => {
    if (!user) {
      toast.info('Please log in to add songs to your jukebox');
      navigate('/login');
      return;
    }
    // Show confirmation popup
    setConfirmAction({ type: 'token', songId, song });
  };

  const handleStarSong = (songId, song) => {
    if (!user) {
      toast.info('Please log in to show love for songs');
      navigate('/login');
      return;
    }
    // Show confirmation popup
    setConfirmAction({ type: 'star', songId, song });
  };

  const confirmAddSong = async () => {
    try {
      await axios.post(`${API}/user/purchase-song`, { song_id: confirmAction.songId });
      toast.success('🎵 Song added to your Jukebox!');
      refetchUser(); // Update token balance
      setConfirmAction(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add song');
    }
  };

  const confirmStarSong = async () => {
    try {
      await axios.post(`${API}/songs/${confirmAction.songId}/star`);
      toast.success('⭐ Showed love for this song!');
      refetchUser(); // Update star balance
      setConfirmAction(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to star song');
    }
  };

  return (
    <div className="channel-page">
      {/* Mobile Player - Minimal (play button + Powered by) */}
      <div className="radio-player radio-player-mobile custom-audio-player">
        <audio 
          ref={(audio) => setAudioRef(audio)}
          src={currentChannel.live365Stream}
          preload="none"
        />
        
        <div className="custom-player-content">
          <button 
            className="play-button-large" 
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? '⏸' : '▶️'}
          </button>
          
          {currentChannel.poweredBy && (
            <p className="powered-by-mobile">Powered by {currentChannel.poweredBy}</p>
          )}
        </div>
      </div>

      {/* Now Playing Card - only show for channels with API */}
      {currentChannel.apiChannel && nowPlaying && (
        <div className="now-playing-card">
          <div className="now-playing-art">
            <img
              key={`${nowPlaying.title}-${nowPlaying.artist}`}
              data-testid="now-playing-artwork"
              src={nowPlaying.jukebox_song?.album_art_url || nowPlaying.art || DEFAULT_ARTWORK}
              alt={`${nowPlaying.title} artwork`}
              onError={(e) => handleArtError(e, nowPlaying.art)}
            />
          </div>
          <div className="now-playing-info">
            <div className="now-playing-label">Now Playing</div>
            <div className="now-playing-title">{nowPlaying.title}</div>
            <div className="now-playing-artist">{nowPlaying.artist}</div>
            {nowPlaying.album && <div className="now-playing-album">{nowPlaying.album}</div>}
          </div>
        </div>
      )}
      {currentChannel.apiChannel && nowPlaying && (
        <p style={{ 
          color: '#888', 
          fontSize: '0.75rem', 
          textAlign: 'center', 
          margin: '0.5rem 0 1rem 0',
          fontStyle: 'italic'
        }}>
          ℹ️ Metadata will update ~1 min before the current song ends
        </p>
      )}

      {/* Jukebox Available Banner - only show for channels with Jukebox */}
      {currentChannel.hasJukebox && nowPlaying && nowPlaying.available_in_jukebox && nowPlaying.jukebox_song && (
        <div className="jukebox-available-banner">
          <div className="banner-content">
            <div className="banner-text">
              <h3>🎵 This track is available in Jukebox!</h3>
              <p className="banner-subtitle">"{nowPlaying.jukebox_song.title}" by {nowPlaying.jukebox_song.artist}</p>
            </div>
            <div className="banner-actions">
              <button 
                className="banner-action-btn star-btn"
                onClick={() => handleStarSong(nowPlaying.jukebox_song.id, nowPlaying.jukebox_song)}
                title="Show Love (1 star)"
              >
                <span className="action-icon">⭐</span>
                <span className="action-text">Show Love</span>
              </button>
              <button 
                className="banner-action-btn token-btn"
                onClick={() => handleAddSong(nowPlaying.jukebox_song.id, nowPlaying.jukebox_song)}
                title={`Add to your Jukebox (${nowPlaying.jukebox_song.token_cost} token)`}
              >
                <span className="action-icon">🪙</span>
                <span className="action-text">Add to Jukebox</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Radio Player - Minimal */}
      <div className="radio-section">
        {/* Desktop Player - Simple play button + Powered by */}
        <div className="radio-player radio-player-desktop">
          <div className="custom-audio-player desktop-custom-player" style={{ 
            padding: '2rem', 
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.98) 100%)',
            borderRadius: '16px',
            maxWidth: '360px',
            margin: '0 auto'
          }}>
            <audio 
              ref={(audio) => setAudioRef(audio)}
              src={currentChannel.live365Stream}
              preload="none"
            />
            
            {/* Play Button */}
            <button 
              className="play-button-large" 
              onClick={togglePlay}
              aria-label={isPlaying ? "Pause" : "Play"}
              data-testid="desktop-play-button"
              style={{ 
                fontSize: '2.5rem', 
                padding: '1rem 3rem', 
                cursor: 'pointer',
                background: isPlaying ? 'rgba(239, 68, 68, 0.2)' : 'rgba(139, 92, 246, 0.2)',
                border: `2px solid ${isPlaying ? '#ef4444' : '#8b5cf6'}`,
                borderRadius: '50px',
                color: isPlaying ? '#ef4444' : '#8b5cf6',
                transition: 'all 0.3s ease'
              }}
            >
              {isPlaying ? '⏸ Pause' : '▶️ Play'}
            </button>
            
            {/* Powered by branding */}
            <p style={{ 
              color: '#64748b', 
              fontSize: '0.75rem', 
              marginTop: '1.25rem'
            }}>
              Powered by {currentChannel.poweredBy}
            </p>
          </div>
        </div>
      </div>

      {/* Recently Played */}
      <div className="recently-played-section">
        <h2>Recently Played</h2>
        
        {/* For channels with Live365 Recently Played iframe */}
        {currentChannel.live365RecentlyPlayed && (
          <>
            {/* Desktop Recently Played - Live365 iframe */}
            <div className="recently-played-widget recently-played-desktop">
              <iframe 
                title="Recently Played Desktop"
                src={currentChannel.live365RecentlyPlayed}
                width="450" 
                height="511"
                frameBorder="0"
                style={{ maxWidth: '100%', margin: '0 auto', display: 'block' }}
              />
            </div>

            {/* Mobile Recently Played - Live365 iframe (scaled) */}
            <div className="recently-played-widget recently-played-mobile">
              <iframe 
                title="Recently Played Mobile"
                src={currentChannel.live365RecentlyPlayed}
                width="100%" 
                height="400"
                frameBorder="0"
                style={{ maxWidth: '100%', margin: '0 auto', display: 'block' }}
              />
            </div>
          </>
        )}

        {/* For channels with AzuraCast API (custom component) */}
        {currentChannel.apiChannel && !currentChannel.live365RecentlyPlayed && (
          <div className="recently-played-widget recently-played-mobile custom-recently-played">
            {recentlyPlayed.length > 0 ? (
              <div className="recently-played-list">
                {recentlyPlayed.map((track, index) => (
                  <div key={track.id || `${track.title}-${track.artist}-${index}`} className="recent-track-card">
                    <div className="recent-track-info">
                      <h4>{track.title}</h4>
                      <p className="recent-track-artist">{track.artist}</p>
                      {currentChannel.hasJukebox && track.available_in_jukebox && track.jukebox_song && (
                        <div className="jukebox-badge-inline">
                          <span className="badge-text">🎵 In Jukebox</span>
                          <button 
                            className="quick-add-btn"
                            onClick={() => handleAddSong(track.jukebox_song.id, track.jukebox_song)}
                          >
                            Add for {track.jukebox_song.token_cost} 🎫
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-history">Loading recently played tracks...</p>
            )}
          </div>
        )}
      </div>

      {/* Recently Played Jukebox Songs - only for channels with Jukebox */}
      {currentChannel.hasJukebox && (
        <div className="recently-played-section jukebox-history-section">
          <h2>🎵 Recently Played Jukebox Songs</h2>
          <p className="section-subtitle">Support these artists by adding their songs to your collection!</p>
          
          <div className="jukebox-history-widget">
            {recentlyPlayed.filter(track => track.available_in_jukebox).length > 0 ? (
              <div className="jukebox-history-list">
                {recentlyPlayed
                  .filter(track => track.available_in_jukebox && track.jukebox_song)
                  .slice(0, 3)
                  .map((track, index) => (
                    <div key={track.id || `${track.title}-${track.artist}-${index}`} className="jukebox-history-card">
                      <div className="jukebox-history-art">
                        <img
                          src={track.jukebox_song?.album_art_url || track.art || DEFAULT_ARTWORK}
                          alt={track.title}
                          onError={(e) => handleArtError(e, track.art)}
                        />
                      </div>
                      <div className="jukebox-history-info">
                        <h4>{track.title}</h4>
                        <p className="jukebox-history-artist">{track.artist}</p>
                        {track.jukebox_song.album && (
                          <p className="jukebox-history-album">{track.jukebox_song.album}</p>
                        )}
                        <div className="jukebox-history-actions">
                          <button 
                            className="jukebox-add-btn"
                            onClick={() => handleAddSong(track.jukebox_song.id, track.jukebox_song)}
                          >
                            🎫 Add for {track.jukebox_song.token_cost} token
                          </button>
                          <button 
                            className="jukebox-star-btn"
                            onClick={() => handleStarSong(track.jukebox_song.id, track.jukebox_song)}
                          >
                            ⭐ Show Love
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="no-history">No Jukebox songs played recently. Check back soon!</p>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="modal-overlay" onClick={() => setConfirmAction(null)}>
          <div className="modal-content jukebox-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{confirmAction.type === 'star' ? 'Show Love?' : 'Add to Jukebox?'}</h3>
            <div className="confirm-display">
              <span className="confirm-icon">
                {confirmAction.type === 'star' ? '⭐' : '🪙'}
              </span>
              <span className="confirm-amount">x1</span>
            </div>
            {confirmAction.song && (
              <p className="confirm-song-info">
                <strong>{confirmAction.song.title}</strong><br/>
                {confirmAction.song.artist}
              </p>
            )}
            <div className="modal-actions">
              <button 
                className="cta-btn"
                onClick={() => {
                  if (confirmAction.type === 'star') {
                    confirmStarSong();
                  } else {
                    confirmAddSong();
                  }
                }}
              >
                ✓ Yes
              </button>
              <button 
                className="secondary-btn"
                onClick={() => setConfirmAction(null)}
              >
                ✗ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Show Schedule - Embedded Calendar */}
      {currentChannel.calendarUrl && (
        <div className="calendar-section">
          <h2 style={{ textAlign: 'center' }}>Schedule</h2>
          <div className="calendar-embed">
            <iframe 
              src={currentChannel.calendarUrl}
              style={{ border: 0, width: '100%', height: '400px', borderRadius: '10px' }}
              frameBorder="0"
              scrolling="no"
              title={`${currentChannel.name} Schedule`}
            />
          </div>
        </div>
      )}

      {/* Subtle Feedback Prompt Banner */}
      {showFeedbackPrompt && user && (
        <div 
          data-testid="feedback-prompt-banner"
          style={{
            position: 'fixed',
            bottom: '60px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.95) 0%, rgba(59, 130, 246, 0.95) 100%)',
            borderRadius: '12px',
            padding: '0.75rem 1.25rem',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            zIndex: 1001,
            maxWidth: '90%',
            width: '420px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '1rem',
            animation: 'slideUp 0.3s ease-out'
          }}
        >
          <p style={{ 
            color: '#fff', 
            margin: 0, 
            fontSize: '0.85rem',
            lineHeight: '1.3',
            flex: 1
          }}>
            🎧 20 min listening! Earn points with feedback?
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <button
              onClick={dismissFeedbackPrompt}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '8px',
                padding: '0.4rem 0.75rem',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.8rem'
              }}
            >
              Later
            </button>
            <button
              onClick={() => {
                setShowFeedbackPrompt(false);
                setShowFeedbackModal(true);
              }}
              style={{
                background: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '0.4rem 0.75rem',
                color: '#7c3aed',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.8rem'
              }}
            >
              Yes! ⭐
            </button>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div 
          className="modal-overlay" 
          onClick={() => setShowFeedbackModal(false)}
          style={{ zIndex: 1001 }}
        >
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '450px',
              padding: '2rem',
              borderRadius: '16px'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>🎵 Quick Feedback</h3>
            <p style={{ color: '#94a3b8', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
              {nowPlaying ? (
                <>Currently playing: <strong>{nowPlaying.title}</strong> by <strong>{nowPlaying.artist}</strong></>
              ) : (
                <>Share your thoughts about what you're hearing!</>
              )}
            </p>
            
            {/* Rating Selection */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '500' }}>
                How would you rate this?
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                {[
                  { value: 'love', emoji: '😍', label: 'Love it!' },
                  { value: 'like', emoji: '👍', label: 'Like it' },
                  { value: 'okay', emoji: '😐', label: "It's okay" },
                  { value: 'skip', emoji: '👎', label: 'Not for me' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFeedbackData(prev => ({ ...prev, rating: option.value }))}
                    style={{
                      background: feedbackData.rating === option.value 
                        ? 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)' 
                        : 'rgba(255, 255, 255, 0.1)',
                      border: feedbackData.rating === option.value 
                        ? '2px solid #a78bfa' 
                        : '2px solid transparent',
                      borderRadius: '12px',
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.25rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span style={{ fontSize: '1.5rem' }}>{option.emoji}</span>
                    <span style={{ fontSize: '0.7rem', color: '#e2e8f0' }}>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Optional Comment */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Any comments? (optional)
              </label>
              <textarea
                value={feedbackData.comment}
                onChange={(e) => setFeedbackData(prev => ({ ...prev, comment: e.target.value }))}
                placeholder="Tell us what you think..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  fontSize: '0.9rem',
                  minHeight: '80px',
                  resize: 'vertical'
                }}
              />
            </div>
            
            {/* Favorite Genre */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                What is your favorite kind of music? (optional)
              </label>
              <input
                type="text"
                value={feedbackData.favoriteGenre}
                onChange={(e) => setFeedbackData(prev => ({ ...prev, favoriteGenre: e.target.value }))}
                placeholder="Punk, Hip Hop, Alternative, etc..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#fff',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setFeedbackData({ rating: null, comment: '', favoriteGenre: '' });
                  setLastFeedbackTime(listeningMinutes);
                }}
                className="secondary-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleFeedbackSubmit}
                className="cta-btn"
                disabled={!feedbackData.rating}
              >
                Submit & Earn Points! 🎉
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Auth Pages
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [role, setRole] = useState('listener');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: code+password
  const [resetEmail, setResetEmail] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isRegister) {
        await register(email, password, role);
        toast.success('Account created! Please check your email to verify your account.', { duration: 6000 });
        toast.info('Check your spam folder if you don\'t see the email.', { duration: 6000 });
      } else {
        await login(email, password);
        toast.success('Logged in successfully!');
      }
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      
      toast.success('Reset code sent! Check your email inbox.');
      setResetStep(2);
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to send reset code';
      toast.error(errorMsg);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      console.log('Attempting password reset with:', {
        email: resetEmail,
        reset_code: resetCode,
        password_length: newPassword.length
      });
      
      await axios.post(`${API}/auth/reset-password`, {
        email: resetEmail,
        reset_code: resetCode,
        new_password: newPassword
      });
      
      toast.success('Password reset successfully! You can now log in.');
      setShowForgotPassword(false);
      setResetStep(1);
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Reset password error:', error);
      console.error('Error details:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to reset password';
      toast.error(errorMsg);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        {!showForgotPassword ? (
          <>
            <h1 data-testid="auth-title">{isRegister ? 'Create Account' : 'Welcome Back'}</h1>
            <form onSubmit={handleSubmit} data-testid="auth-form">
              <input
                data-testid="email-input"
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                data-testid="password-input"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {isRegister && (
                <select data-testid="role-select" value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="listener">Listener</option>
                  <option value="artist">Artist</option>
                </select>
              )}
              <button data-testid="submit-btn" type="submit">{isRegister ? 'Sign Up' : 'Log In'}</button>
            </form>
            {!isRegister && (
              <div className="forgot-password-notice">
                <button 
                  type="button"
                  onClick={() => setShowForgotPassword(true)} 
                  className="forgot-password-link"
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: '#8b5cf6', 
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px',
                    padding: '0',
                    marginTop: '10px'
                  }}
                >
                  Forgot your password?
                </button>
              </div>
            )}
            <p className="auth-toggle">
              {isRegister ? 'Already have an account?' : "Don't have an account?"}
              <button data-testid="toggle-auth-btn" onClick={() => setIsRegister(!isRegister)}>
                {isRegister ? 'Log In' : 'Sign Up'}
              </button>
            </p>
          </>
        ) : (
          <>
            <h1>Reset Password</h1>
            {resetStep === 1 ? (
              <form onSubmit={handleForgotPassword}>
                <p style={{ marginBottom: '1rem', color: '#9ca3af' }}>
                  Enter your email address and we'll send you a reset code.
                </p>
                <input
                  type="email"
                  placeholder="Email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
                <button type="submit">Send Reset Code</button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword}>
                <p style={{ marginBottom: '1rem', color: '#9ca3af' }}>
                  Enter the 6-digit code and your new password.
                </p>
                <input
                  type="text"
                  placeholder="6-Digit Reset Code"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  required
                  maxLength="6"
                />
                <input
                  type="password"
                  placeholder="New Password (min 6 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength="6"
                />
                <input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength="6"
                />
                <button type="submit">Reset Password</button>
              </form>
            )}
            <p className="auth-toggle">
              <button onClick={() => {
                setShowForgotPassword(false);
                setResetStep(1);
                setResetEmail('');
                setResetCode('');
                setNewPassword('');
                setConfirmPassword('');
              }}>
                Back to Login
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// Songs Page


const EmailVerification = () => {
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    // Verify email
    axios.post(`${API}/auth/verify-email?token=${token}`)
      .then(response => {
        setStatus('success');
        setMessage('Email verified successfully! You can now log in.');
        setTimeout(() => navigate('/login'), 3000);
      })
      .catch(error => {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Verification failed');
      });
  }, [location, navigate]);

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h1>Email Verification</h1>
        {status === 'verifying' && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Verifying your email...</p>
          </div>
        )}
        {status === 'success' && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#22c55e' }}>
            <p>✅ {message}</p>
            <p style={{ marginTop: '1rem', color: '#9ca3af' }}>Redirecting to login...</p>
          </div>
        )}
        {status === 'error' && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444' }}>
            <p>❌ {message}</p>
            <button 
              onClick={() => navigate('/login')}
              style={{ marginTop: '1rem' }}
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Songs = () => {
  const [songs, setSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const [genres, setGenres] = useState([]);
  const [playingSongId, setPlayingSongId] = useState(null);
  const [starredSongs, setStarredSongs] = useState(new Set());
  const [starringInProgress, setStarringInProgress] = useState(null);
  const [searchMode, setSearchMode] = useState('song'); // 'song' or 'artist'
  const [artists, setArtists] = useState([]);
  const [filteredArtists, setFilteredArtists] = useState([]);
  // Shuffle player state
  const [shuffleSong, setShuffleSong] = useState(null);
  const [shufflePlaying, setShufflePlaying] = useState(false);
  const shuffleAudioRef = useRef(null);
  const { user, refetchUser} = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSongs();
    fetchArtists();
    if (user) {
      fetchStarredSongs();
    }
  }, [user]);

  useEffect(() => {
    if (searchMode === 'song') {
      filterSongs();
    } else {
      filterArtists();
    }
  }, [songs, searchTerm, selectedGenre, sortOrder, searchMode, artists]);

  const fetchSongs = async () => {
    try {
      const response = await axios.get(`${API}/songs`);
      // Filter out Quizboy and Never Say Never songs from public display
      const hiddenArtists = ['quizboy', 'never say never'];
      const filteredSongs = response.data.filter(song => 
        !hiddenArtists.includes(song.artist?.toLowerCase())
      );
      setSongs(filteredSongs);
      
      // Extract unique genres (handle comma-separated genres)
      const allGenres = filteredSongs
        .map(song => song.genre)
        .filter(g => g)
        .flatMap(genre => genre.split(',').map(g => g.trim()))
        .filter(g => g); // Remove empty strings
      
      const uniqueGenres = [...new Set(allGenres)].sort();
      setGenres(uniqueGenres);
    } catch (error) {
      toast.error('Failed to load songs');
    }
    setLoading(false);
  };

  const fetchStarredSongs = async () => {
    try {
      const response = await axios.get(`${API}/user/starred-songs`);
      const starredIds = new Set(response.data.map(song => song.id));
      setStarredSongs(starredIds);
    } catch (error) {
      console.error('Failed to load starred songs');
    }
  };

  const fetchArtists = async () => {
    try {
      const response = await axios.get(`${API}/artists`);
      setArtists(response.data);
    } catch (error) {
      console.error('Failed to load artists');
    }
  };

  const filterArtists = () => {
    let filtered = artists;

    if (searchTerm) {
      filtered = filtered.filter(artist =>
        artist.artist_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        artist.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredArtists(filtered);
  };

  const filterSongs = () => {
    let filtered = songs;

    // Filter by search term (title or artist)
    if (searchTerm) {
      filtered = filtered.filter(song =>
        song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        song.artist.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by genre (handle comma-separated genres)
    if (selectedGenre !== 'all') {
      filtered = filtered.filter(song => {
        if (!song.genre) return false;
        // Split genres by comma and check if selected genre is in the list
        const songGenres = song.genre.split(',').map(g => g.trim());
        return songGenres.includes(selectedGenre);
      });
    }

    // Sort by date added
    filtered = [...filtered].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      
      if (sortOrder === 'newest') {
        return dateB - dateA; // Newest first
      } else {
        return dateA - dateB; // Oldest first
      }
    });

    setFilteredSongs(filtered);
  };

  const handlePreviewPlay = (songId, audioElement) => {
    // Stop any currently playing song
    const allAudios = document.querySelectorAll('audio');
    allAudios.forEach(audio => {
      if (audio !== audioElement) {
        audio.pause();
        audio.currentTime = 0;
      }
    });

    if (playingSongId === songId) {
      audioElement.pause();
      setPlayingSongId(null);
    } else {
      audioElement.play();
      setPlayingSongId(songId);
      
      // Stop after 30 seconds
      setTimeout(() => {
        if (audioElement && !audioElement.paused) {
          audioElement.pause();
          audioElement.currentTime = 0;
          setPlayingSongId(null);
          toast.info('Preview ended - Purchase to hear the full song');
        }
      }, 30000);
    }
  };

  const handlePurchase = async (songId) => {
    if (!user) {
      toast.error('Please log in to purchase songs');
      navigate('/login');
      return;
    }

    try {
      await axios.post(`${API}/user/purchase-song`, { song_id: songId });
      toast.success('Song added to your jukebox!');
      refetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    }
  };

  const handleStarSong = async (songId) => {
    if (!user) {
      toast.error('Please log in to show love for songs');
      navigate('/login');
      return;
    }

    if (starringInProgress === songId) return;

    setStarringInProgress(songId);
    try {
      const isStarred = starredSongs.has(songId);
      
      if (isStarred) {
        // Unstar
        await axios.delete(`${API}/songs/${songId}/star`);
        setStarredSongs(prev => {
          const newSet = new Set(prev);
          newSet.delete(songId);
          return newSet;
        });
        toast.success('Removed from Loved Songs');
      } else {
        // Star
        const response = await axios.post(`${API}/songs/${songId}/star`);
        setStarredSongs(prev => new Set([...prev, songId]));
        toast.success(`⭐ Loved! (${response.data.stars_remaining} stars remaining)`);
        
        // Update song star count in local state
        setSongs(prev => prev.map(song => 
          song.id === songId 
            ? { ...song, star_count: (song.star_count || 0) + 1 }
            : song
        ));
      }
      
      refetchUser();
      await fetchSongs(); // Refresh to get updated star counts
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to star song');
    } finally {
      setStarringInProgress(null);
    }
  };

  // ============ Shuffle player helpers (Mode C: random with anti-repeat window) ============
  // Keep a memory of recently-played song IDs. Next pick is uniformly random
  // across the catalog EXCLUDING anything in that window. Window size scales
  // with catalog size but is clamped so small catalogs still work.
  const recentlyPlayedRef = useRef([]);

  const getRecentWindowSize = () => {
    if (!songs.length) return 0;
    // 15% of catalog, clamped between 3 and 15. For a catalog of ~1 song, window = 0.
    return Math.max(0, Math.min(15, Math.floor(songs.length * 0.15)));
  };

  const stopOtherAudio = () => {
    // Pause any other <audio> element (e.g. 30s previews) so playback is exclusive
    const shuffleEl = shuffleAudioRef.current;
    document.querySelectorAll('audio').forEach(a => {
      if (a !== shuffleEl && !a.paused) {
        a.pause();
        try { a.currentTime = 0; } catch (_) {}
      }
    });
  };

  const pickRandomSong = () => {
    if (!songs.length) return null;
    const windowSize = getRecentWindowSize();
    const excluded = new Set(recentlyPlayedRef.current.slice(-windowSize));
    // Candidates = songs not in the exclusion window
    let candidates = songs.filter(s => !excluded.has(s.id));
    // Safety net: if for any reason candidates is empty (tiny catalog), fall back to any song
    if (candidates.length === 0) candidates = songs;
    const next = candidates[Math.floor(Math.random() * candidates.length)];
    // Remember it (trim to at most windowSize entries)
    const memory = [...recentlyPlayedRef.current, next.id];
    recentlyPlayedRef.current = memory.slice(-Math.max(1, windowSize));
    return next;
  };

  const pickNextShuffleSong = (autoplay = false) => {
    if (!songs.length) return;
    const next = pickRandomSong();
    if (!next) return;
    setShuffleSong(next);
    if (autoplay) {
      // wait a tick for the <audio> src to update, then play
      setTimeout(() => {
        if (shuffleAudioRef.current) {
          stopOtherAudio();
          shuffleAudioRef.current.play().catch(() => {});
        }
      }, 100);
    }
  };

  const toggleShufflePlay = () => {
    if (!songs.length) {
      toast.error('No songs available to shuffle');
      return;
    }
    if (!shuffleSong) {
      pickNextShuffleSong(true);
      return;
    }
    if (!shuffleAudioRef.current) return;
    if (shufflePlaying) {
      shuffleAudioRef.current.pause();
    } else {
      stopOtherAudio();
      shuffleAudioRef.current.play().catch(() => {});
    }
  };

  const skipShuffleSong = () => {
    if (!songs.length) return;
    pickNextShuffleSong(true);
  };

  const handleShuffleStar = async () => {
    if (!shuffleSong) return;
    if (!user) {
      toast.error('Please log in to show love for songs');
      navigate('/login');
      return;
    }
    if (starringInProgress === shuffleSong.id) return;
    setStarringInProgress(shuffleSong.id);
    try {
      const isStarred = starredSongs.has(shuffleSong.id);
      if (isStarred) {
        await axios.delete(`${API}/songs/${shuffleSong.id}/star`);
        setStarredSongs(prev => {
          const s = new Set(prev);
          s.delete(shuffleSong.id);
          return s;
        });
        toast.success('Removed from Loved Songs');
      } else {
        const response = await axios.post(`${API}/songs/${shuffleSong.id}/star`);
        setStarredSongs(prev => new Set([...prev, shuffleSong.id]));
        toast.success(`⭐ Loved! (${response.data.stars_remaining} stars remaining)`);
      }
      refetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to star song');
    } finally {
      setStarringInProgress(null);
    }
  };

  const handleShufflePurchase = async () => {
    if (!shuffleSong) return;
    if (!user) {
      toast.error('Please log in to purchase songs');
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${API}/user/purchase-song`, { song_id: shuffleSong.id });
      toast.success('Song added to your jukebox!');
      refetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    }
  };
  // ============ End shuffle player helpers ============

  if (loading) {
    return <div className="loading">Loading songs...</div>;
  }

  return (
    <div className="songs-page">
      <div className="songs-header">
        <div>
          <h1 data-testid="songs-title">Jukebox Songs</h1>
          <p data-testid="songs-subtitle">Purchase songs with tokens • Show love with stars • Build your collection</p>
        </div>
        {user && (
          <div className="user-stars-display">
            <span className="stars-icon">⭐</span>
            <span className="stars-count">{user.star_balance || 0} stars</span>
            <span className="stars-hint">Show love to songs!</span>
          </div>
        )}
      </div>

      {/* Shuffle Player - random song from the entire bank */}
      <div className="shuffle-player" data-testid="shuffle-player">
        <audio
          ref={shuffleAudioRef}
          src={shuffleSong?.audio_url || ''}
          preload="none"
          onPlay={() => setShufflePlaying(true)}
          onPause={() => setShufflePlaying(false)}
          onEnded={() => pickNextShuffleSong(true)}
        />
        <div className="shuffle-art">
          {shuffleSong?.album_art_url ? (
            <img
              src={shuffleSong.album_art_url}
              alt={shuffleSong.title}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="shuffle-art-placeholder">🎵</div>
          )}
        </div>
        <div className="shuffle-info">
          <div className="shuffle-label">🔀 Shuffle Play</div>
          {shuffleSong ? (
            <>
              <div className="shuffle-title" title={shuffleSong.title}>{shuffleSong.title}</div>
              <div className="shuffle-artist" title={shuffleSong.artist}>{shuffleSong.artist}</div>
            </>
          ) : (
            <div className="shuffle-artist">Press play for a random song</div>
          )}
        </div>
        <div className="shuffle-controls">
          <button
            className="shuffle-btn shuffle-btn-primary"
            onClick={toggleShufflePlay}
            data-testid="shuffle-play-btn"
            aria-label={shufflePlaying ? 'Pause' : 'Play'}
          >
            {shufflePlaying ? '⏸' : '▶'}
          </button>
          <button
            className="shuffle-btn"
            onClick={skipShuffleSong}
            data-testid="shuffle-skip-btn"
            aria-label="Skip to next random song"
            title="Next random song"
          >
            ⏭
          </button>
          {shuffleSong && (
            <>
              <button
                className={`shuffle-btn shuffle-btn-star ${starredSongs.has(shuffleSong.id) ? 'starred' : ''}`}
                onClick={handleShuffleStar}
                disabled={starringInProgress === shuffleSong.id}
                data-testid="shuffle-star-btn"
                title="Show love (1 star)"
              >
                ⭐
              </button>
              <button
                className="shuffle-btn shuffle-btn-buy"
                onClick={handleShufflePurchase}
                data-testid="shuffle-add-btn"
                title={`Add to Jukebox (${shuffleSong.token_cost || 1} token)`}
              >
                🪙 Add
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search Mode Toggle */}
      <div className="search-mode-toggle">
        <button 
          className={`mode-btn ${searchMode === 'song' ? 'active' : ''}`}
          onClick={() => setSearchMode('song')}
          data-testid="search-songs-btn"
        >
          🎵 Search Songs
        </button>
        <button 
          className={`mode-btn ${searchMode === 'artist' ? 'active' : ''}`}
          onClick={() => {
            setSearchMode('artist');
            setSearchTerm('');
          }}
          data-testid="search-artists-btn"
        >
          📁 Search Catalogs
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div className="search-filter-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder={searchMode === 'song' ? "Search by song title or artist..." : "Search by catalog name..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-input"
          />
        </div>
        {searchMode === 'song' && (
          <>
            <div className="filter-box">
              <select
                value={selectedGenre}
                onChange={(e) => setSelectedGenre(e.target.value)}
                data-testid="genre-filter"
              >
                <option value="all">All Genres</option>
                {genres.map((genre) => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
            </div>
            <div className="filter-box">
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                data-testid="sort-filter"
              >
                <option value="newest">Recently Added</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
          </>
        )}
      </div>

      {/* Artist Grid */}
      {searchMode === 'artist' && (
        <div className="artists-grid">
          {filteredArtists.map((artist) => (
            <div 
              key={artist.id} 
              className="artist-card" 
              onClick={() => navigate(`/artist/${artist.id}`)}
              data-testid={`artist-card-${artist.id}`}
            >
              <div className="artist-avatar">
                <span className="avatar-icon">👤</span>
              </div>
              <div className="artist-info">
                <h3 data-testid={`artist-name-${artist.id}`}>{artist.artist_name}</h3>
                <p className="song-count">{artist.song_count} song{artist.song_count !== 1 ? 's' : ''}</p>
              </div>
            </div>
          ))}
          {filteredArtists.length === 0 && (
            <div className="empty-state">
              <p>No artists found matching your search.</p>
            </div>
          )}
        </div>
      )}

      {/* Songs Grid */}
      {searchMode === 'song' && (
        <div className="songs-grid">
          {filteredSongs.map((song) => (
          <div key={song.id} className="song-card" data-testid={`song-card-${song.id}`}>
            <div className="song-info">
              <h3 data-testid={`song-title-${song.id}`}>{song.title}</h3>
              <p data-testid={`song-artist-${song.id}`}>
                <span 
                  className="artist-link-underlined"
                  onClick={() => navigate(`/artist/${song.artist_id || song.artist_email}`)}
                >
                  {song.artist}
                </span>
              </p>
              {song.album && <p className="album" data-testid={`song-album-${song.id}`}>{song.album}</p>}
              {song.genre && (
                <div className="genre-tags">
                  {song.genre.split(',').map((g, idx) => (
                    <span key={`genre-${idx}-${g.trim()}`} className="genre-tag">{g.trim()}</span>
                  ))}
                </div>
              )}
              
              {/* 30-second Preview */}
              {(song.preview_url || song.audio_url) && (
                <div className="preview-section">
                  <audio 
                    id={`audio-${song.id}`}
                    src={song.preview_url || song.audio_url}
                    preload="none"
                    onEnded={() => setPlayingSongId(null)}
                    data-testid={`preview-audio-${song.id}`}
                  />
                  <button
                    className="preview-btn"
                    onClick={(e) => {
                      const audio = document.getElementById(`audio-${song.id}`);
                      handlePreviewPlay(song.id, audio);
                    }}
                    data-testid={`preview-btn-${song.id}`}
                  >
                    {playingSongId === song.id ? '⏸ Pause Preview' : '▶ 30s Preview'}
                  </button>
                </div>
              )}
              
              <div className="song-actions">
                <div className="star-section">
                  <button
                    className={`star-btn ${starredSongs.has(song.id) ? 'starred' : ''}`}
                    onClick={() => handleStarSong(song.id)}
                    disabled={starringInProgress === song.id}
                    data-testid={`star-btn-${song.id}`}
                    title={starredSongs.has(song.id) ? 'Remove from Loved Songs' : 'Show Love (1 star)'}
                  >
                    {starringInProgress === song.id ? '...' : starredSongs.has(song.id) ? '❤️' : '🤍'}
                  </button>
                </div>

                <div className="purchase-section">
                  <span className="token-cost" data-testid={`song-cost-${song.id}`}>{song.token_cost} token{song.token_cost !== 1 ? 's' : ''}</span>
                  <button 
                    data-testid={`purchase-btn-${song.id}`}
                    className="purchase-btn" 
                    onClick={() => handlePurchase(song.id)}
                  >
                    Add to Jukebox
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
          {filteredSongs.length === 0 && (
            <div className="empty-state" data-testid="empty-songs">
              <p>No songs found matching your search.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Jukebox Page
const Jukebox = () => {
  const [songs, setSongs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistSongs, setPlaylistSongs] = useState([]);
  const [sortedSongs, setSortedSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recent');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [showAddToPlaylist, setShowAddToPlaylist] = useState(false);
  const [selectedSongForPlaylist, setSelectedSongForPlaylist] = useState(null);
  const [starredSongs, setStarredSongs] = useState(new Set());
  const [starredSongsList, setStarredSongsList] = useState([]); // Full list of starred songs
  const [starringInProgress, setStarringInProgress] = useState(null);
  const [showUnplaylisted, setShowUnplaylisted] = useState(false);
  const [showLovedSongs, setShowLovedSongs] = useState(false);
  const [allPlaylistSongIds, setAllPlaylistSongIds] = useState(new Set());
  
  // Mini Player State
  const [playQueue, setPlayQueue] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState(null);

  const { user, refetchUser } = useAuth();

  useEffect(() => {
    fetchJukebox();
    fetchPlaylists();
    if (user) {
      fetchStarredSongs();
    }
  }, [user]);

  // Auto-scroll to currently playing tile
  useEffect(() => {
    if (isPlayerVisible && playQueue.length > 0 && playQueue[currentTrackIndex]) {
      const playingTileId = playQueue[currentTrackIndex].id;
      const tileElement = document.querySelector(`[data-testid="jukebox-song-${playingTileId}"]`);
      
      if (tileElement) {
        tileElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }
    }
  }, [currentTrackIndex, isPlayerVisible]);

  // Listening heartbeat for loyalty points tracking (Phase 0) - Jukebox player
  useEffect(() => {
    let heartbeatInterval = null;
    
    if (isPlaying && user) {
      // Send heartbeat every 60 seconds while playing
      heartbeatInterval = setInterval(async () => {
        try {
          await axios.post(`${API}/user/track-activity`, {
            activity_type: 'listening_minutes',
            value: 1,  // 1 minute
            metadata: { source: 'jukebox' }
          });
        } catch (error) {
          // Silently fail - don't interrupt listening experience
          console.debug('Heartbeat tracking failed:', error);
        }
      }, 60000); // Every 60 seconds
    }
    
    return () => {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }
    };
  }, [isPlaying, user]);

  useEffect(() => {
    if (!selectedPlaylist) {
      sortSongs();
    }
  }, [songs, sortBy, selectedPlaylist]);

  useEffect(() => {
    if (!selectedPlaylist) {
      filterSongs();
    }
  }, [sortedSongs, searchTerm, selectedPlaylist]);

  const fetchJukebox = async () => {
    try {
      const response = await axios.get(`${API}/user/jukebox`);
      setSongs(response.data || []);
    } catch (error) {
      // Don't show error for empty jukebox - just set empty array
      if (error.response?.status === 404 || !error.response) {
        setSongs([]);
      } else {
        toast.error('Failed to load jukebox');
      }
    }
    setLoading(false);
  };

  const fetchStarredSongs = async () => {
    try {
      const response = await axios.get(`${API}/user/starred-songs`);
      const starredIds = new Set(response.data.map(song => song.id));
      setStarredSongs(starredIds);
      setStarredSongsList(response.data); // Store full list for Loved Songs display
    } catch (error) {
      console.error('Failed to load starred songs');
    }
  };

  const fetchPlaylists = async () => {
    try {
      const response = await axios.get(`${API}/user/playlists`);
      const playlistsData = response.data;
      
      // Fetch all playlist song IDs to properly filter "Not in Mix Tape"
      const allSongIdsInPlaylists = new Set();
      for (const playlist of playlistsData) {
        try {
          const playlistDetail = await axios.get(`${API}/user/playlists/${playlist.id}`);
          playlistDetail.data.songs.forEach(song => allSongIdsInPlaylists.add(song.id));
        } catch (err) {
          console.error(`Failed to load playlist ${playlist.id}`);
        }
      }
      
      setPlaylists(playlistsData);
      setAllPlaylistSongIds(allSongIdsInPlaylists);
    } catch (error) {
      console.error('Failed to load playlists');
    }
  };

  const fetchPlaylistSongs = async (playlistId) => {
    try {
      const response = await axios.get(`${API}/user/playlists/${playlistId}`);
      setPlaylistSongs(response.data.songs);
      setSelectedPlaylist(response.data.playlist);
    } catch (error) {
      toast.error('Failed to load playlist');
    }
  };

  const createPlaylist = async () => {
    if (!newPlaylistName.trim()) {
      toast.error('Please enter a mix tape name');
      return;
    }
    try {
      await axios.post(`${API}/user/playlists`, { name: newPlaylistName, description: '' });
      toast.success('Mix Tape created!');
      setNewPlaylistName('');
      setShowCreatePlaylist(false);
      fetchPlaylists();
    } catch (error) {
      toast.error('Failed to create mix tape');
    }
  };

  const deletePlaylist = async (playlistId) => {
    if (!window.confirm('Are you sure you want to delete this playlist?')) return;
    try {
      await axios.delete(`${API}/user/playlists/${playlistId}`);
      toast.success('Mix Tape deleted');
      fetchPlaylists();
      if (selectedPlaylist?.id === playlistId) {
        setSelectedPlaylist(null);
      }
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };

  const addSongToPlaylist = async (playlistId) => {
    try {
      await axios.post(`${API}/user/playlists/add-song`, {
        playlist_id: playlistId,
        song_id: selectedSongForPlaylist
      });
      toast.success('Song added to Mix Tape!');
      setShowAddToPlaylist(false);
      setSelectedSongForPlaylist(null);
      fetchPlaylists();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add song');
    }
  };

  const removeSongFromPlaylist = async (songId) => {
    if (!window.confirm('Remove this song from the playlist?')) return;
    try {
      await axios.delete(`${API}/user/playlists/${selectedPlaylist.id}/songs/${songId}`);
      toast.success('Song removed from Mix Tape');
      fetchPlaylistSongs(selectedPlaylist.id);
      fetchPlaylists();
    } catch (error) {
      toast.error('Failed to remove song');
    }
  };

  const sortSongs = () => {
    let sorted = [...songs];
    switch (sortBy) {
      case 'name':
        sorted.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'oldest':
        sorted.sort((a, b) => {
          const dateA = new Date(a.purchase_date || a.created_at);
          const dateB = new Date(b.purchase_date || b.created_at);
          return dateA - dateB;
        });
        break;
      case 'recent':
      default:
        sorted.sort((a, b) => {
          const dateA = new Date(a.purchase_date || a.created_at);
          const dateB = new Date(b.purchase_date || b.created_at);
          return dateB - dateA;
        });
        break;
    }
    setSortedSongs(sorted);
  };

  const filterSongs = () => {
    let filtered = sortedSongs;
    if (searchTerm) {
      filtered = filtered.filter(song =>
        song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        song.artist.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredSongs(filtered);
  };

  const handlePurchaseFromLoved = async (songId) => {
    if (!user) {
      toast.error('Please log in to purchase songs');
      return;
    }

    try {
      await axios.post(`${API}/user/purchase-song`, { song_id: songId });
      toast.success('🎵 Song added to your Jukebox!');
      refetchUser(); // Update token balance
      await fetchJukebox(); // Refresh library
      await fetchStarredSongs(); // Refresh loved songs (removes purchased song)
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    }
  };

  // Mini Player Functions
  const playAll = (songs) => {
    if (!songs || songs.length === 0) {
      toast.error('No songs to play');
      return;
    }
    setPlayQueue(songs);
    setCurrentTrackIndex(0);
    setIsPlayerVisible(true);
    setIsPlaying(true);
    toast.success(`🎵 Playing ${songs.length} songs`);
  };

  const playShuffle = (songs) => {
    if (!songs || songs.length === 0) {
      toast.error('No songs to play');
      return;
    }
    const shuffled = [...songs].sort(() => Math.random() - 0.5);
    setPlayQueue(shuffled);
    setCurrentTrackIndex(0);
    setIsPlayerVisible(true);
    setIsPlaying(true);
    toast.success(`🔀 Shuffling ${shuffled.length} songs`);
  };

  const playNext = () => {
    if (currentTrackIndex < playQueue.length - 1) {
      setCurrentTrackIndex(currentTrackIndex + 1);
      setIsPlaying(true);
    } else {
      toast.info('End of queue');
      setIsPlaying(false);
    }
  };

  const playPrevious = () => {
    if (currentTrackIndex > 0) {
      setCurrentTrackIndex(currentTrackIndex - 1);
      setIsPlaying(true);
    }
  };

  const closeMiniPlayer = () => {
    setIsPlayerVisible(false);
    setIsPlaying(false);
    setPlayQueue([]);
    setCurrentTrackIndex(0);
    if (audioRef) {
      audioRef.pause();
    }
  };

  const handleStarSong = async (songId) => {
    if (!user) {
      toast.error('Please log in to show love for songs');
      return;
    }

    // Check if user already owns this song
    const userOwnsSong = songs.some(ownedSong => ownedSong.id === songId);
    if (userOwnsSong) {
      toast.info('💿 Song already in your Library');
      return;
    }

    if (starringInProgress === songId) return;

    setStarringInProgress(songId);
    try {
      const isStarred = starredSongs.has(songId);
      
      if (isStarred) {
        // Unstar
        await axios.delete(`${API}/songs/${songId}/star`);
        setStarredSongs(prev => {
          const newSet = new Set(prev);
          newSet.delete(songId);
          return newSet;
        });
        toast.success('Removed from Loved Songs');
      } else {
        // Star
        const response = await axios.post(`${API}/songs/${songId}/star`);
        setStarredSongs(prev => new Set([...prev, songId]));
        toast.success(`⭐ Loved! (${response.data.stars_remaining} stars remaining)`);
        
        // Update song star count in local state
        setSongs(prev => prev.map(song => 
          song.id === songId 
            ? { ...song, star_count: (song.star_count || 0) + 1 }
            : song
        ));
      }
      
      refetchUser();
      await fetchJukebox(); // Refresh to get updated star counts
      await fetchStarredSongs(); // Refresh starred songs list for Loved Songs display
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to star song');
    } finally {
      setStarringInProgress(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading your jukebox...</div>;
  }

  // Get songs that are not in any playlist
  const getUnplaylistedSongs = () => {
    return filteredSongs.filter(song => !allPlaylistSongIds.has(song.id));
  };

  // Get songs that user has starred (loved) but NOT purchased
  const getLovedSongs = () => {
    // Return starred songs that are NOT in the user's purchased library
    // Once a song is purchased, it moves to the main Library
    const purchasedSongIds = new Set(songs.map(s => s.id));
    let lovedSongs = starredSongsList.filter(song => !purchasedSongIds.has(song.id));
    
    // Apply search filter if present
    if (searchTerm) {
      lovedSongs = lovedSongs.filter(song =>
        song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        song.artist.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    switch (sortBy) {
      case 'name':
        lovedSongs.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'oldest':
        lovedSongs.sort((a, b) => {
          const dateA = new Date(a.starred_at || a.created_at);
          const dateB = new Date(b.starred_at || b.created_at);
          return dateA - dateB;
        });
        break;
      case 'recent':
      default:
        lovedSongs.sort((a, b) => {
          const dateA = new Date(a.starred_at || a.created_at);
          const dateB = new Date(b.starred_at || b.created_at);
          return dateB - dateA;
        });
        break;
    }
    
    return lovedSongs;
  };

  const displaySongs = selectedPlaylist 
    ? playlistSongs 
    : showLovedSongs
      ? getLovedSongs()
      : showUnplaylisted
        ? getUnplaylistedSongs()
        : filteredSongs;

  // handleCreateMixtape function removed

  return (
    <div className="jukebox-page">
      <div className="jukebox-header">
        <div>
          <h1 data-testid="jukebox-title">My Jukebox</h1>
        </div>
        
        {((songs.length > 0 || showLovedSongs) && !selectedPlaylist) && (
          <div className="jukebox-controls">
            <div className="search-box">
              <input
                type="text"
                placeholder={showLovedSongs ? "Search loved songs..." : "Search your library..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="jukebox-search-input"
              />
            </div>
            <div className="sort-box">
              <label>Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="sort-select"
              >
                <option value="recent">Recently {showLovedSongs ? 'Loved' : 'Purchased'}</option>
                <option value="oldest">Oldest First</option>
                <option value="name">Song Name (A-Z)</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="jukebox-layout">
        {/* Playlists Sidebar */}
        <div className="playlists-sidebar">
          <div className="sidebar-header">
            <h3>Mix Tapes</h3>
            <button 
              className="create-playlist-btn"
              onClick={() => setShowCreatePlaylist(!showCreatePlaylist)}
              data-testid="create-playlist-btn"
            >
              +
            </button>
          </div>

          {showCreatePlaylist && (
            <div className="create-playlist-form">
              <input
                type="text"
                placeholder="Mix Tape name..."
                value={newPlaylistName}
                onChange={(e) => setNewPlaylistName(e.target.value)}
                data-testid="playlist-name-input"
              />
              <button onClick={createPlaylist} data-testid="save-playlist-btn">Create</button>
            </div>
          )}

          <div 
            className={`playlist-item ${!selectedPlaylist && !showUnplaylisted && !showLovedSongs ? 'active' : ''}`}
            onClick={() => {
              setSelectedPlaylist(null);
              setShowUnplaylisted(false);
              setShowLovedSongs(false);
            }}
            data-testid="all-songs-btn"
          >
            <span>📀 All Songs</span>
            <span className="song-count">{songs.length}</span>
          </div>

          <div 
            className={`playlist-item ${showUnplaylisted && !showLovedSongs ? 'active' : ''}`}
            onClick={() => {
              setSelectedPlaylist(null);
              setShowUnplaylisted(true);
              setShowLovedSongs(false);
            }}
            data-testid="unplaylisted-songs-btn"
          >
            <span>📂 Not in Mix Tape</span>
            <span className="song-count">
              {songs.filter(song => !allPlaylistSongIds.has(song.id)).length}
            </span>
          </div>

          <div 
            className={`playlist-item ${showLovedSongs ? 'active' : ''}`}
            onClick={() => {
              setSelectedPlaylist(null);
              setShowUnplaylisted(false);
              setShowLovedSongs(true);
            }}
            data-testid="loved-songs-btn"
          >
            <span>❤️ Loved Songs</span>
            <span className="song-count">
              {starredSongsList.length}
            </span>
          </div>

          {playlists.map(playlist => (
            <div 
              key={playlist.id}
              className={`playlist-item ${selectedPlaylist?.id === playlist.id ? 'active' : ''}`}
              data-testid={`playlist-${playlist.id}`}
            >
              <div onClick={() => fetchPlaylistSongs(playlist.id)} style={{ flex: 1, cursor: 'pointer' }}>
                <span>🎵 {playlist.name}</span>
                <span className="song-count">{playlist.song_count || 0}</span>
              </div>
              <button 
                className="delete-playlist-btn"
                onClick={(e) => { e.stopPropagation(); deletePlaylist(playlist.id); }}
                data-testid={`delete-playlist-${playlist.id}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>

        {/* Songs Grid */}
        <div className="songs-content">
          {/* Library Header with Play Controls - shown when no playlist selected */}
          {!selectedPlaylist && displaySongs.length > 0 && (
            <div className="playlist-header-bar" style={{ marginBottom: '1rem' }}>
              <div>
                <h2>
                  {showLovedSongs ? '❤️ Loved Songs' : showUnplaylisted ? '📂 Not in Mix Tape' : '📀 All Songs'}
                </h2>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button 
                    onClick={() => playAll(displaySongs)}
                    className="playlist-action-btn play-all-btn"
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: 'rgba(16, 185, 129, 0.2)',
                      border: '1px solid rgba(16, 185, 129, 0.5)',
                      borderRadius: '6px',
                      color: '#10b981',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}
                  >
                    <span className="btn-icon">▶</span>
                    <span className="btn-text"> Play All ({displaySongs.length})</span>
                  </button>
                  <button 
                    onClick={() => playShuffle(displaySongs)}
                    className="playlist-action-btn shuffle-btn"
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: 'rgba(139, 92, 246, 0.2)',
                      border: '1px solid rgba(139, 92, 246, 0.5)',
                      borderRadius: '6px',
                      color: '#8b5cf6',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}
                  >
                    <span className="btn-icon">🔀</span>
                    <span className="btn-text"> Shuffle ({displaySongs.length})</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {selectedPlaylist && (
            <div className="playlist-header-bar">
              <div>
                <h2>{selectedPlaylist.name}</h2>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button 
                    onClick={() => playAll(displaySongs)}
                    className="playlist-action-btn play-all-btn"
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: 'rgba(16, 185, 129, 0.2)',
                      border: '1px solid rgba(16, 185, 129, 0.5)',
                      borderRadius: '6px',
                      color: '#10b981',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}
                  >
                    <span className="btn-icon">▶</span>
                    <span className="btn-text"> Play All ({displaySongs.length})</span>
                  </button>
                  <button 
                    onClick={() => playShuffle(displaySongs)}
                    className="playlist-action-btn shuffle-btn"
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: 'rgba(139, 92, 246, 0.2)',
                      border: '1px solid rgba(139, 92, 246, 0.5)',
                      borderRadius: '6px',
                      color: '#8b5cf6',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}
                  >
                    <span className="btn-icon">🔀</span>
                    <span className="btn-text"> Shuffle ({displaySongs.length})</span>
                  </button>
                </div>
              </div>
              <button 
                onClick={() => setSelectedPlaylist(null)} 
                data-testid="back-to-library-btn"
                className="back-to-library-btn"
              >
                <span className="btn-icon">←</span>
                <span className="btn-text"> Back to Library</span>
              </button>
            </div>
          )}

          <div className="jukebox-grid">
            {displaySongs.map((song) => (
              <div 
                key={song.id} 
                className={`jukebox-card ${isPlayerVisible && playQueue[currentTrackIndex]?.id === song.id ? 'now-playing' : ''}`}
                data-testid={`jukebox-song-${song.id}`}
              >
                <div className="song-info">
                  <h3 data-testid={`jukebox-song-title-${song.id}`}>
                    {isPlayerVisible && playQueue[currentTrackIndex]?.id === song.id && (
                      <span className="now-playing-indicator">🎵 </span>
                    )}
                    {song.title}
                  </h3>
                  <p data-testid={`jukebox-song-artist-${song.id}`}>{song.artist}</p>
                  {song.album && <p className="album">{song.album}</p>}
                  {song.genre && (
                    <div className="genre-tags">
                      {song.genre.split(',').map((g, idx) => (
                        <span key={`genre-${idx}-${g.trim()}`} className="genre-tag">{g.trim()}</span>
                      ))}
                    </div>
                  )}

                  {/* Star counts removed from My Jukebox - visible only in Artist Dashboard catalog */}
                  
                  {/* Show Purchase button in Loved Songs, Add to Mix Tape in Library */}
                  {!selectedPlaylist && !showLovedSongs && (
                    <button 
                      className="add-to-playlist-btn"
                      onClick={() => {
                        setSelectedSongForPlaylist(song.id);
                        setShowAddToPlaylist(true);
                      }}
                      data-testid={`add-to-playlist-${song.id}`}
                    >
                      + Add to Mix Tape
                    </button>
                  )}
                  
                  {showLovedSongs && (
                    <button 
                      className="purchase-btn"
                      onClick={() => handlePurchaseFromLoved(song.id)}
                      data-testid={`loved-purchase-btn-${song.id}`}
                      style={{ marginTop: '0.5rem', width: '100%' }}
                    >
                      🎵 Add to Jukebox ({song.token_cost} token{song.token_cost !== 1 ? 's' : ''})
                    </button>
                  )}
                  
                  {selectedPlaylist && (
                    <button 
                      className="remove-from-playlist-btn"
                      onClick={() => removeSongFromPlaylist(song.id)}
                      data-testid={`remove-from-playlist-${song.id}`}
                    >
                      Remove from Mix Tape
                    </button>
                  )}

                  <div className="song-actions">
                    {(() => {
                      // Check if user owns this song (it's in their purchased jukebox)
                      const userOwnsSong = songs.some(ownedSong => ownedSong.id === song.id);
                      
                      // In Loved Songs section, show preview for non-owned songs
                      if (showLovedSongs && !userOwnsSong) {
                        // User starred but didn't purchase - show preview only
                        if (song.preview_url) {
                          return (
                            <>
                              <div style={{ marginTop: '10px', color: '#888', fontSize: '0.9em' }}>
                                🎧 30s Preview (Purchase to unlock full song)
                              </div>
                              <audio data-testid={`audio-player-${song.id}`} controls preload="none" style={{ width: '100%', marginTop: '5px' }}>
                                <source src={song.preview_url} type="audio/mpeg" />
                              </audio>
                            </>
                          );
                        }
                        return null;
                      }
                      
                      // User owns the song - show full audio
                      if (song.audio_url) {
                        return (
                          <>
                            <audio data-testid={`audio-player-${song.id}`} controls preload="none" style={{ width: '100%', marginTop: '10px' }}>
                              <source src={song.audio_url} type="audio/mpeg" />
                            </audio>
                            <a 
                              data-testid={`download-btn-${song.id}`}
                              href={song.audio_url} 
                              download 
                              className="download-btn"
                            >
                              Download MP3
                            </a>
                          </>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!showLovedSongs && songs.length === 0 && (
            <div className="empty-state" data-testid="empty-jukebox">
              <p>Your jukebox is empty. Browse songs to get started!</p>
            </div>
          )}

          {songs.length > 0 && displaySongs.length === 0 && !selectedPlaylist && !showUnplaylisted && !showLovedSongs && (
            <div className="empty-state" data-testid="no-results">
              <p>No songs found matching your search.</p>
            </div>
          )}

          {songs.length > 0 && displaySongs.length === 0 && showUnplaylisted && (
            <div className="empty-state" data-testid="no-unplaylisted">
              <p>All your songs are organized in playlists! 🎉</p>
            </div>
          )}

          {displaySongs.length === 0 && showLovedSongs && (
            <div className="empty-state" data-testid="no-loved-songs">
              <p>You haven't shown love to any songs yet. Browse songs and click the ❤️ button!</p>
            </div>
          )}

          {selectedPlaylist && displaySongs.length === 0 && (
            <div className="empty-state">
              <p>This playlist is empty. Add songs from your library!</p>
            </div>
          )}
        </div>
      </div>

      {/* Add to Mix Tape Modal */}
      {showAddToPlaylist && (
        <div className="modal-overlay" onClick={() => setShowAddToPlaylist(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add to Mix Tape</h3>
            <div className="playlist-list">
              {playlists.map(playlist => (
                <button
                  key={playlist.id}
                  className="playlist-option"
                  onClick={() => addSongToPlaylist(playlist.id)}
                  data-testid={`add-to-playlist-option-${playlist.id}`}
                >
                  {playlist.name}
                </button>
              ))}
              {playlists.length === 0 && <p>No mix tapes yet. Create one first!</p>}
            </div>
            <button onClick={() => setShowAddToPlaylist(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Mixtape Creation Modal */}
      {/* Mixtape modal removed */}

      {/* Mini Player */}
      {isPlayerVisible && playQueue.length > 0 && (
        <div className="mini-player">
          <audio
            ref={(audio) => {
              setAudioRef(audio);
              if (audio && isPlaying) {
                audio.play().catch(e => console.error('Playback failed:', e));
              }
            }}
            src={playQueue[currentTrackIndex]?.audio_url}
            autoPlay={isPlaying}
            onEnded={playNext}
            onError={(e) => {
              console.error('Audio error:', e);
              toast.error('Failed to play song');
              playNext();
            }}
          />
          
          <div className="mini-player-content">
            {/* Song Info */}
            <div className="player-song-info">
              <div 
                className="player-album-art" 
                style={{ 
                  backgroundImage: playQueue[currentTrackIndex]?.album_art_url 
                    ? `url(${playQueue[currentTrackIndex].album_art_url})` 
                    : 'none',
                  backgroundColor: 'rgba(139, 92, 246, 0.3)'
                }}
              >
                {!playQueue[currentTrackIndex]?.album_art_url && <span>🎵</span>}
              </div>
              <div className="player-song-details">
                <div className="player-song-title">{playQueue[currentTrackIndex]?.title}</div>
                <div className="player-song-artist">{playQueue[currentTrackIndex]?.artist}</div>
              </div>
            </div>

            {/* Controls */}
            <div className="player-controls">
              <button 
                onClick={playPrevious}
                disabled={currentTrackIndex === 0}
                className="player-btn"
                title="Previous"
              >
                ⏮
              </button>
              <button 
                onClick={() => {
                  if (audioRef) {
                    if (isPlaying) {
                      audioRef.pause();
                      setIsPlaying(false);
                    } else {
                      audioRef.play();
                      setIsPlaying(true);
                    }
                  }
                }}
                className="player-btn player-btn-play"
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? '⏸' : '▶'}
              </button>
              <button 
                onClick={playNext}
                disabled={currentTrackIndex >= playQueue.length - 1}
                className="player-btn"
                title="Next"
              >
                ⏭
              </button>
            </div>

            {/* Queue Info */}
            <div className="player-queue-info">
              <span>{currentTrackIndex + 1} / {playQueue.length}</span>
            </div>

            {/* Close Button */}
            <button onClick={closeMiniPlayer} className="player-close" title="Close Player">
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Account Page
const Account = () => {
  const { user, refetchUser } = useAuth();
  const [balance, setBalance] = useState(0);
  const [starBalance, setStarBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loyaltyStats, setLoyaltyStats] = useState(null);
  const [username, setUsername] = useState('');
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBalance();
    fetchLoyaltyStats();
    fetchUsername();
  }, [user]);

  const fetchBalance = async () => {
    try {
      const response = await axios.get(`${API}/user/balance`);
      setBalance(response.data.token_balance);
      setStarBalance(response.data.star_balance || 0);
    } catch (error) {
      console.error('Failed to fetch balance');
    }
  };

  const fetchLoyaltyStats = async () => {
    try {
      const response = await axios.get(`${API}/user/loyalty-stats`);
      setLoyaltyStats(response.data);
    } catch (error) {
      console.debug('Failed to fetch loyalty stats');
    }
  };

  const fetchUsername = async () => {
    try {
      const response = await axios.get(`${API}/user/username`);
      setUsername(response.data.username);
      setNewUsername(response.data.username);
    } catch (error) {
      console.debug('Failed to fetch username');
    }
  };

  const handleUpdateUsername = async () => {
    if (!newUsername.trim()) {
      toast.error('Username cannot be empty');
      return;
    }
    try {
      const response = await axios.post(`${API}/user/username`, { username: newUsername.trim() });
      setUsername(response.data.username);
      setEditingUsername(false);
      toast.success('Username updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update username');
    }
  };

  const handleBuyTokens = async (packageId) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      toast.info('Redirecting to payment...');
      
      const response = await axios.post(`${API}/user/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      });
      
      if (response.data.url) {
        toast.success('Opening checkout in new tab...');
        // Open Stripe checkout in new tab (works in iframe environment)
        const checkoutWindow = window.open(response.data.url, '_blank');
        if (!checkoutWindow) {
          toast.error('Please allow popups to complete checkout');
        }
        setLoading(false); // Reset loading state after opening checkout
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate checkout. Please try again.');
    } finally {
      setLoading(false); // Always reset loading state
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      
      toast.success('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };
  

  return (
    <div className="account-page">
      <div className="account-header">
        <h1 data-testid="account-title">My Account</h1>
        
        {/* Username Section */}
        <div style={{
          background: 'rgba(139, 92, 246, 0.1)',
          borderRadius: '12px',
          padding: '1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.75rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Your Listener Tag:</span>
            {editingUsername ? (
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="Enter username"
                maxLength={30}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '8px',
                  padding: '0.5rem 0.75rem',
                  color: '#fff',
                  fontSize: '1rem',
                  width: '180px'
                }}
              />
            ) : (
              <span style={{ 
                color: '#a5b4fc', 
                fontWeight: '600', 
                fontSize: '1.1rem' 
              }}>
                {username || 'Loading...'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {editingUsername ? (
              <>
                <button
                  onClick={handleUpdateUsername}
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingUsername(false);
                    setNewUsername(username);
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditingUsername(true)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.5rem 1rem',
                  color: '#a5b4fc',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                ✏️ Edit
              </button>
            )}
          </div>
        </div>
        
        <div className="balance-display">
          <div className="token-balance" data-testid="token-balance">
            <span className="balance-label">Token Balance:</span>
            <span className="balance-amount">{balance} tokens</span>
          </div>
          <div className="star-balance" data-testid="star-balance">
            <span className="balance-label">Star Balance:</span>
            <span className="balance-amount">{starBalance} stars</span>
          </div>
        </div>
      </div>

      <div className="token-packages">
        <h2 data-testid="packages-title">Buy Tokens & Stars</h2>
        <p data-testid="packages-subtitle">Use tokens to add songs to your jukebox • Use stars to show love!</p>
        <div className="packages-grid">
          <div className="package-card" data-testid="package-1.99">
            <h3>2 Tokens</h3>
            <p className="bonus-stars">+ 4 Stars ⭐</p>
            <p className="price">$1.99</p>
            <button data-testid="buy-1.99-btn" onClick={() => handleBuyTokens('1.99')} disabled={loading}>
              {loading ? 'Processing...' : 'Purchase'}
            </button>
          </div>
          <div className="package-card" data-testid="package-2.99">
            <h3>5 Tokens</h3>
            <p className="bonus-stars">+ 10 Stars ⭐</p>
            <p className="price">$2.99</p>
            <button data-testid="buy-2.99-btn" onClick={() => handleBuyTokens('2.99')} disabled={loading}>
              {loading ? 'Processing...' : 'Purchase'}
            </button>
          </div>
          <div className="package-card popular" data-testid="package-4.99">
            <div className="popular-badge">Best Value</div>
            <h3>10 Tokens</h3>
            <p className="bonus-stars">+ 15 Stars ⭐</p>
            <p className="price">$4.99</p>
            <button data-testid="buy-4.99-btn" onClick={() => handleBuyTokens('4.99')} disabled={loading}>
              {loading ? 'Processing...' : 'Purchase'}
            </button>
          </div>
          <div className="package-card superfan" data-testid="package-19.99">
            <div className="popular-badge">Superfan</div>
            <h3>40 Tokens</h3>
            <p className="bonus-stars">+ 40 Stars ⭐</p>
            <p className="price">$19.99</p>
            <button data-testid="buy-19.99-btn" onClick={() => handleBuyTokens('19.99')} disabled={loading}>
              {loading ? 'Processing...' : 'Purchase'}
            </button>
          </div>
        </div>
      </div>

      {/* Listener Loyalty Stats (Phase 0/1) */}
      {loyaltyStats && (
        <div className="loyalty-stats-section" style={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)',
          borderRadius: '16px',
          padding: '1.5rem',
          marginBottom: '2rem',
          border: '1px solid rgba(139, 92, 246, 0.2)'
        }}>
          <h2 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            🎧 Listener Stats
          </h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
            gap: '1rem' 
          }}>
            <div style={{
              background: 'rgba(139, 92, 246, 0.15)',
              borderRadius: '12px',
              padding: '1rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6' }}>
                {loyaltyStats.monthly_points || 0}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#a5b4fc' }}>Monthly Points</div>
            </div>
            <div style={{
              background: 'rgba(16, 185, 129, 0.15)',
              borderRadius: '12px',
              padding: '1rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
                {loyaltyStats.total_listening_minutes}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#6ee7b7' }}>Minutes Listened</div>
            </div>
            <div style={{
              background: 'rgba(251, 191, 36, 0.15)',
              borderRadius: '12px',
              padding: '1rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#fbbf24' }}>
                {loyaltyStats.songs_rated}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#fcd34d' }}>Love Shown ⭐</div>
            </div>
            <div style={{
              background: 'rgba(59, 130, 246, 0.15)',
              borderRadius: '12px',
              padding: '1rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>
                {loyaltyStats.days_active_count}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#93c5fd' }}>Days Active</div>
            </div>
          </div>
          
          {/* Lifetime Points - smaller display */}
          <div style={{
            marginTop: '0.75rem',
            padding: '0.5rem 1rem',
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
              Lifetime Points: <strong style={{ color: '#94a3b8' }}>{loyaltyStats.lifetime_points || 0}</strong>
            </span>
          </div>
          
          <div style={{ 
            marginTop: '0.75rem', 
            padding: '0.75rem 1rem',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            <span style={{ color: '#a5b4fc' }}>
              Current Tier: <strong style={{ 
                color: loyaltyStats.listener_tier === 'insider' ? '#fbbf24' : 
                       loyaltyStats.listener_tier === 'curator' ? '#8b5cf6' : 
                       loyaltyStats.listener_tier === 'contributor' ? '#10b981' : '#94a3b8',
                textTransform: 'capitalize'
              }}>
                {loyaltyStats.listener_tier === 'new' ? 'New Listener' : loyaltyStats.listener_tier}
              </strong>
            </span>
            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
              {loyaltyStats.listener_tier === 'new' && `${50 - (loyaltyStats.monthly_points || 0)} pts to Contributor`}
              {loyaltyStats.listener_tier === 'contributor' && `${100 - (loyaltyStats.monthly_points || 0)} pts to Curator`}
              {loyaltyStats.listener_tier === 'curator' && `${200 - (loyaltyStats.monthly_points || 0)} pts to Insider`}
              {loyaltyStats.listener_tier === 'insider' && '🏆 Top tier reached!'}
            </span>
          </div>
          
          {/* Monthly reset info */}
          <p style={{ 
            color: '#64748b', 
            fontSize: '0.75rem', 
            textAlign: 'center',
            marginTop: '0.75rem',
            fontStyle: 'italic'
          }}>
            Monthly points and tiers reset on the 1st of each month. Compete for monthly prizes!
          </p>

          {/* Ways to Earn Points */}
          <div style={{
            marginTop: '1.25rem',
            padding: '1rem',
            background: 'rgba(139, 92, 246, 0.1)',
            borderRadius: '12px',
            border: '1px solid rgba(139, 92, 246, 0.2)'
          }}>
            <h4 style={{ margin: '0 0 0.75rem 0', color: '#a5b4fc', fontSize: '0.9rem', textAlign: 'center' }}>
              ✨ Ways to Earn Points
            </h4>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: '0.5rem',
              fontSize: '0.75rem',
              color: '#94a3b8'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ color: '#8b5cf6' }}>🎧</span> Listen: 1pt/10min
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ color: '#fbbf24' }}>⭐</span> Show Love: 2pts
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ color: '#10b981' }}>📝</span> Feedback: 5pts
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ color: '#3b82f6' }}>📅</span> Daily Bonus: 3pts
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ color: '#ec4899' }}>🎵</span> Buy Song: 5pts
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ color: '#f59e0b' }}>💳</span> Token Pack: 10pts
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Section */}
      <div className="password-section">
        <h2>🔒 Change Password</h2>
        <form onSubmit={handlePasswordChange} className="password-form">
          <div className="form-group">
            <label>Current Password</label>
            <input
              type="password"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
              required
              placeholder="Enter current password"
            />
          </div>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
              required
              minLength="6"
              placeholder="Enter new password (min 6 characters)"
            />
          </div>
          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
              required
              minLength="6"
              placeholder="Confirm new password"
            />
          </div>
          <button type="submit" className="change-password-btn" disabled={changingPassword}>
            {changingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
      
    </div>
  );
};

// Payment Success Page
const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refetchUser } = useAuth();
  const [status, setStatus] = useState('processing');
  const [tokens, setTokens] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [location]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 20; // Increased from 10 to 20 (40 seconds total)
    
    if (attempts >= maxAttempts) {
      // On timeout, refresh user data anyway - payment might have succeeded
      try {
        await refetchUser();
      } catch (e) {
        console.error('Failed to refetch user:', e);
      }
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/user/checkout-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setTokens(response.data.tokens);
        await refetchUser();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Payment status check error:', error);
      // On error, try to refresh user data in case payment went through
      if (attempts >= 3) {
        try {
          await refetchUser();
        } catch (e) {
          console.error('Failed to refetch user:', e);
        }
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  };

  return (
    <div className="payment-success-page">
      <div className="success-card" data-testid="payment-status">
        {status === 'processing' && (
          <>
            <div className="spinner"></div>
            <h2>Processing Payment...</h2>
            <p>Please wait while we confirm your transaction</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="success-icon">✓</div>
            <h2 data-testid="success-message">Payment Successful!</h2>
            <p data-testid="tokens-added">{tokens} tokens have been added to your account</p>
            <button data-testid="continue-btn" onClick={() => navigate('/songs')}>Browse Songs</button>
          </>
        )}
        {status === 'failed' && (
          <>
            <div className="error-icon">✗</div>
            <h2>Payment Failed</h2>
            <p>Your payment session has expired. Please try again.</p>
            <button onClick={() => navigate('/account')}>Try Again</button>
          </>
        )}
        {(status === 'timeout' || status === 'error') && (
          <>
            <div className="error-icon">⏱</div>
            <h2>Payment Processing</h2>
            <p>Your payment is being processed. If tokens don't appear in your account within 2 minutes, please contact support.</p>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#9ca3af' }}>
              Most payments complete within 30 seconds.
            </p>
            <button onClick={() => { refetchUser(); navigate('/account'); }}>Check My Account</button>
          </>
        )}
      </div>
    </div>
  );
};

// Artist Dashboard
const ArtistDashboard = () => {
  const [earnings, setEarnings] = useState({ 
    token_balance: 0, 
    token_value: 0,
    star_balance: 0,
    star_value: 0,
    total_value: 0,
    transactions: [] 
  });
  const [payouts, setPayouts] = useState([]);
  const [cashoutAmount, setCashoutAmount] = useState('');
  const [connectionStatus, setConnectionStatus] = useState({ connected: false, details_submitted: false });
  const [connectingAccount, setConnectingAccount] = useState(false);
  const [artistProfile, setArtistProfile] = useState({ artist_name: '', artist_link: '', bio: '' });
  const [editingProfile, setEditingProfile] = useState(false);
  const [mySongs, setMySongs] = useState([]);
  const [editingSong, setEditingSong] = useState(null);
  const [songLimits, setSongLimits] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null, onCancel: null, showCheckbox: false, checkboxLabel: '', checkboxChecked: false });
  const [activeTab, setActiveTab] = useState('earnings'); // 'earnings', 'profile', 'catalog', 'sales'
  const [salesReport, setSalesReport] = useState({ summary: [], total_tokens: 0, total_stars: 0 });
  const { user, refetchUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    fetchEarnings();
    fetchPayouts();
    fetchConnectionStatus();
    fetchProfile();
    fetchMySongs();
    fetchSongLimits();
    
    // Check if returning from Stripe onboarding
    const params = new URLSearchParams(location.search);
    if (params.get('connected') === 'true') {
      toast.success('Bank account connected successfully!');
      fetchConnectionStatus();
      // Clean URL
      window.history.replaceState({}, '', '/artist');
    }
  }, [location]);

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API}/artist/earnings`);
      setEarnings(response.data);
    } catch (error) {
      toast.error('Failed to load earnings');
    }
  };

  const fetchPayouts = async () => {
    try {
      const response = await axios.get(`${API}/artist/payouts`);
      setPayouts(response.data);
    } catch (error) {
      console.error('Failed to load payouts');
    }
  };

  const fetchConnectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/artist/connection-status`);
      setConnectionStatus(response.data);
    } catch (error) {
      console.error('Failed to load connection status');
    }
  };

  const fetchProfile = async () => {
    if (user) {
      setArtistProfile({
        artist_name: user.artist_name || '',
        artist_link: user.artist_link || '',
        bio: user.bio || ''
      });
    }
  };

  const fetchMySongs = async () => {
    try {
      const response = await axios.get(`${API}/songs`);
      // Filter to only this artist's JUKEBOX songs (rotation songs go through admin approval)
      const artistSongs = response.data.filter(song => 
        song.artist_email === user?.email && song.category === 'jukebox'
      );
      setMySongs(artistSongs);
    } catch (error) {
      console.error('Failed to load songs');
    }
  };

  const fetchSongLimits = async () => {
    try {
      const response = await axios.get(`${API}/artist/song-limits`);
      setSongLimits(response.data);
    } catch (error) {
      console.error('Failed to load song limits');
    }
  };

  const fetchSalesReport = async () => {
    try {
      const response = await axios.get(`${API}/artist/sales-report`);
      setSalesReport(response.data);
    } catch (error) {
      toast.error('Failed to load sales report');
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('profile_picture', file);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/artist/upload-profile-picture`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Profile picture uploaded!');
      refetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload profile picture');
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await axios.put(`${API}/artist/profile`, null, {
        params: {
          artist_name: artistProfile.artist_name,
          artist_link: artistProfile.artist_link,
          bio: artistProfile.bio
        }
      });
      toast.success('Profile updated successfully!');
      setEditingProfile(false);
      refetchUser();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleRequestRotationRemoval = (song) => {
    setConfirmDialog({
      show: true,
      message: `Request removal of "${song.title}" from radio rotation?\n\nThis will notify the admin. The song will remain in your Jukebox catalog.`,
      onConfirm: async () => {
        setConfirmDialog({ show: false, message: '', onConfirm: null, onCancel: null });
        try {
          await axios.post(`${API}/artist/request-removal`, {
            song_id: song.id,
            title: song.title,
            artist_name: song.artist,
            genre: song.genre,
            album: song.album,
            reason: 'Artist requested removal from radio rotation only'
          });
          toast.success('Removal request sent to admin');
        } catch (error) {
          toast.error('Failed to send removal request');
        }
      },
      onCancel: () => {
        setConfirmDialog({ show: false, message: '', onConfirm: null, onCancel: null });
      }
    });
  };

  const handleDeleteSong = (songId) => {
    // Find the song to check if it's a jukebox song
    const song = mySongs.find(s => s.id === songId);
    
    if (!song) {
      toast.error('Song not found');
      return;
    }

    // Show confirmation with optional checkbox for jukebox songs
    setConfirmDialog({
      show: true,
      message: `Are you sure you want to delete "${song.title}" from your Jukebox catalog?\n\nThis cannot be undone.`,
      showCheckbox: song.category === 'jukebox',
      checkboxLabel: 'Also remove from radio rotation (sends request to admin)',
      checkboxChecked: false,
      onConfirm: async (checkboxValue) => {
        setConfirmDialog({ show: false, message: '', onConfirm: null, onCancel: null, showCheckbox: false, checkboxLabel: '', checkboxChecked: false });
        
        // If checkbox was checked, send removal request
        if (checkboxValue) {
          try {
            await axios.post(`${API}/artist/request-removal`, {
              song_id: songId,
              title: song.title,
              artist_name: song.artist,
              genre: song.genre,
              album: song.album,
              reason: 'Artist requested removal from radio rotation during catalog deletion'
            });
            toast.info('Removal request sent to admin for radio rotation');
          } catch (error) {
            console.error('Failed to create removal request:', error);
            toast.error('Failed to create removal request');
          }
        }
        
        // Delete from jukebox catalog
        try {
          await axios.delete(`${API}/songs/${songId}`);
          toast.success('Song removed from catalog');
          fetchMySongs();
          fetchSongLimits();
        } catch (error) {
          console.error('Failed to delete song:', error);
          toast.error('Failed to delete song');
        }
      },
      onCancel: () => {
        setConfirmDialog({ show: false, message: '', onConfirm: null, onCancel: null, showCheckbox: false, checkboxLabel: '', checkboxChecked: false });
      }
    });
  };

  const handleConnectAccount = async () => {
    setConnectingAccount(true);
    try {
      const response = await axios.get(`${API}/artist/connect-account`);
      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to initiate account connection');
      setConnectingAccount(false);
    }
  };

  const handleCashout = async () => {
    const dollarAmount = parseFloat(cashoutAmount);
    if (!dollarAmount || dollarAmount <= 0) {
      toast.error('Please enter a valid dollar amount');
      return;
    }

    if (dollarAmount < 2.50) {
      toast.error('Minimum cashout is $2.50');
      return;
    }

    if (!connectionStatus.connected || !connectionStatus.payouts_enabled) {
      toast.error('Please connect your bank account first');
      return;
    }

    try {
      const response = await axios.post(`${API}/artist/cashout`, { amount: dollarAmount });
      toast.success(response.data.message);
      setCashoutAmount('');
      fetchEarnings();
      fetchPayouts();
      refetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cashout failed');
    }
  };

  return (
    <div className="artist-dashboard">
      <div className="dashboard-header">
        <h1 data-testid="artist-title">Artist Dashboard</h1>
        <button 
          className="submit-track-btn cta-btn"
          onClick={() => window.location.href = '/artist/submit'}
        >
          🎵 Submit for Radio Play
        </button>
      </div>

      {/* Dashboard Tabs */}
      <div className="dashboard-tabs">
        <button 
          className={`tab-btn ${activeTab === 'earnings' ? 'active' : ''}`}
          onClick={() => setActiveTab('earnings')}
        >
          💰 Earnings
        </button>
        <button 
          className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          👤 Profile
        </button>
        <button 
          className={`tab-btn ${activeTab === 'catalog' ? 'active' : ''}`}
          onClick={() => setActiveTab('catalog')}
        >
          🎵 My Catalog
        </button>
        <button 
          className={`tab-btn ${activeTab === 'sales' ? 'active' : ''}`}
          onClick={() => { setActiveTab('sales'); fetchSalesReport(); }}
        >
          📊 Sales Report
        </button>
      </div>

      {/* Earnings Tab */}
      {activeTab === 'earnings' && (
        <>
          {/* Bank Connection Status */}
      <div className={`connection-card ${connectionStatus.connected ? 'connected' : 'disconnected'}`} data-testid="connection-card">
        <h2>Bank Account Status</h2>
        {!connectionStatus.connected && (
          <div className="connection-content">
            <p className="connection-message">Connect your bank account to receive automatic payouts (2-3 business days)</p>
            <button 
              className="connect-bank-btn"
              onClick={handleConnectAccount}
              disabled={connectingAccount}
              data-testid="connect-bank-btn"
            >
              {connectingAccount ? 'Redirecting...' : '🔗 Connect Bank Account'}
            </button>
          </div>
        )}
        {connectionStatus.connected && !connectionStatus.payouts_enabled && (
          <div className="connection-content">
            <p className="connection-message warning">⚠️ Account setup incomplete. Please complete verification.</p>
            <button 
              className="connect-bank-btn"
              onClick={handleConnectAccount}
              data-testid="complete-setup-btn"
            >
              Complete Setup
            </button>
          </div>
        )}
        {connectionStatus.connected && connectionStatus.payouts_enabled && (
          <div className="connection-content">
            <p className="connection-message success">✓ Bank account connected - Ready for payouts</p>
            <button 
              className="update-account-btn"
              onClick={handleConnectAccount}
              data-testid="update-account-btn"
            >
              Update Bank Info
            </button>
          </div>
        )}
      </div>
      
      <div className="earnings-card" data-testid="earnings-card">
        <h2>💰 Earnings Summary</h2>
        {earnings && (
          <div className="earnings-stats">
            <div className="stat-box">
              <h3>Total Lifetime Earnings</h3>
              <p className="stat-value">${earnings.total_lifetime_earnings || '0.00'}</p>
              <p className="stat-detail">
                ${earnings.lifetime_token_earnings || '0.00'} from tokens + ${earnings.lifetime_star_earnings || '0.00'} from stars
              </p>
            </div>
            
            <div className="stat-box">
              <h3>Available to Cash Out</h3>
              <p className="stat-value">${earnings.available_to_cashout || '0.00'}</p>
              <p className="stat-detail">
                {earnings.earned_tokens || 0} tokens earned (${earnings.earned_tokens_value || '0.00'}) + {earnings.earned_stars || 0} stars (${earnings.star_balance_value || '0.00'})
              </p>
              {earnings.can_cashout ? (
                <p className="stat-status success">✓ Can cash out now (min $2.50)</p>
              ) : (
                <p className="stat-status warning">Need ${((earnings.min_cashout || 2.5) - (earnings.available_to_cashout || 0)).toFixed(2)} more to cash out</p>
              )}
            </div>
          </div>
        )}
        <div className="earnings-note">
          <p>💡 <strong>Lifetime Earnings</strong>: Total $ earned from all sales (includes cashed out amounts)</p>
          <p>💡 <strong>Available to Cash Out</strong>: Current balance you can withdraw now</p>
          <p>💡 After you cash out, your available balance resets but lifetime earnings stay</p>
        </div>
      </div>

      <div className="cashout-section">
        <h2>💰 Cash Out</h2>
        <div style={{ background: 'rgba(139, 92, 246, 0.1)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>Available to Cash Out: ${earnings.available_to_cashout || '0.00'}</h3>
          <div style={{ fontSize: '0.9rem', color: '#9ca3af' }}>
            <div>💎 From Tokens: ${earnings.earned_tokens_value || '0.00'} ({earnings.earned_tokens || 0} tokens × $0.25)</div>
            <div>⭐ From Stars: ${earnings.star_balance_value || '0.00'} ({earnings.earned_stars || 0} stars × $0.02)</div>
          </div>
        </div>
        <p data-testid="cashout-rate">Minimum cashout: $2.50 | Standard transfer: 2-3 business days | You receive the full amount!</p>
        {connectionStatus.connected && connectionStatus.payouts_enabled ? (
          <div className="cashout-form">
            <input
              data-testid="cashout-input"
              type="number"
              step="0.01"
              min="2.50"
              placeholder="Dollar amount (e.g., 5.00)"
              value={cashoutAmount}
              onChange={(e) => setCashoutAmount(e.target.value)}
            />
            {cashoutAmount && parseFloat(cashoutAmount) > 0 && (
              <div style={{ 
                fontSize: '0.85rem', 
                color: '#9ca3af', 
                marginTop: '0.5rem',
                padding: '0.5rem',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '4px'
              }}>
                {(() => {
                  const requestedAmount = parseFloat(cashoutAmount);
                  const tokensValue = earnings.earned_tokens_value || 0;
                  const starsValue = earnings.star_balance_value || 0;
                  const tokensCount = earnings.earned_tokens || 0;
                  const starsCount = earnings.earned_stars || 0;
                  
                  if (requestedAmount > tokensValue + starsValue) {
                    return <span style={{ color: '#ef4444' }}>⚠️ Amount exceeds available balance (${(tokensValue + starsValue).toFixed(2)})</span>;
                  }
                  
                  // Calculate what will be used (tokens first, then stars)
                  let tokensToUse = Math.min(tokensCount, Math.floor(requestedAmount / 0.25));
                  let tokensUsedValue = tokensToUse * 0.25;
                  let remainingNeeded = requestedAmount - tokensUsedValue;
                  let starsToUse = Math.ceil(remainingNeeded / 0.02);
                  let starsUsedValue = starsToUse * 0.02;
                  
                  // Adjust if we don't have enough stars
                  if (starsToUse > starsCount) {
                    starsToUse = starsCount;
                    starsUsedValue = starsToUse * 0.02;
                  }
                  
                  const totalUsed = tokensUsedValue + starsUsedValue;
                  
                  return (
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>This cashout will use:</div>
                      {tokensToUse > 0 && <div>• {tokensToUse} tokens (${tokensUsedValue.toFixed(2)})</div>}
                      {starsToUse > 0 && <div>• {starsToUse} stars (${starsUsedValue.toFixed(3)})</div>}
                      <div style={{ marginTop: '0.25rem', fontWeight: 600 }}>Total: ${totalUsed.toFixed(2)}</div>
                    </div>
                  );
                })()}
              </div>
            )}
            <button data-testid="cashout-btn" onClick={handleCashout}>Request Cashout</button>
          </div>
        ) : (
          <p className="cashout-disabled" data-testid="cashout-disabled">Connect your bank account to enable cashouts</p>
        )}
      </div>

      <div className="payouts-history">
        <h2>Payout History</h2>
        {payouts.length > 0 ? (
          <table data-testid="payouts-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Tokens</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((payout) => (
                <tr key={payout.id} data-testid={`payout-${payout.id}`}>
                  <td>{new Date(payout.requested_at).toLocaleDateString()}</td>
                  <td>{payout.tokens_cashed}</td>
                  <td>${payout.payout_amount.toFixed(2)}</td>
                  <td><span className={`status ${payout.status}`}>{payout.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p data-testid="no-payouts">No payout history yet</p>
        )}
      </div>
        </>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="profile-section">
          <h2>Artist Profile</h2>
          <p className="section-description">This information appears on your artist profile page</p>
          
          <div className="profile-form">
            <div className="form-group">
              <label htmlFor="artist_name">Artist/Display Name</label>
              <input
                id="artist_name"
                type="text"
                value={artistProfile.artist_name}
                onChange={(e) => setArtistProfile({...artistProfile, artist_name: e.target.value})}
                placeholder="Enter your artist name"
                disabled={!editingProfile}
              />
            </div>

            <div className="form-group">
              <label htmlFor="artist_link">Find Me Here Link</label>
              <input
                id="artist_link"
                type="url"
                value={artistProfile.artist_link}
                onChange={(e) => setArtistProfile({...artistProfile, artist_link: e.target.value})}
                placeholder="https://your-website.com or social media link"
                disabled={!editingProfile}
              />
              <small>This link appears on your artist profile for listeners to find you</small>
            </div>

            <div className="form-group">
              <label htmlFor="bio">Bio</label>
              <textarea
                id="bio"
                value={artistProfile.bio}
                onChange={(e) => setArtistProfile({...artistProfile, bio: e.target.value})}
                placeholder="Tell listeners about yourself or your project..."
                rows="4"
                disabled={!editingProfile}
              />
              <small>Share your story with listeners (labels, managers, or artists)</small>
            </div>

            <div className="form-group">
              <label htmlFor="profile_picture">Profile Picture</label>
              <input
                id="profile_picture"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleProfilePictureUpload}
                disabled={!editingProfile}
              />
              <small>Upload a profile picture (JPEG, PNG, or WebP)</small>
              {user?.profile_picture_url && (
                <div className="current-profile-pic">
                  <img src={user.profile_picture_url.startsWith('http') ? user.profile_picture_url : `${API}${user.profile_picture_url}`} alt="Current profile" />
                </div>
              )}
            </div>

            <div className="profile-actions">
              {!editingProfile ? (
                <button className="edit-btn" onClick={() => setEditingProfile(true)}>
                  ✏️ Edit Profile
                </button>
              ) : (
                <>
                  <button className="save-btn" onClick={handleUpdateProfile}>
                    💾 Save Changes
                  </button>
                  <button className="cancel-btn" onClick={() => {
                    setEditingProfile(false);
                    fetchProfile();
                  }}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>

          {artistProfile.artist_link && (
            <div className="profile-preview">
              <h3>Preview</h3>
              <p>Listeners will see this button on your profile:</p>
              <a 
                href={artistProfile.artist_link} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="artist-link-btn"
              >
                🔗 Find Me Here
              </a>
            </div>
          )}
        </div>
      )}

      {/* Catalog Tab */}
      {activeTab === 'catalog' && (
        <div className="catalog-section">
          <h2>My Catalog ({mySongs.length} songs)</h2>
          <p className="section-description">Manage your uploaded songs</p>

          {/* Song Limits Display */}
          {songLimits && (
            <div className="song-limits-display">
              <div className="limit-card">
                <h3>🎵 Jukebox Songs</h3>
                <div className="limit-bar">
                  <div 
                    className="limit-progress" 
                    style={{ 
                      width: `${(songLimits.jukebox.current / songLimits.jukebox.limit) * 100}%`,
                      backgroundColor: songLimits.jukebox.remaining <= 5 ? '#ff4757' : '#1dd1a1'
                    }}
                  ></div>
                </div>
                <p className="limit-text">
                  {songLimits.jukebox.current} / {songLimits.jukebox.limit} songs
                  <span className={songLimits.jukebox.remaining === 0 ? 'limit-full' : ''}>
                    {songLimits.jukebox.remaining > 0 ? ` (${songLimits.jukebox.remaining} remaining)` : ' (LIMIT REACHED)'}
                  </span>
                </p>
              </div>
            </div>
          )}

          {mySongs.length > 0 ? (
            <div className="catalog-grid">
              {mySongs.map((song) => (
                <div key={song.id} className="catalog-song-card">
                  <div className="song-details">
                    <h3>{song.title}</h3>
                    <p className="artist-name">{song.artist}</p>
                    {song.album && <p className="album-name">{song.album}</p>}
                    {song.genre && <span className="genre-badge">{song.genre}</span>}
                    <div className="song-actions-catalog">
                      <button 
                        className="edit-genre-btn" 
                        onClick={() => setEditingSong(song)}
                        title="Update genre tags"
                      >
                        🏷️ Edit Genre
                      </button>
                      <button 
                        className="request-removal-btn" 
                        onClick={() => handleRequestRotationRemoval(song)}
                        title="Request removal from radio rotation"
                      >
                        📻 Remove from Rotation
                      </button>
                      <button 
                        className="delete-song-btn" 
                        onClick={() => handleDeleteSong(song.id)}
                        title="Delete song from catalog"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-catalog">
              <p>You haven't uploaded any songs yet.</p>
              <button 
                className="submit-track-btn"
                onClick={() => window.location.href = '/artist/submit'}
              >
                🎵 Submit Your First Song
              </button>
            </div>
          )}
        </div>
      )}

      {/* Edit Genre Modal */}
      {editingSong && (
        <div className="modal-overlay" onClick={() => setEditingSong(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Genre - {editingSong.title}</h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                await axios.put(`${API}/songs/${editingSong.id}/genre`, {
                  genre: editingSong.genre
                });
                toast.success('Genre updated successfully');
                setEditingSong(null);
                fetchMySongs();
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to update genre');
              }
            }}>
              <div className="form-group">
                <label>Genre Tags</label>
                <input
                  type="text"
                  value={editingSong.genre || ''}
                  onChange={(e) => setEditingSong({...editingSong, genre: e.target.value})}
                  placeholder="e.g., Rock, Pop, Hip-Hop"
                  required
                />
                <small>Use commas to separate multiple genres</small>
              </div>
              <div className="modal-actions">
                <button type="submit" className="cta-btn">
                  ✓ Save Changes
                </button>
                <button type="button" className="secondary-btn" onClick={() => setEditingSong(null)}>
                  ✗ Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom Confirmation Dialog */}
      {confirmDialog.show && (
        <div className="modal-overlay" onClick={confirmDialog.onCancel}>
          <div className="modal-content confirmation-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Action</h3>
            <p style={{ whiteSpace: 'pre-line' }}>{confirmDialog.message}</p>
            
            {confirmDialog.showCheckbox && (
              <div className="checkbox-container">
                <label>
                  <input
                    type="checkbox"
                    checked={confirmDialog.checkboxChecked}
                    onChange={(e) => setConfirmDialog({...confirmDialog, checkboxChecked: e.target.checked})}
                  />
                  <span>{confirmDialog.checkboxLabel}</span>
                </label>
              </div>
            )}
            
            <div className="modal-actions">
              <button className="cta-btn" onClick={() => confirmDialog.onConfirm(confirmDialog.checkboxChecked)}>
                ✓ Yes
              </button>
              <button className="secondary-btn" onClick={confirmDialog.onCancel}>
                ✗ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sales Report Tab */}
      {activeTab === 'sales' && (
        <div className="sales-report-content">
          <div className="section-header">
            <h2>📊 Artist Sales Report</h2>
            <button 
              className="export-btn"
              onClick={() => {
                const exportData = salesReport.summary.map(item => ({
                  'Artist Name': item.artist_name,
                  'Songs': item.song_count,
                  'Total Tokens Earned': item.total_tokens,
                  'Total Stars Earned': item.total_stars,
                  'Token Value': `$${(item.total_tokens * 0.25).toFixed(2)}`,
                  'Star Value': `$${(item.total_stars * 0.02).toFixed(2)}`,
                  'Total Value': `$${((item.total_tokens * 0.25) + (item.total_stars * 0.02)).toFixed(2)}`
                }));
                
                if (exportData.length === 0) {
                  toast.error('No sales data to export');
                  return;
                }
                
                const headers = Object.keys(exportData[0]);
                let csv = headers.join(',') + '\n';
                exportData.forEach(row => {
                  const values = headers.map(header => {
                    const value = row[header];
                    if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                      return `"${value.replace(/"/g, '""')}"`;
                    }
                    return value;
                  });
                  csv += values.join(',') + '\n';
                });
                
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `artist_sales_report_${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                toast.success('Sales report exported successfully');
              }}
              disabled={!salesReport.summary || salesReport.summary.length === 0}
            >
              📥 Export to CSV
            </button>
          </div>

          <div className="sales-summary-card">
            <h3>Sales Summary</h3>
            {salesReport.summary && salesReport.summary.length > 0 ? (
              <>
                <div className="summary-grid">
                  {salesReport.summary.map((item, index) => (
                    <div key={item.artist_name || item.artist_email || `sales-${index}`} className="artist-sales-card">
                      <h4>{item.artist_name}</h4>
                      <p><strong>Songs:</strong> {item.song_count}</p>
                      <div className="earnings-breakdown">
                        <div className="token-earnings">
                          <span className="label">🎫 Tokens:</span>
                          <span className="value">{item.total_tokens}</span>
                          <span className="cash-value">(${(item.total_tokens * 0.25).toFixed(2)})</span>
                        </div>
                        <div className="star-earnings">
                          <span className="label">⭐ Stars:</span>
                          <span className="value">{item.total_stars}</span>
                          <span className="cash-value">(${(item.total_stars * 0.02).toFixed(2)})</span>
                        </div>
                      </div>
                      <div className="total-value">
                        <strong>Total Value:</strong> ${((item.total_tokens * 0.25) + (item.total_stars * 0.02)).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="overall-totals">
                  <h3>Overall Totals</h3>
                  <p><strong>Total Tokens Earned:</strong> {salesReport.total_tokens} 🎫 (${(salesReport.total_tokens * 0.25).toFixed(2)})</p>
                  <p><strong>Total Stars Earned:</strong> {salesReport.total_stars} ⭐ (${(salesReport.total_stars * 0.02).toFixed(2)})</p>
                  <p className="grand-total"><strong>Grand Total Value:</strong> ${((salesReport.total_tokens * 0.25) + (salesReport.total_stars * 0.02)).toFixed(2)}</p>
                </div>
              </>
            ) : (
              <p className="empty-message">No sales data available yet. Sales will appear here once your songs start earning tokens and stars.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Panel
const AdminPanel = () => {
  const [songs, setSongs] = useState([]);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [submissionFilter, setSubmissionFilter] = useState('all'); // all, pending, approved, rejected
  const [payables, setPayables] = useState(null);
  const [jukeboxFiles, setJukeboxFiles] = useState(null);
  const [jukeboxSearch, setJukeboxSearch] = useState('');
  const [masterCatalog, setMasterCatalog] = useState([]);
  const [unverifiedUsers, setUnverifiedUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, songs, submissions, jukebox-files, transactions, payouts, payables, admins, credits, catalog, verification, loyalty, artwork
  const [rejectModal, setRejectModal] = useState({ show: false, submissionId: null, reason: '' });
  const [songManagementModal, setSongManagementModal] = useState({ show: false, type: null, userId: null }); // type: 'jukebox' or 'loved'
  const [availableSongs, setAvailableSongs] = useState([]);
  const [songSearchTerm, setSongSearchTerm] = useState('');
  const [selectedSongForUser, setSelectedSongForUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [artworkAudit, setArtworkAudit] = useState(null);
  const [artworkUploading, setArtworkUploading] = useState(null);
  const [adminFormData, setAdminFormData] = useState({
    email: '',
    password: '',
    setup_key: ''
  });
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, message: '', onConfirm: null, onCancel: null, showInput: false, inputValue: '', inputLabel: '' });
  const [creditFormData, setCreditFormData] = useState({
    user_email: '',
    tokens: 0,
    stars: 0,
    reason: ''
  });
  const [resetPasswordData, setResetPasswordData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    artist_email: '',
    album: '',
    album_art_url: '',
    audio_url: '',
    token_cost: 1,
    genre: '',
    isrc: ''
  });
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const [mp3File, setMp3File] = useState(null);
  const [artworkFile, setArtworkFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  
  // Loyalty Report State
  const [loyaltyReport, setLoyaltyReport] = useState(null);
  const [loyaltyMonth, setLoyaltyMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loadingLoyalty, setLoadingLoyalty] = useState(false);
  const [assigningUsernames, setAssigningUsernames] = useState(false);
  
  // Feedback Report State
  const [feedbackReport, setFeedbackReport] = useState(null);
  const [feedbackMonth, setFeedbackMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchSongs();
    fetchTransactions();
    fetchPayouts();
    fetchSubmissions();
    fetchPayables();
    fetchUsers();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load stats');
    }
  };

  const fetchSongs = async () => {
    try {
      const response = await axios.get(`${API}/songs`);
      setSongs(response.data);
    } catch (error) {
      toast.error('Failed to load songs');
    }
  };

  const fetchMasterCatalog = async () => {
    try {
      const response = await axios.get(`${API}/admin/master-catalog`);
      setMasterCatalog(response.data);
    } catch (error) {
      toast.error('Failed to load master catalog');
    }
  };

  const fetchLoyaltyReport = async (month = loyaltyMonth) => {
    setLoadingLoyalty(true);
    try {
      const response = await axios.get(`${API}/admin/loyalty-report?month=${month}`);
      setLoyaltyReport(response.data);
    } catch (error) {
      toast.error('Failed to load loyalty report');
    }
    setLoadingLoyalty(false);
  };

  const exportLoyaltyCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/loyalty-report/csv?month=${loyaltyMonth}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `loyalty_report_${loyaltyMonth}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report exported!');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const assignAllUsernames = async () => {
    setAssigningUsernames(true);
    try {
      const response = await axios.post(`${API}/admin/assign-usernames`);
      toast.success(`Assigned usernames to ${response.data.users_updated} users!`);
      fetchLoyaltyReport();
    } catch (error) {
      toast.error('Failed to assign usernames');
    }
    setAssigningUsernames(false);
  };

  const fetchArtworkAudit = async (verifyUrls = false) => {
    try {
      const response = await axios.get(`${API}/admin/artwork-audit?verify_urls=${verifyUrls}`);
      setArtworkAudit(response.data);
      if (verifyUrls) {
        toast.success('Deep scan complete - URLs verified');
      }
    } catch (error) {
      toast.error('Failed to load artwork audit');
    }
  };

  const handleArtworkUpload = async (songId, file) => {
    setArtworkUploading(songId);
    try {
      const formData = new FormData();
      formData.append('album_art', file);
      
      const response = await axios.post(`${API}/admin/artwork-fix/${songId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message);
      // Use Deep Verify after upload to catch remaining broken URLs
      fetchArtworkAudit(true);
    } catch (error) {
      console.error('Artwork upload error:', error.response?.data || error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to upload artwork';
      toast.error(`Upload failed: ${errorMsg}`);
    }
    setArtworkUploading(null);
  };

  const fetchFeedbackReport = async (month = feedbackMonth) => {
    setLoadingFeedback(true);
    try {
      const response = await axios.get(`${API}/admin/feedback-report?month=${month}`);
      setFeedbackReport(response.data);
    } catch (error) {
      toast.error('Failed to load feedback report');
    }
    setLoadingFeedback(false);
  };

  const exportFeedbackCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/feedback-report/csv?month=${feedbackMonth}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `feedback_report_${feedbackMonth}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Feedback report exported!');
    } catch (error) {
      toast.error('Failed to export feedback report');
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-transactions`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to load transactions');
    }
  };

  const fetchPayouts = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-payouts`);
      setPayouts(response.data);
    } catch (error) {
      console.error('Failed to load payouts');
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/submissions`);
      setSubmissions(response.data || []);
    } catch (error) {
      console.error('Failed to load submissions');
    }
  };


  const fetchJukeboxFiles = async (searchTerm = '') => {
    try {
      const url = searchTerm 
        ? `${API}/admin/jukebox-files?search=${encodeURIComponent(searchTerm)}`
        : `${API}/admin/jukebox-files`;
      const response = await axios.get(url);
      setJukeboxFiles(response.data);
    } catch (error) {
      console.error('Failed to load jukebox files');
      toast.error('Failed to load jukebox files');
    }
  };


  const handleApproveSubmission = (submissionId) => {
    setConfirmDialog({
      show: true,
      message: 'Approve this track submission?',
      onConfirm: async () => {
        setConfirmDialog({ show: false, message: '', onConfirm: null, onCancel: null, showInput: false, inputValue: '', inputLabel: '' });
        try {
          await axios.post(`${API}/admin/approve-submission/${submissionId}`);
          toast.success('Submission approved!');
          fetchSubmissions();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to approve submission');
        }
      },
      onCancel: () => {
        setConfirmDialog({ show: false, message: '', onConfirm: null, onCancel: null, showInput: false, inputValue: '', inputLabel: '' });
      }
    });
  };

  const handleRejectSubmission = (submissionId) => {
    // Show modal to get rejection reason
    setRejectModal({ show: true, submissionId, reason: '' });
  };
  
  const confirmRejectSubmission = async () => {
    try {
      const reason = rejectModal.reason.trim() || 'Rejected by admin';
      await axios.post(`${API}/admin/reject-submission/${rejectModal.submissionId}`, { reason });
      toast.success('Submission rejected with feedback');
      setRejectModal({ show: false, submissionId: null, reason: '' });
      fetchSubmissions();
    } catch (error) {
      console.error('Reject error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject submission');
    }
  };
  
  const openSongManagementModal = async (type, userId) => {
    try {
      // Fetch all jukebox songs
      const response = await axios.get(`${API}/songs`);
      setAvailableSongs(response.data);
      setSongManagementModal({ show: true, type, userId });
      setSongSearchTerm('');
      setSelectedSongForUser(null);
    } catch (error) {
      toast.error('Failed to load songs');
    }
  };
  
  const addSongToUserLibrary = async () => {
    if (!selectedSongForUser) {
      toast.error('Please select a song');
      return;
    }
    
    try {
      if (songManagementModal.type === 'jukebox') {
        // Add to user's jukebox (purchases collection)
        await axios.post(`${API}/admin/add-song-to-user`, {
          user_id: songManagementModal.userId,
          song_id: selectedSongForUser.id
        });
        toast.success(`Added "${selectedSongForUser.title}" to user's Jukebox`);
      } else if (songManagementModal.type === 'loved') {
        // Add to user's loved songs (starred_songs collection)
        await axios.post(`${API}/admin/star-song-for-user`, {
          user_id: songManagementModal.userId,
          song_id: selectedSongForUser.id
        });
        toast.success(`Added "${selectedSongForUser.title}" to user's Loved Songs`);
      }
      
      setSongManagementModal({ show: false, type: null, userId: null });
      setSelectedSongForUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add song');
    }
  };

  const handleDeleteSubmission = (submissionId) => {
    setConfirmDialog({
      show: true,
      message: 'Are you sure you want to delete this submission?\n\nThis cannot be undone.',
      onConfirm: async () => {
        setConfirmDialog({ show: false, message: '', onConfirm: null, onCancel: null, showInput: false, inputValue: '', inputLabel: '' });
        try {
          await axios.delete(`${API}/admin/delete-submission/${submissionId}`);
          toast.success('Submission deleted');
          fetchSubmissions();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to delete submission');
        }
      },
      onCancel: () => {
        setConfirmDialog({ show: false, message: '', onConfirm: null, onCancel: null, showInput: false, inputValue: '', inputLabel: '' });
      }
    });
  };

  const handleViewFiles = async (submission) => {
    const files = submission.files || {};
    
    if (!files.mp3 && !files.album_art && !files.preview) {
      toast.error('No files available');
      return;
    }
    
    const token = localStorage.getItem('token');
    
    // Use fetch to download files properly (avoids SPA routing issues)
    const downloadFile = async (url, filename) => {
      try {
        toast.info(`Downloading ${filename}...`, { autoClose: 2000 });
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Download error:', errorText);
          throw new Error(`Download failed: ${response.status}`);
        }
        
        // Get the file as a blob
        const blob = await response.blob();
        
        // Create a download link from the blob
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
        
        return true;
      } catch (error) {
        console.error('Download failed:', error);
        toast.error(`Failed to download ${filename}`);
        return false;
      }
    };
    
    // Download MP3
    if (files.mp3) {
      await downloadFile(
        `${API}/admin/download-file/${submission.id}/mp3`,
        `${submission.artist_name} - ${submission.title}.mp3`
      );
    }
    
    // Download album art
    if (files.album_art) {
      await downloadFile(
        `${API}/admin/download-file/${submission.id}/album_art`,
        `${submission.artist_name} - ${submission.title}_artwork.jpg`
      );
    }
    
    // Download preview if exists
    if (files.preview) {
      await downloadFile(
        `${API}/admin/download-file/${submission.id}/preview`,
        `${submission.artist_name} - ${submission.title}_preview.mp3`
      );
    }
    
    toast.success('Downloads complete - check your Downloads folder');
  };

  const fetchPayables = async () => {
    try {
      const response = await axios.get(`${API}/admin/payables`);
      setPayables(response.data);
    } catch (error) {
      console.error('Failed to load payables');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handleUserSearch = (searchTerm) => {
    setUserSearchTerm(searchTerm);
    if (!searchTerm) {
      setFilteredUsers(users);
      return;
    }
    
    const filtered = users.filter(user =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.artist_name && user.artist_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredUsers(filtered);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setCreditFormData({
      ...creditFormData,
      user_email: user.email
    });
  };

  const handleDeleteUser = async (userEmail) => {
    console.log('handleDeleteUser called:', userEmail);
    
    setConfirmDialog({
      show: true,
      title: '⚠️ Delete Account',
      message: `Are you sure you want to DELETE ${userEmail}? This action cannot be undone and will remove all user data.`,
      onConfirm: async () => {
        setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });
        
        try {
          console.log('Making API call to delete user...');
          await axios.delete(`${API}/admin/users/${userEmail}`);
          console.log('User deleted successfully');
          
          toast.success(`User account ${userEmail} deleted successfully`);
          setSelectedUser(null);
          fetchUsers();
        } catch (error) {
          console.error('Delete user error:', error);
          toast.error(error.response?.data?.detail || 'Failed to delete user');
        }
      }
    });
  };

  const handleChangeRole = async (userEmail, newRole) => {
    console.log('handleChangeRole called:', userEmail, newRole);
    
    setConfirmDialog({
      show: true,
      title: 'Confirm Role Change',
      message: `Change ${userEmail} from ${selectedUser?.role} to ${newRole}?`,
      onConfirm: async () => {
        setConfirmDialog({ show: false, title: '', message: '', onConfirm: null });
        
        try {
          console.log('Making API call to change role...');
          toast.info('Updating role...', { duration: 1000 });
          
          const response = await axios.put(`${API}/admin/users/${userEmail}/role`, null, {
            params: { new_role: newRole }
          });
          
          console.log('Role change response:', response.data);
          toast.success(response.data.message);
          
          // Refresh user list
          await fetchUsers();
          
          // Update selected user to show new role immediately
          if (selectedUser && selectedUser.email === userEmail) {
            setSelectedUser({ ...selectedUser, role: newRole });
          }
        } catch (error) {
          console.error('Role change error:', error);
          console.error('Error response:', error.response);
          toast.error(error.response?.data?.detail || error.message || 'Failed to change role');
        }
      }
    });
  };


  const fetchUnverifiedUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/unverified-users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUnverifiedUsers(response.data.users || []);
    } catch (error) {
      console.error('Fetch unverified users error:', error);
      toast.error('Failed to load unverified users');
    }
  };

  const handleVerifyUser = async (userEmail) => {
    try {
      await axios.post(`${API}/admin/verify-user/${userEmail}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(`User ${userEmail} has been verified`);
      // Refresh list
      fetchUnverifiedUsers();
    } catch (error) {
      console.error('Verify user error:', error);
      toast.error(error.response?.data?.detail || 'Failed to verify user');
    }
  };


  const handleCreditUser = async (e) => {
    e.preventDefault();
    
    if (!creditFormData.user_email) {
      toast.error('Please select a user');
      return;
    }
    
    if (creditFormData.tokens === 0 && creditFormData.stars === 0) {
      toast.error('Please specify tokens or stars to credit');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/credit-user`, null, {
        params: {
          user_email: creditFormData.user_email,
          tokens: parseInt(creditFormData.tokens) || 0,
          stars: parseInt(creditFormData.stars) || 0,
          reason: creditFormData.reason
        }
      });
      
      toast.success(response.data.message);
      setCreditFormData({ user_email: '', tokens: 0, stars: 0, reason: '' });
      fetchUsers(); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to credit user');
    }
  };

  const handleUpdateUserTag = async (userEmail, newTag) => {
    try {
      const response = await axios.post(`${API}/admin/update-user-tag`, {
        user_email: userEmail,
        username: newTag
      });
      
      toast.success(newTag ? `User tag updated to: ${response.data.username}` : `Random tag assigned: ${response.data.username}`);
      
      // Update selectedUser and refresh users list
      setSelectedUser(prev => ({ ...prev, username: response.data.username }));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user tag');
    }
  };


  const handleAdminResetPassword = async (e) => {
    e.preventDefault();
    
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }
    
    if (resetPasswordData.new_password !== resetPasswordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (resetPasswordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/reset-user-password`, {
        email: selectedUser.email,
        new_password: resetPasswordData.new_password
      });
      
      toast.success(`Password reset successfully for ${selectedUser.email}`);
      setResetPasswordData({ new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };


  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    // For submissions, ensure important fields are included and properly ordered
    let processedData = data;
    if (filename === 'submissions') {
      processedData = data.map(row => ({
        id: row.id || '',
        title: row.title || '',
        artist_name: row.artist_name || '',
        email: row.artist_email || row.submitter_email || '',
        genre: row.genre || '',
        album: row.album || '',
        description: row.description || '',
        isrc: row.isrc || '',
        iswc: row.iswc || '',
        is_cover_song: row.is_cover_song ? 'Yes' : 'No',
        licensing_confirmed: row.licensing_confirmed ? 'Yes' : 'No',
        opt_in_jukebox: row.opt_in_jukebox ? 'Yes' : 'No',
        opt_in_pirate_ship: row.opt_in_pirate_ship ? 'Yes' : 'No',
        status: row.status || '',
        submitted_at: row.submitted_at || '',
        type: row.type || 'track_submission'
      }));
    }
    
    // Get headers from first object
    const headers = Object.keys(processedData[0]);
    
    // Create CSV content
    let csv = headers.join(',') + '\n';
    
    processedData.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        // Handle values that contain commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      });
      csv += values.join(',') + '\n';
    });
    
    // Create download link
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${data.length} records to ${filename}.csv`);
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/create-admin`, {
        email: adminFormData.email,
        password: adminFormData.password,
        setup_key: adminFormData.setup_key,
        role: adminFormData.role || 'listener'
      });
      
      toast.success(`Account created: ${adminFormData.email}`);
      setShowAdminForm(false);
      setAdminFormData({ email: '', password: '', setup_key: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploadProgress(true);
      
      if (uploadMode === 'file') {
        // File upload mode - use FormData
        if (!mp3File) {
          toast.error('Please select an MP3 file');
          setUploadProgress(false);
          return;
        }
        if (!formData.artist_email) {
          toast.error('Please enter the artist\'s email address');
          setUploadProgress(false);
          return;
        }
        
        const uploadFormData = new FormData();
        uploadFormData.append('title', formData.title);
        uploadFormData.append('artist_name', formData.artist);
        uploadFormData.append('artist_email', formData.artist_email);
        uploadFormData.append('album', formData.album || '');
        uploadFormData.append('genre', formData.genre || '');
        uploadFormData.append('token_cost', formData.token_cost);
        uploadFormData.append('isrc', formData.isrc || '');
        uploadFormData.append('mp3_file', mp3File);
        if (artworkFile) {
          uploadFormData.append('album_art', artworkFile);
        }
        
        await axios.post(`${API}/admin/songs/upload`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        toast.success('Song uploaded and added successfully!');
        setMp3File(null);
        setArtworkFile(null);
      } else {
        // URL mode - legacy behavior
        if (!formData.audio_url) {
          toast.error('Please enter an audio URL');
          setUploadProgress(false);
          return;
        }
        await axios.post(`${API}/admin/songs`, formData);
        toast.success('Song added successfully!');
      }
      
      setShowForm(false);
      setFormData({ title: '', artist: '', artist_email: '', album: '', album_art_url: '', audio_url: '', token_cost: 1, genre: '', isrc: '' });
      fetchSongs();
      fetchStats();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to add song';
      toast.error(errorMsg);
    } finally {
      setUploadProgress(false);
    }
  };

  const handleDelete = async (songId) => {
    if (!window.confirm('Are you sure you want to delete this song?')) return;
    
    try {
      await axios.delete(`${API}/admin/songs/${songId}`);
      toast.success('Song deleted');
      fetchSongs();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete song');
    }
  };




  const exportPayablesToCSV = () => {
    if (!payables || !payables.breakdown) return;

    const headers = ['Account Email', 'Account Type', 'Tokens', 'Token Value ($)', 'Stars', 'Star Value ($)', 'Total Payable ($)', 'Can Cashout', 'Needs to Earn ($)'];
    
    const rows = payables.breakdown.map(account => [
      account.account_email,
      account.account_type.toUpperCase(),
      account.tokens,
      account.token_value?.toFixed(2),
      account.stars,
      account.star_value?.toFixed(2),
      account.total_payable?.toFixed(2),
      account.can_cashout ? 'Yes' : 'No',
      account.needs_to_earn || '0.00'
    ]);

    // Add summary rows
    rows.push([]);
    rows.push(['SUMMARY', '', '', '', '', '', '', '', '']);
    rows.push(['Tokens Payable - Listeners', '', '', `$${payables.summary?.tokens_payable_listeners?.toFixed(2)}`, '', '', '', '', '']);
    rows.push(['Stars Payable - Listeners', '', '', '', '', `$${payables.summary?.stars_payable_listeners?.toFixed(2)}`, '', '', '']);
    rows.push(['Tokens Payable - Artists', '', '', `$${payables.summary?.tokens_payable_artists?.toFixed(2)}`, '', '', '', '', '']);
    rows.push(['Stars Payable - Artists', '', '', '', '', `$${payables.summary?.stars_payable_artists?.toFixed(2)}`, '', '', '']);
    rows.push(['GRAND TOTAL', '', '', '', '', '', `$${payables.summary?.grand_total_payable?.toFixed(2)}`, '', '']);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payables_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Payables exported to CSV');
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1 data-testid="admin-title">Admin Panel</h1>
      </div>

      {/* Tab Navigation */}
      <div className="admin-tabs">
        <button 
          className={activeTab === 'dashboard' ? 'active' : ''} 
          onClick={() => setActiveTab('dashboard')}
          data-testid="tab-dashboard"
        >
          Dashboard
        </button>
        <button 
          className={activeTab === 'songs' ? 'active' : ''} 
          onClick={() => setActiveTab('songs')}
          data-testid="tab-songs"
        >
          Songs
        </button>
        <button 
          className={activeTab === 'catalog' ? 'active' : ''} 
          onClick={() => { setActiveTab('catalog'); fetchMasterCatalog(); }}
          data-testid="tab-catalog"
        >
          Master Catalog
        </button>
        <button 
          className={activeTab === 'submissions' ? 'active' : ''} 
          onClick={() => setActiveTab('submissions')}
          data-testid="tab-submissions"
        >
          Submissions
        </button>
        <button 
          className={activeTab === 'jukebox-files' ? 'active' : ''} 
          onClick={() => setActiveTab('jukebox-files')}
          data-testid="tab-jukebox-files"
        >
          Jukebox Files
        </button>
        <button 
          className={activeTab === 'transactions' ? 'active' : ''} 
          onClick={() => setActiveTab('transactions')}
          data-testid="tab-transactions"
        >
          Transactions
        </button>
        <button 
          className={activeTab === 'payouts' ? 'active' : ''} 
          onClick={() => setActiveTab('payouts')}
          data-testid="tab-payouts"
        >
          Payouts
        </button>
        <button 
          className={activeTab === 'payables' ? 'active' : ''} 
          onClick={() => setActiveTab('payables')}
          data-testid="tab-payables"
        >
          Payables
        </button>
        <button 
          className={activeTab === 'admins' ? 'active' : ''} 
          onClick={() => setActiveTab('admins')}
          data-testid="tab-admins"
        >
          Manage Users
        </button>
        <button 
          className={activeTab === 'credits' ? 'active' : ''} 
          onClick={() => setActiveTab('credits')}
          data-testid="tab-credits"
        >
          Credit Users
        </button>
        <button 
          className={activeTab === 'verification' ? 'active' : ''} 
          onClick={() => {
            setActiveTab('verification');
            fetchUnverifiedUsers();
          }}
          data-testid="tab-verification"
        >
          Verify Users
        </button>
        <button 
          className={activeTab === 'loyalty' ? 'active' : ''} 
          onClick={() => {
            setActiveTab('loyalty');
            fetchLoyaltyReport();
          }}
          data-testid="tab-loyalty"
          style={{ background: activeTab === 'loyalty' ? 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)' : undefined }}
        >
          🎧 Loyalty Report
        </button>
        <button 
          className={activeTab === 'feedback' ? 'active' : ''} 
          onClick={() => {
            setActiveTab('feedback');
            fetchFeedbackReport();
          }}
          data-testid="tab-feedback"
          style={{ background: activeTab === 'feedback' ? 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)' : undefined }}
        >
          💬 Feedback Report
        </button>
        <button 
          className={activeTab === 'artwork' ? 'active' : ''} 
          onClick={() => {
            setActiveTab('artwork');
            fetchArtworkAudit();
          }}
          data-testid="tab-artwork"
          style={{ background: activeTab === 'artwork' ? 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' : undefined }}
        >
          🖼️ Artwork Cleanup
        </button>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && stats && (
        <div className="dashboard-content">
          <div className="stats-grid">
            <div className="stat-card" data-testid="stat-revenue">
              <h3>Total Revenue</h3>
              <p className="stat-value">${stats.revenue.total}</p>
              <span className="stat-label">From token sales</span>
            </div>
            <div className="stat-card" data-testid="stat-profit">
              <h3>Gross Profit</h3>
              <p className="stat-value">${stats.revenue.gross_profit}</p>
              <span className="stat-label">Revenue - Payouts</span>
            </div>
            <div className="stat-card" data-testid="stat-users">
              <h3>Total Users</h3>
              <p className="stat-value">{stats.users.total}</p>
              <span className="stat-label">{stats.users.listeners} listeners, {stats.users.artists} artists</span>
            </div>
            <div className="stat-card" data-testid="stat-purchases">
              <h3>Song Purchases</h3>
              <p className="stat-value">{stats.purchases.total}</p>
              <span className="stat-label">Total songs sold</span>
            </div>
          </div>

          <div className="dashboard-row">
            <div className="info-card">
              <h3>Token Statistics</h3>
              <div className="info-item">
                <span>Tokens Sold:</span>
                <strong>{stats.tokens.purchased}</strong>
              </div>
              <div className="info-item">
                <span>Artist Earnings:</span>
                <strong>{stats.tokens.earned_by_artists} tokens</strong>
              </div>
              <div className="info-item">
                <span>Cashed Out:</span>
                <strong>{stats.tokens.cashed_out} tokens</strong>
              </div>
            </div>

            <div className="info-card">
              <h3>Artist Payouts</h3>
              <div className="info-item">
                <span>Total Paid:</span>
                <strong>${stats.payouts.total_amount}</strong>
              </div>
              <div className="info-item">
                <span>Pending:</span>
                <strong>${stats.payouts.pending_amount}</strong>
              </div>
              <div className="info-item">
                <span>Payout Requests:</span>
                <strong>{stats.payouts.count}</strong>
              </div>
            </div>
          </div>

          {stats.top_songs.length > 0 && (
            <div className="top-songs-section">
              <h3>Top Selling Songs</h3>
              <table data-testid="top-songs-table">
                <thead>
                  <tr>
                    <th>Song</th>
                    <th>Artist</th>
                    <th>Purchases</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.top_songs.map((song, index) => (
                    <tr key={song.id || `${song.title}-${song.artist}-${index}`}>
                      <td>{song.title}</td>
                      <td>{song.artist}</td>
                      <td>{song.purchases}</td>
                      <td>${song.revenue.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Songs Tab */}
      {activeTab === 'songs' && (
        <div className="songs-content">
          <div className="section-header">
            <h2>Manage Songs</h2>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={() => {
                  const exportData = songs.map(song => ({
                    'Title': song.title,
                    'Artist': song.artist,
                    'Artist Email': song.artist_email || '',
                    'Album': song.album || '',
                    'Genre': song.genre || '',
                    'Token Cost': song.token_cost,
                    'Category': song.category || 'jukebox',
                    'Tokens Earned': song.total_earned_tokens || 0,
                    'Stars Earned': song.total_earned_stars || 0,
                    'Created': song.created_at || ''
                  }));
                  exportToCSV(exportData, 'songs');
                }}
                style={{ background: '#4CAF50' }}
              >
                📥 Export CSV
              </button>
              <button data-testid="add-song-btn" onClick={() => setShowForm(!showForm)}>
                {showForm ? 'Cancel' : 'Add Song'}
              </button>
            </div>
          </div>

          {/* Song Search */}
          <div style={{ marginBottom: '15px' }}>
            <input
              type="text"
              placeholder="Search songs by title, artist, or email..."
              value={songSearchTerm}
              onChange={(e) => setSongSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 15px',
                borderRadius: '5px',
                border: '1px solid #444',
                background: '#1a1a1a',
                color: 'white',
                fontSize: '1rem'
              }}
            />
          </div>

          {showForm && (
            <form className="admin-form" onSubmit={handleSubmit} data-testid="song-form">
              {/* Mode Toggle */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <button
                  type="button"
                  onClick={() => setUploadMode('file')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: uploadMode === 'file' ? '#1DB954' : '#333',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  📁 Upload Files
                </button>
                <button
                  type="button"
                  onClick={() => setUploadMode('url')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: uploadMode === 'url' ? '#1DB954' : '#333',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  🔗 Use URLs
                </button>
              </div>

              <input
                data-testid="form-title"
                placeholder="Song Title *"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
              <input
                data-testid="form-artist"
                placeholder="Artist Name (as it should appear) *"
                value={formData.artist}
                onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                required
              />
              
              {uploadMode === 'file' && (
                <input
                  data-testid="form-artist-email"
                  type="email"
                  placeholder="Artist's Account Email (must be registered) *"
                  value={formData.artist_email}
                  onChange={(e) => setFormData({ ...formData, artist_email: e.target.value })}
                  required
                  style={{ background: '#1a1a1a', border: '1px solid #444' }}
                />
              )}
              
              <input
                data-testid="form-album"
                placeholder="Album (optional)"
                value={formData.album}
                onChange={(e) => setFormData({ ...formData, album: e.target.value })}
              />
              
              <input
                data-testid="form-genre"
                placeholder="Genre (e.g., Rock, Pop, Jazz)"
                value={formData.genre}
                onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
              />
              
              {uploadMode === 'file' && (
                <input
                  data-testid="form-isrc"
                  placeholder="ISRC Code (optional)"
                  value={formData.isrc}
                  onChange={(e) => setFormData({ ...formData, isrc: e.target.value })}
                />
              )}
              
              {uploadMode === 'file' ? (
                <>
                  {/* File Upload Mode */}
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#888', fontSize: '0.9em' }}>
                      MP3 File *
                    </label>
                    <input
                      data-testid="form-mp3-file"
                      type="file"
                      accept="audio/mpeg,audio/mp3,.mp3"
                      onChange={(e) => setMp3File(e.target.files[0])}
                      required
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        background: '#1a1a1a', 
                        border: '1px solid #444',
                        borderRadius: '5px',
                        color: 'white'
                      }}
                    />
                    {mp3File && <span style={{ color: '#1DB954', fontSize: '0.85em' }}>✓ {mp3File.name}</span>}
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#888', fontSize: '0.9em' }}>
                      Album Artwork (optional, JPG/PNG)
                    </label>
                    <input
                      data-testid="form-artwork-file"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png"
                      onChange={(e) => setArtworkFile(e.target.files[0])}
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        background: '#1a1a1a', 
                        border: '1px solid #444',
                        borderRadius: '5px',
                        color: 'white'
                      }}
                    />
                    {artworkFile && <span style={{ color: '#1DB954', fontSize: '0.85em' }}>✓ {artworkFile.name}</span>}
                  </div>
                </>
              ) : (
                <>
                  {/* URL Mode */}
                  <input
                    data-testid="form-art-url"
                    placeholder="Album Art URL"
                    value={formData.album_art_url}
                    onChange={(e) => setFormData({ ...formData, album_art_url: e.target.value })}
                  />
                  <input
                    data-testid="form-audio-url"
                    placeholder="Audio File URL (MP3) *"
                    value={formData.audio_url}
                    onChange={(e) => setFormData({ ...formData, audio_url: e.target.value })}
                    required
                  />
                </>
              )}
              
              <input
                data-testid="form-token-cost"
                type="number"
                placeholder="Token Cost"
                value={formData.token_cost}
                onChange={(e) => setFormData({ ...formData, token_cost: parseInt(e.target.value) || 1 })}
                required
                min="1"
              />
              
              <button 
                data-testid="submit-song-btn" 
                type="submit"
                disabled={uploadProgress}
                style={{ 
                  opacity: uploadProgress ? 0.7 : 1,
                  cursor: uploadProgress ? 'wait' : 'pointer'
                }}
              >
                {uploadProgress ? '⏳ Uploading...' : (uploadMode === 'file' ? '📤 Upload & Add Song' : '➕ Add Song')}
              </button>
              
              {uploadMode === 'file' && (
                <p style={{ color: '#888', fontSize: '0.85em', marginTop: '10px' }}>
                  💡 The artist must have a registered account. Song will appear in their catalog and they'll earn from sales.
                </p>
              )}
            </form>
          )}

          <div className="admin-songs-list">
            <table data-testid="songs-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Artist</th>
                  <th>Token Cost</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {songs
                  .filter(song => {
                    if (!songSearchTerm) return true;
                    const search = songSearchTerm.toLowerCase();
                    return (
                      song.title?.toLowerCase().includes(search) ||
                      song.artist?.toLowerCase().includes(search) ||
                      song.artist_email?.toLowerCase().includes(search)
                    );
                  })
                  .map((song) => (
                  <tr key={song.id} data-testid={`admin-song-${song.id}`}>
                    <td>{song.title}</td>
                    <td>{song.artist}</td>
                    <td>{song.token_cost}</td>
                    <td>
                      <button 
                        data-testid={`delete-song-${song.id}`}
                        className="delete-btn" 
                        onClick={() => handleDelete(song.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Master Catalog Tab */}
      {activeTab === 'catalog' && (
        <div className="catalog-content">
          <div className="section-header">
            <h2>Master Catalog - Performance Metrics</h2>
            <p style={{ color: '#888', fontSize: '0.9em' }}>All songs with engagement and revenue data</p>
          </div>

          <div className="admin-songs-list">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Artist</th>
                  <th>Genre</th>
                  <th>⭐ Stars</th>
                  <th>💰 Purchases</th>
                  <th>🎵 Tokens Earned</th>
                  <th>Token Cost</th>
                </tr>
              </thead>
              <tbody>
                {masterCatalog.length === 0 ? (
                  <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>No songs in catalog</td></tr>
                ) : (
                  masterCatalog.map(song => (
                    <tr key={song.id}>
                      <td>{song.title}</td>
                      <td>{song.artist}</td>
                      <td>{song.genre || 'N/A'}</td>
                      <td>{song.star_count || 0}</td>
                      <td>{song.purchase_count || 0}</td>
                      <td>{song.tokens_earned || 0}</td>
                      <td>{song.token_cost}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="transactions-content">
          <div className="section-header">
            <h2>Token Purchase Transactions</h2>
            <button 
              className="export-btn"
              onClick={() => exportToCSV(transactions, 'transactions')}
              disabled={transactions.length === 0}
            >
              📥 Export to CSV
            </button>
          </div>
          <table data-testid="transactions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Tokens</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id} data-testid={`transaction-${tx.id}`}>
                  <td>{new Date(tx.created_at).toLocaleDateString()}</td>
                  <td>{tx.user_email || tx.user_id}</td>
                  <td>{tx.tokens}</td>
                  <td>${tx.amount.toFixed(2)}</td>
                  <td><span className={`status ${tx.payment_status}`}>{tx.payment_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {transactions.length === 0 && <p className="empty-message">No transactions yet</p>}
        </div>
      )}

      {/* Payouts Tab */}
      {activeTab === 'payouts' && (
        <div className="payouts-content">
          <div className="section-header">
            <h2>Artist Payout Requests</h2>
            <button 
              className="export-btn"
              onClick={() => exportToCSV(payouts, 'payouts')}
              disabled={payouts.length === 0}
            >
              📥 Export to CSV
            </button>
          </div>
          <table data-testid="payouts-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Artist</th>
                <th>Tokens</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((payout) => (
                <tr key={payout.id} data-testid={`payout-${payout.id}`}>
                  <td>{new Date(payout.requested_at).toLocaleDateString()}</td>
                  <td>{payout.artist_email || payout.artist_id}</td>
                  <td>{payout.tokens_cashed}</td>
                  <td>${payout.payout_amount.toFixed(2)}</td>
                  <td><span className={`status ${payout.status}`}>{payout.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {payouts.length === 0 && <p className="empty-message">No payout requests yet</p>}
        </div>
      )}

      {/* Submissions Tab */}
      {activeTab === 'submissions' && (
        <div className="submissions-content">
          <div className="section-header">
            <h2>Artist Track Submissions</h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <select 
                value={submissionFilter} 
                onChange={(e) => setSubmissionFilter(e.target.value)}
                style={{ 
                  padding: '8px 12px', 
                  borderRadius: '5px', 
                  border: '1px solid #374151',
                  background: '#1f2937',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All Submissions</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <button 
                className="export-btn"
                onClick={() => exportToCSV(submissions, 'submissions')}
                disabled={submissions.length === 0}
              >
                📥 Export to CSV
              </button>
            </div>
          </div>
          {submissions.length > 0 ? (
            <div className="submissions-grid">
              {submissions
                .filter(submission => submissionFilter === 'all' || submission.status === submissionFilter)
                .map((submission) => (
                <div key={submission.id} className={`submission-card ${submission.type === 'removal_request' ? 'removal-request' : ''}`}>
                  <div className="submission-header">
                    <h3>{submission.title}</h3>
                    <span className={`status-badge ${submission.status}`}>
                      {submission.type === 'removal_request' ? '🔴 Removal Request' : submission.status}
                    </span>
                  </div>
                  <p><strong>Artist:</strong> {submission.artist_name}</p>
                  <p><strong>Email:</strong> {submission.artist_email || submission.submitter_email}</p>
                  <p><strong>Genre:</strong> {submission.genre}</p>
                  {submission.album && <p><strong>Album:</strong> {submission.album}</p>}
                  {submission.type === 'removal_request' ? (
                    <p><strong>Reason:</strong> {submission.reason}</p>
                  ) : (
                    <>
                      {submission.description && <p><strong>Description:</strong> {submission.description}</p>}
                      {submission.isrc && <p><strong>ISRC:</strong> {submission.isrc}</p>}
                      {submission.iswc && <p><strong>ISWC:</strong> {submission.iswc}</p>}
                      <p><strong>Cover Song:</strong> {submission.is_cover_song ? '⚠️ Yes' : 'No - Original'}</p>
                      {submission.is_cover_song && (
                        <p><strong>Licensing:</strong> {submission.licensing_confirmed ? '✓ Confirmed' : '❌ Not Confirmed'}</p>
                      )}
                      <p><strong>Jukebox Opt-in:</strong> {submission.opt_in_jukebox ? 'Yes ✓' : 'No'}</p>
                      <p><strong>Pirate Ship Opt-in:</strong> {submission.opt_in_pirate_ship ? 'Yes 🏴‍☠️' : 'No'}</p>
                    </>
                  )}
                  <p><strong>Submitted:</strong> {new Date(submission.submitted_at).toLocaleDateString()}</p>
                  
                  {/* Show rejection details if rejected */}
                  {submission.status === 'rejected' && submission.rejection_reason && (
                    <div style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '6px',
                      padding: '0.75rem',
                      marginTop: '0.5rem'
                    }}>
                      <p style={{ margin: 0, fontWeight: 600, color: '#ef4444' }}>Rejection Reason:</p>
                      <p style={{ margin: '0.5rem 0 0 0' }}>{submission.rejection_reason}</p>
                      {submission.reviewed_by && (
                        <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#9ca3af' }}>
                          Reviewed by {submission.reviewed_by} on {new Date(submission.reviewed_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Download Files Section */}
                  {submission.type !== 'removal_request' && submission.files && (
                    <div className="submission-files">
                      <p><strong>Files:</strong></p>
                      <div className="file-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {submission.files.mp3 && (
                          <button 
                            onClick={async () => {
                              const token = localStorage.getItem('token');
                              try {
                                toast.info('Downloading MP3...', { autoClose: 2000 });
                                const response = await fetch(`${API}/admin/download-file/${submission.id}/mp3`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (!response.ok) throw new Error('Download failed');
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${submission.artist_name} - ${submission.title}.mp3`;
                                a.click();
                                window.URL.revokeObjectURL(url);
                                toast.success('MP3 downloaded!');
                              } catch (e) {
                                console.error(e);
                                toast.error('MP3 download failed');
                              }
                            }}
                            className="download-file-btn"
                            style={{ cursor: 'pointer' }}
                          >
                            🎵 Download MP3
                          </button>
                        )}
                        {submission.files.album_art && (
                          <button 
                            onClick={async () => {
                              const token = localStorage.getItem('token');
                              try {
                                toast.info('Downloading artwork...', { autoClose: 2000 });
                                const response = await fetch(`${API}/admin/download-file/${submission.id}/album_art`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (!response.ok) throw new Error('Download failed');
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${submission.artist_name} - ${submission.title}_artwork.jpg`;
                                a.click();
                                window.URL.revokeObjectURL(url);
                                toast.success('Artwork downloaded!');
                              } catch (e) {
                                console.error(e);
                                toast.error('Artwork download failed');
                              }
                            }}
                            className="download-file-btn"
                            style={{ cursor: 'pointer' }}
                          >
                            🖼 Download Artwork
                          </button>
                        )}
                        {submission.files.preview && (
                          <button 
                            onClick={async () => {
                              const token = localStorage.getItem('token');
                              try {
                                toast.info('Downloading preview...', { autoClose: 2000 });
                                const response = await fetch(`${API}/admin/download-file/${submission.id}/preview`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (!response.ok) throw new Error('Download failed');
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${submission.artist_name} - ${submission.title}_preview.mp3`;
                                a.click();
                                window.URL.revokeObjectURL(url);
                                toast.success('Preview downloaded!');
                              } catch (e) {
                                console.error(e);
                                toast.error('Preview download failed');
                              }
                            }}
                            className="download-file-btn"
                            style={{ cursor: 'pointer' }}
                          >
                            ⏱ Download Preview
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="submission-actions">
                    {submission.type === 'removal_request' ? (
                      <>
                        <button 
                          className="approve-btn"
                          onClick={() => handleApproveSubmission(submission.id)}
                          disabled={submission.status === 'approved'}
                        >
                          ✓ Confirm Removal
                        </button>
                        <button 
                          className="reject-btn"
                          onClick={() => handleRejectSubmission(submission.id)}
                          disabled={submission.status === 'rejected'}
                        >
                          ✗ Deny
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          className="approve-btn"
                          onClick={() => handleApproveSubmission(submission.id)}
                          disabled={submission.status === 'approved'}
                        >
                          ✓ Approve
                        </button>
                        <button 
                          className="reject-btn"
                          onClick={() => handleRejectSubmission(submission.id)}
                          disabled={submission.status === 'rejected'}
                        >
                          ✗ Reject
                        </button>
                      </>
                    )}
                    <button 
                      className="delete-btn"
                      onClick={() => handleDeleteSubmission(submission.id)}
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-message">No submissions yet</p>
          )}
        </div>
      )}
      
      {/* Rejection Reason Modal */}
      {rejectModal.show && (
        <div className="modal-overlay" onClick={() => setRejectModal({ show: false, submissionId: null, reason: '' })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Reject Submission</h3>
            <p>Please provide a reason for rejecting this submission. This feedback will be sent to the artist.</p>
            <textarea
              placeholder="Example: Audio quality needs improvement, licensing documentation required, etc."
              value={rejectModal.reason}
              onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
              rows="5"
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                background: 'rgba(15, 23, 41, 0.8)',
                color: 'white',
                fontSize: '0.95rem',
                marginBottom: '1rem',
                resize: 'vertical'
              }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setRejectModal({ show: false, submissionId: null, reason: '' })}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={confirmRejectSubmission}
                className="reject-btn"
                style={{ padding: '0.5rem 1rem' }}
              >
                Reject Submission
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Song Management Modal */}
      {songManagementModal.show && (
        <div className="modal-overlay" onClick={() => setSongManagementModal({ show: false, type: null, userId: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h3>{songManagementModal.type === 'jukebox' ? '➕ Add Song to Jukebox' : '❤️ Add to Loved Songs'}</h3>
            <p style={{ marginBottom: '1rem', color: '#888' }}>
              Search and select a Jukebox song to add to this user's library.
            </p>
            
            {/* Search Box */}
            <input
              type="text"
              placeholder="Search by song title or artist..."
              value={songSearchTerm}
              onChange={(e) => setSongSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                background: 'rgba(15, 23, 41, 0.8)',
                color: 'white',
                fontSize: '0.95rem',
                marginBottom: '1rem'
              }}
            />
            
            {/* Song List */}
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              {availableSongs
                .filter(song => 
                  song.title.toLowerCase().includes(songSearchTerm.toLowerCase()) ||
                  song.artist.toLowerCase().includes(songSearchTerm.toLowerCase())
                )
                .map(song => (
                  <div
                    key={song.id}
                    onClick={() => setSelectedSongForUser(song)}
                    style={{
                      padding: '0.75rem',
                      borderBottom: '1px solid rgba(139, 92, 246, 0.1)',
                      cursor: 'pointer',
                      background: selectedSongForUser?.id === song.id ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedSongForUser?.id !== song.id) {
                        e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedSongForUser?.id !== song.id) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{song.title}</div>
                    <div style={{ fontSize: '0.85rem', color: '#888' }}>{song.artist} • 💎 {song.token_cost} token{song.token_cost !== 1 ? 's' : ''}</div>
                  </div>
                ))}
            </div>
            
            {selectedSongForUser && (
              <div style={{
                padding: '0.75rem',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <strong style={{ color: '#10b981' }}>Selected:</strong> {selectedSongForUser.title} by {selectedSongForUser.artist}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button 
                onClick={() => {
                  setSongManagementModal({ show: false, type: null, userId: null });
                  setSelectedSongForUser(null);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={addSongToUserLibrary}
                disabled={!selectedSongForUser}
                style={{
                  padding: '0.5rem 1rem',
                  background: selectedSongForUser ? (songManagementModal.type === 'jukebox' ? '#10b981' : '#ec4899') : '#666',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: selectedSongForUser ? 'pointer' : 'not-allowed',
                  fontWeight: '600'
                }}
              >
                {songManagementModal.type === 'jukebox' ? 'Add to Jukebox' : 'Add to Loved Songs'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Jukebox Files Tab */}
      {activeTab === 'jukebox-files' && (
        <div className="jukebox-files-content">
          <div className="section-header">
            <h2>Jukebox Files Browser</h2>
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search by artist name, email, or account ID..."
                value={jukeboxSearch}
                onChange={(e) => setJukeboxSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchJukeboxFiles(jukeboxSearch)}
              />
              <button onClick={() => fetchJukeboxFiles(jukeboxSearch)} className="search-btn">
                🔍 Search
              </button>
              <button onClick={() => { setJukeboxSearch(''); fetchJukeboxFiles(''); }} className="clear-btn">
                Clear
              </button>
            </div>
          </div>

          {!jukeboxFiles ? (
            <div className="loading-state">
              <button onClick={() => fetchJukeboxFiles()} className="load-btn">
                📂 Load Jukebox Files
              </button>
            </div>
          ) : jukeboxFiles.artists && jukeboxFiles.artists.length > 0 ? (
            <div className="jukebox-artists-list">
              <p className="results-count">
                Found {jukeboxFiles.total_artists} artist{jukeboxFiles.total_artists !== 1 ? 's' : ''} with Jukebox files
              </p>
              {jukeboxFiles.artists.map((artist) => (
                <div key={artist.user_id} className="artist-files-card">
                  <div className="artist-header">
                    <div>
                      <h3>{artist.artist_name}</h3>
                      <p className="artist-email">{artist.artist_email}</p>
                      <p className="account-id">
                        <strong>Account ID:</strong> <code>{artist.user_id}</code>
                        <button 
                          onClick={() => {
                            navigator.clipboard.writeText(artist.user_id);
                            toast.success('Account ID copied!');
                          }}
                          className="copy-id-btn"
                          title="Copy Account ID"
                        >
                          📋
                        </button>
                      </p>
                    </div>
                    <div className="file-count-badge">
                      {artist.files.length} file{artist.files.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  
                  <div className="files-list">
                    {artist.files.map((file) => (
                      <div key={file.song_id} className="file-item">
                        <div className="file-info">
                          <h4>{file.title}</h4>
                          <div className="file-meta">
                            <span className="token-cost">💎 {file.token_cost} token{file.token_cost !== 1 ? 's' : ''}</span>
                            {file.created_at && (
                              <span className="upload-date">
                                📅 {new Date(file.created_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="file-actions">
                          {file.audio_url && (
                            <>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(file.audio_url);
                                  toast.success('Audio URL copied!');
                                }}
                                className="action-btn copy-btn"
                                title="Copy Audio URL"
                              >
                                📋 Copy URL
                              </button>
                              <a
                                href={file.audio_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="action-btn download-btn"
                                title="Download/Open Audio"
                              >
                                ⬇️ Download
                              </a>
                            </>
                          )}
                          {file.preview_url && (
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(file.preview_url);
                                toast.success('Preview URL copied!');
                              }}
                              className="action-btn copy-btn preview"
                              title="Copy Preview URL"
                            >
                              📋 Preview URL
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-message">
              {jukeboxSearch 
                ? `No Jukebox files found matching "${jukeboxSearch}"`
                : 'No Jukebox files found'}
            </p>
          )}
        </div>
      )}


      {/* Payables Tab */}
      {activeTab === 'payables' && payables && (
        <div className="payables-content">
          <div className="section-header">
            <h2>Payables Overview</h2>
            <button onClick={exportPayablesToCSV} className="export-btn">
              📥 Export to CSV
            </button>
          </div>
          
          <div className="payables-summary">
            <div className="summary-card">
              <h3>Tokens Payable - Listeners</h3>
              <p className="amount">${payables.summary?.tokens_payable_listeners?.toFixed(2)}</p>
              <p className="note">Tokens listeners have to spend</p>
            </div>
            <div className="summary-card">
              <h3>Stars Payable - Listeners</h3>
              <p className="amount">${payables.summary?.stars_payable_listeners?.toFixed(2)}</p>
              <p className="note">Stars listeners have to spend</p>
            </div>
            <div className="summary-card">
              <h3>Tokens Payable - Artists</h3>
              <p className="amount">${payables.summary?.tokens_payable_artists?.toFixed(2)}</p>
              <p className="note">Tokens artists have earned</p>
            </div>
            <div className="summary-card">
              <h3>Stars Payable - Artists</h3>
              <p className="amount">${payables.summary?.stars_payable_artists?.toFixed(2)}</p>
              <p className="note">Stars artists have earned</p>
            </div>
            <div className="summary-card total">
              <h3>Grand Total Payable</h3>
              <p className="amount">${payables.summary?.grand_total_payable?.toFixed(2)}</p>
              <p className="note">Total cash liability across all accounts</p>
            </div>
          </div>

          <h3>Account Breakdown ({payables.total_accounts || 0} accounts)</h3>
          <div className="table-responsive">
            <table className="payables-table">
              <thead>
                <tr>
                  <th>Account Email</th>
                  <th>Account Type</th>
                  <th>Tokens</th>
                  <th>Token Value</th>
                  <th>Stars</th>
                  <th>Star Value</th>
                  <th>Total Payable</th>
                  <th>Can Cashout</th>
                  <th>Needs to Earn</th>
                </tr>
              </thead>
              <tbody>
                {payables.breakdown?.map((account, idx) => (
                  <tr key={account.account_email || `payable-${idx}`}>
                    <td className="artist-email">{account.account_email}</td>
                    <td className="uppercase">{account.account_type}</td>
                    <td>{account.tokens}</td>
                    <td className="money">${account.token_value?.toFixed(2)}</td>
                    <td>{account.stars}</td>
                    <td className="money">${account.star_value?.toFixed(2)}</td>
                    <td className="money total-cell"><strong>${account.total_payable?.toFixed(2)}</strong></td>
                    <td className="center">{account.can_cashout ? '✅' : '❌'}</td>
                    <td>${account.needs_to_earn ? account.needs_to_earn.toFixed(2) : '0.00'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(!payables.breakdown || payables.breakdown.length === 0) && <p className="empty-message">No payables owed at this time</p>}
        </div>
      )}

      {/* Manage Users Tab */}
      {activeTab === 'admins' && (
        <div className="admins-content">
          <div className="section-header">
            <h2>Manage User Accounts</h2>
            <button onClick={() => setShowAdminForm(!showAdminForm)} className="cta-btn">
              {showAdminForm ? 'Cancel' : '+ Add User'}
            </button>
          </div>

          {showAdminForm && (
            <div className="admin-form-card">
              <h3>Create New User Account</h3>
              <form onSubmit={handleCreateAdmin} className="admin-form">
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    value={adminFormData.email}
                    onChange={(e) => setAdminFormData({ ...adminFormData, email: e.target.value })}
                    placeholder="user@example.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    value={adminFormData.password}
                    onChange={(e) => setAdminFormData({ ...adminFormData, password: e.target.value })}
                    placeholder="Secure password"
                    required
                    minLength={8}
                  />
                  <small>Minimum 8 characters</small>
                </div>
                <div className="form-group">
                  <label>Account Role *</label>
                  <select
                    value={adminFormData.role || 'listener'}
                    onChange={(e) => setAdminFormData({ ...adminFormData, role: e.target.value })}
                    required
                  >
                    <option value="listener">Listener</option>
                    <option value="artist">Artist</option>
                    <option value="admin">Admin</option>
                  </select>
                  <small>Select the appropriate role for this account</small>
                </div>
                <div className="form-group">
                  <label>Admin Setup Key *</label>
                  <input
                    type="password"
                    value={adminFormData.setup_key}
                    onChange={(e) => setAdminFormData({ ...adminFormData, setup_key: e.target.value })}
                    placeholder="Enter your admin setup key"
                    required
                  />
                  <small>Your ADMIN_SETUP_KEY from environment variables</small>
                </div>
                <div className="form-actions">
                  <button type="button" onClick={() => setShowAdminForm(false)} className="secondary-btn">
                    Cancel
                  </button>
                  <button type="submit" className="cta-btn">
                    Create Account
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="admin-info-card">
            <h3>👥 User Roles</h3>
            <p><strong>Admin:</strong> Full access to manage songs, submissions, transactions, and payouts</p>
            <p><strong>Artist:</strong> Can submit tracks, view earnings, request payouts</p>
            <p><strong>Listener:</strong> Can purchase songs, build jukebox collection</p>
            <p className="security-note">
              <strong>Security:</strong> Only create admin accounts for trusted team members. 
              User credentials are protected and cannot be created through public registration.
            </p>
          </div>
        </div>
      )}

      {/* Credit Users Tab */}
      {activeTab === 'credits' && (
        <div className="credits-content">
          <div className="section-header">
            <h2>User Account Management</h2>
            <button 
              onClick={() => {
                const exportData = users.map(user => ({
                  'Email': user.email,
                  'Role': user.role,
                  'Artist Name': user.artist_name || '',
                  'Token Balance': user.token_balance || 0,
                  'Star Balance': user.star_balance || 0,
                  'Total Stars Earned': user.total_stars_earned || 0,
                  'Verified': user.is_verified ? 'Yes' : 'No',
                  'Stripe Connected': user.stripe_account_id ? 'Yes' : 'No',
                  'Created': user.created_at || ''
                }));
                exportToCSV(exportData, 'users');
              }}
              style={{ background: '#4CAF50' }}
            >
              📥 Export CSV
            </button>
            <button 
              onClick={async () => {
                if (window.confirm('This will assign random user tags to all users who don\'t have one. Continue?')) {
                  try {
                    const response = await axios.post(`${API}/admin/assign-usernames`);
                    toast.success(`Assigned tags to ${response.data.users_updated} users`);
                    fetchUsers();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to assign tags');
                  }
                }
              }}
              style={{ background: '#8b5cf6' }}
            >
              🏷️ Backfill Missing Tags
            </button>
          </div>

          <div className="user-management-grid">
            {/* User Search & Selection */}
            <div className="user-search-panel">
              <h3>Find User Account</h3>
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search by email, name, or role..."
                  value={userSearchTerm}
                  onChange={(e) => handleUserSearch(e.target.value)}
                  className="user-search-input"
                />
              </div>

              <div className="users-table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>User Tag</th>
                      <th>Role</th>
                      <th>Tokens</th>
                      <th>Stars</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.slice(0, 10).map((user) => (
                      <tr key={user.id} className={selectedUser?.email === user.email ? 'selected' : ''}>
                        <td>{user.email}</td>
                        <td style={{ color: user.username ? '#8b5cf6' : '#ef4444', fontWeight: user.username ? 'normal' : 'bold' }}>
                          {user.username || '⚠️ NOT SET'}
                        </td>
                        <td><span className={`role-badge ${user.role}`}>{user.role}</span></td>
                        <td>{user.token_balance || 0}</td>
                        <td>{user.star_balance || 0}</td>
                        <td>
                          <button 
                            className="select-user-btn" 
                            onClick={() => handleSelectUser(user)}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredUsers.length === 0 && (
                  <p className="no-users">No users found</p>
                )}
                {filteredUsers.length > 10 && (
                  <p className="more-results">Showing 10 of {filteredUsers.length} results. Refine your search for more.</p>
                )}
              </div>
            </div>

            {/* User Actions Panel */}
            <div className="user-actions-panel">
              {selectedUser ? (
                <>
                  <h3>Account: {selectedUser.email}</h3>
                  <div className="user-details-card">
                    <div className="detail-row">
                      <span className="label">User Tag:</span>
                      <strong style={{ color: selectedUser.username ? '#8b5cf6' : '#ef4444' }}>
                        {selectedUser.username || 'NOT SET'}
                      </strong>
                    </div>
                    <div className="detail-row">
                      <span className="label">Role:</span>
                      <span className={`role-badge ${selectedUser.role}`}>{selectedUser.role}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Token Balance:</span>
                      <strong>{selectedUser.token_balance || 0} tokens</strong>
                    </div>
                    <div className="detail-row">
                      <span className="label">Star Balance:</span>
                      <strong>{selectedUser.star_balance || 0} stars</strong>
                    </div>
                    {selectedUser.artist_name && (
                      <div className="detail-row">
                        <span className="label">Artist Name:</span>
                        <strong>{selectedUser.artist_name}</strong>
                      </div>
                    )}
                  </div>

                  {/* User Tag Management */}
                  <div className="action-section">
                    <h4>🏷️ User Tag</h4>
                    <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '0.75rem' }}>
                      Used to identify users on social media for rewards announcements
                    </p>
                    <div className="inline-credit-form">
                      <div className="form-row" style={{ alignItems: 'flex-end' }}>
                        <div className="form-group" style={{ flex: 1 }}>
                          <label htmlFor="user-tag">Custom Tag</label>
                          <input
                            id="user-tag"
                            type="text"
                            placeholder="Enter custom tag or leave blank for random"
                            defaultValue={selectedUser.username || ''}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const newTag = e.target.value.trim();
                                handleUpdateUserTag(selectedUser.email, newTag);
                              }
                            }}
                          />
                        </div>
                        <button 
                          type="button" 
                          className="action-btn"
                          style={{ background: '#8b5cf6', marginBottom: '0' }}
                          onClick={() => {
                            const input = document.getElementById('user-tag');
                            handleUpdateUserTag(selectedUser.email, input.value.trim());
                          }}
                        >
                          Save
                        </button>
                        <button 
                          type="button" 
                          className="action-btn"
                          style={{ background: '#3b82f6', marginBottom: '0' }}
                          onClick={() => handleUpdateUserTag(selectedUser.email, '')}
                        >
                          🎲 Random
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Credit Form */}
                  <div className="action-section">
                    <h4>💰 Credit Tokens/Stars</h4>
                    <form onSubmit={handleCreditUser} className="inline-credit-form">
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="tokens">Tokens</label>
                          <input
                            id="tokens"
                            type="number"
                            min="0"
                            value={creditFormData.tokens}
                            onChange={(e) => setCreditFormData({ ...creditFormData, tokens: e.target.value })}
                            placeholder="0"
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="stars">Stars</label>
                          <input
                            id="stars"
                            type="number"
                            min="0"
                            value={creditFormData.stars}
                            onChange={(e) => setCreditFormData({ ...creditFormData, stars: e.target.value })}
                            placeholder="0"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="reason">Reason</label>
                        <input
                          id="reason"
                          type="text"
                          value={creditFormData.reason}
                          onChange={(e) => setCreditFormData({ ...creditFormData, reason: e.target.value })}
                          placeholder="e.g., Promo, Testing, Refund"
                        />
                      </div>
                      <button type="submit" className="action-btn credit-btn">
                        Credit Account
                      </button>
                    </form>
                  </div>

                  {/* Reset Password */}
                  <div className="action-section">
                    <h4>🔑 Reset User Password</h4>
                    <form onSubmit={handleAdminResetPassword} className="inline-credit-form">
                      <div className="form-group">
                        <label htmlFor="new_password">New Password</label>
                        <input
                          id="new_password"
                          type="password"
                          value={resetPasswordData.new_password}
                          onChange={(e) => setResetPasswordData({ ...resetPasswordData, new_password: e.target.value })}
                          placeholder="Enter new password (min 6 characters)"
                          required
                          minLength="6"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="confirm_password">Confirm Password</label>
                        <input
                          id="confirm_password"
                          type="password"
                          value={resetPasswordData.confirm_password}
                          onChange={(e) => setResetPasswordData({ ...resetPasswordData, confirm_password: e.target.value })}
                          placeholder="Confirm new password"
                          required
                          minLength="6"
                        />
                      </div>
                      <button type="submit" className="action-btn reset-password-btn">
                        Reset Password
                      </button>
                    </form>
                  </div>

                  {/* Song Management (Customer Service) */}
                  <div className="action-section">
                    <h4>🎵 Add Songs (Customer Service)</h4>
                    <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '1rem' }}>
                      Use these buttons to manually add songs to a user's library in case of issues or customer service situations.
                    </p>
                    <div className="role-buttons">
                      <button 
                        type="button"
                        className="action-btn"
                        style={{ background: 'rgba(16, 185, 129, 0.2)', borderColor: '#10b981', color: '#10b981' }}
                        onClick={() => openSongManagementModal('jukebox', selectedUser.id)}
                      >
                        ➕ Add to Jukebox
                      </button>
                      <button 
                        type="button"
                        className="action-btn"
                        style={{ background: 'rgba(236, 72, 153, 0.2)', borderColor: '#ec4899', color: '#ec4899' }}
                        onClick={() => openSongManagementModal('loved', selectedUser.id)}
                      >
                        ❤️ Add to Loved Songs
                      </button>
                    </div>
                  </div>

                  {/* Role Conversion */}
                  <div className="action-section">
                    <h4>🔄 Convert Role</h4>
                    <div className="role-buttons">
                      {selectedUser.role !== 'listener' && (
                        <button 
                          type="button"
                          className="action-btn role-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            console.log('Listener button clicked');
                            handleChangeRole(selectedUser.email, 'listener');
                          }}
                        >
                          → Listener
                        </button>
                      )}
                      {selectedUser.role !== 'artist' && (
                        <button 
                          type="button"
                          className="action-btn role-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            console.log('Artist button clicked');
                            handleChangeRole(selectedUser.email, 'artist');
                          }}
                        >
                          → Artist
                        </button>
                      )}
                      {selectedUser.role !== 'admin' && (
                        <button 
                          type="button"
                          className="action-btn role-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            console.log('Admin button clicked');
                            handleChangeRole(selectedUser.email, 'admin');
                          }}
                        >
                          → Admin
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Delete Account */}
                  <div className="action-section danger-zone">
                    <h4>⚠️ Danger Zone</h4>
                    <button 
                      type="button"
                      className="action-btn delete-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        console.log('Delete button clicked');
                        handleDeleteUser(selectedUser.email);
                      }}
                    >
                      🗑️ Delete Account
                    </button>
                    <small>This action cannot be undone</small>
                  </div>
                </>
              ) : (
                <div className="no-selection">
                  <p>👈 Select a user account to manage</p>
                  <small>Search and select a user from the table to credit tokens/stars, change roles, or delete accounts.</small>
                </div>
              )}
            </div>
          </div>

          <div className="credit-info-card">
            <h3>📊 Important: Payables Tracking</h3>
            <p className="highlight-note">
              <strong>When you credit tokens or stars to users, they flow through the economy and are automatically tracked in payables:</strong>
            </p>
            <ul>
              <li>✓ <strong>Credited Tokens:</strong> When users spend tokens on songs, artists earn those tokens → appears in <strong>Payables</strong></li>
              <li>✓ <strong>Credited Stars:</strong> When users star songs, artists earn those stars → appears in <strong>Payables</strong></li>
              <li>✓ <strong>Artist Earnings:</strong> All artist token/star balances are automatically calculated in the Payables section</li>
            </ul>
            <p className="note">
              The system tracks all artist earnings regardless of whether the original tokens/stars came from purchases or admin credits. Check the <strong>Payables tab</strong> for current amounts owed to artists.
            </p>
          </div>
        </div>
      )}

      {/* Custom Confirmation Modal */}
      {confirmDialog.show && (
        <div className="confirm-modal-overlay" onClick={() => setConfirmDialog({ show: false, title: '', message: '', onConfirm: null })}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{confirmDialog.title}</h3>
            <p>{confirmDialog.message}</p>
            <div className="confirm-modal-actions">
              <button 
                className="modal-btn cancel-btn"
                onClick={() => setConfirmDialog({ show: false, title: '', message: '', onConfirm: null })}
              >
                Cancel
              </button>
              <button 
                className="modal-btn confirm-btn"
                onClick={confirmDialog.onConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Verification Tab */}
      {activeTab === 'verification' && (
        <div className="verification-content">
          <div className="section-header">
            <h2>📧 Email Verification Management</h2>
            <p>Manually verify user accounts that haven't completed email verification</p>
          </div>

          {unverifiedUsers.length === 0 ? (
            <div className="empty-state" style={{ textAlign: 'center', padding: '3rem', background: '#1f2937', borderRadius: '12px' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
              <h3>All Users Verified!</h3>
              <p style={{ color: '#9ca3af', marginTop: '0.5rem' }}>
                There are no unverified users at this time.
              </p>
            </div>
          ) : (
            <div className="unverified-users-table">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Created At</th>
                    <th>Token Expires</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {unverifiedUsers.map((user) => (
                    <tr key={user.email}>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge ${user.role}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        {user.created_at 
                          ? new Date(user.created_at).toLocaleDateString()
                          : 'N/A'
                        }
                      </td>
                      <td>
                        {user.verification_token_expires 
                          ? new Date(user.verification_token_expires).toLocaleDateString()
                          : 'N/A'
                        }
                      </td>
                      <td>
                        <button
                          onClick={() => handleVerifyUser(user.email)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#22c55e',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 600
                          }}
                        >
                          ✓ Verify
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Loyalty Report Tab */}
      {activeTab === 'loyalty' && (
        <div className="loyalty-report-section">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '1.5rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <label style={{ color: '#a5b4fc' }}>Select Month:</label>
              <input
                type="month"
                value={loyaltyMonth}
                onChange={(e) => setLoyaltyMonth(e.target.value)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '8px',
                  padding: '0.5rem',
                  color: '#fff'
                }}
              />
              <button 
                onClick={() => fetchLoyaltyReport(loyaltyMonth)}
                className="cta-btn"
                disabled={loadingLoyalty}
                style={{ padding: '0.5rem 1rem' }}
              >
                {loadingLoyalty ? 'Loading...' : 'Load Report'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={assignAllUsernames}
                className="secondary-btn"
                disabled={assigningUsernames}
                style={{ fontSize: '0.85rem' }}
              >
                {assigningUsernames ? 'Assigning...' : '🏷️ Assign Missing Usernames'}
              </button>
              <button
                onClick={exportLoyaltyCSV}
                className="cta-btn"
                disabled={!loyaltyReport || loyaltyReport.leaderboard.length === 0}
                style={{ fontSize: '0.85rem' }}
              >
                📥 Export CSV
              </button>
            </div>
          </div>

          {loyaltyReport && (
            <>
              {/* Summary Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  background: 'rgba(139, 92, 246, 0.15)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#8b5cf6' }}>
                    {loyaltyReport.total_participants}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#a5b4fc' }}>Participants</div>
                </div>
                <div style={{
                  background: 'rgba(16, 185, 129, 0.15)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#10b981' }}>
                    {loyaltyReport.total_points_awarded}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6ee7b7' }}>Total Points</div>
                </div>
                <div style={{
                  background: 'rgba(251, 191, 36, 0.15)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#fbbf24' }}>
                    {loyaltyReport.total_listening_minutes}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#fcd34d' }}>Minutes Listened</div>
                </div>
                <div style={{
                  background: 'rgba(59, 130, 246, 0.15)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#3b82f6' }}>
                    {loyaltyReport.tier_breakdown.insider}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#93c5fd' }}>Insiders 🏆</div>
                </div>
                <div style={{
                  background: 'rgba(236, 72, 153, 0.15)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#ec4899' }}>
                    {loyaltyReport.total_token_packs_purchased || 0}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#f9a8d4' }}>Packs Bought 💳</div>
                </div>
                <div style={{
                  background: 'rgba(14, 165, 233, 0.15)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#0ea5e9' }}>
                    {loyaltyReport.total_songs_purchased || 0}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#7dd3fc' }}>Songs Purchased 🎵</div>
                </div>
              </div>

              {/* Tier Breakdown */}
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{ margin: '0 0 0.75rem 0', color: '#a5b4fc' }}>Tier Breakdown</h4>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <span style={{ color: '#fbbf24' }}>🏆 Insider: {loyaltyReport.tier_breakdown.insider}</span>
                  <span style={{ color: '#8b5cf6' }}>⭐ Curator: {loyaltyReport.tier_breakdown.curator}</span>
                  <span style={{ color: '#10b981' }}>🎵 Contributor: {loyaltyReport.tier_breakdown.contributor}</span>
                  <span style={{ color: '#94a3b8' }}>👋 New: {loyaltyReport.tier_breakdown.new}</span>
                </div>
              </div>

              {/* Leaderboard */}
              <div className="table-container">
                <h4 style={{ margin: '0 0 1rem 0', color: '#a5b4fc' }}>🏆 Leaderboard - {loyaltyReport.month}</h4>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Username</th>
                      <th>Monthly Pts</th>
                      <th>Lifetime Pts</th>
                      <th>Tier</th>
                      <th>Minutes</th>
                      <th>Love ⭐</th>
                      <th>Feedback</th>
                      <th>Packs 💳</th>
                      <th>Songs 🎵</th>
                      <th>Email (masked)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loyaltyReport.leaderboard.map((user) => (
                      <tr key={user.rank} style={{
                        background: user.rank <= 3 ? 'rgba(251, 191, 36, 0.1)' : undefined
                      }}>
                        <td style={{ fontWeight: 'bold' }}>
                          {user.rank === 1 && '🥇 '}
                          {user.rank === 2 && '🥈 '}
                          {user.rank === 3 && '🥉 '}
                          {user.rank}
                        </td>
                        <td style={{ color: '#a5b4fc', fontWeight: '500' }}>
                          {user.username || 'N/A'}
                        </td>
                        <td style={{ fontWeight: 'bold', color: '#8b5cf6' }}>
                          {user.monthly_points}
                        </td>
                        <td style={{ color: '#94a3b8' }}>
                          {user.listener_points}
                        </td>
                        <td>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            background: user.listener_tier === 'insider' ? 'rgba(251, 191, 36, 0.2)' :
                                       user.listener_tier === 'curator' ? 'rgba(139, 92, 246, 0.2)' :
                                       user.listener_tier === 'contributor' ? 'rgba(16, 185, 129, 0.2)' :
                                       'rgba(148, 163, 184, 0.2)',
                            color: user.listener_tier === 'insider' ? '#fbbf24' :
                                   user.listener_tier === 'curator' ? '#a5b4fc' :
                                   user.listener_tier === 'contributor' ? '#6ee7b7' :
                                   '#94a3b8'
                          }}>
                            {user.listener_tier}
                          </span>
                        </td>
                        <td>{user.total_listening_minutes}</td>
                        <td>{user.songs_rated}</td>
                        <td>{user.feedback_submitted}</td>
                        <td style={{ color: user.token_packs_purchased > 0 ? '#10b981' : '#64748b' }}>
                          {user.token_packs_purchased || 0}
                        </td>
                        <td style={{ color: user.songs_purchased > 0 ? '#3b82f6' : '#64748b' }}>
                          {user.songs_purchased || 0}
                        </td>
                        <td style={{ color: '#64748b', fontSize: '0.85rem' }}>
                          {user.email_masked}
                        </td>
                      </tr>
                    ))}
                    {loyaltyReport.leaderboard.length === 0 && (
                      <tr>
                        <td colSpan="11" style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                          No participants for this month yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!loyaltyReport && !loadingLoyalty && (
            <div style={{ textAlign: 'center', color: '#64748b', padding: '3rem' }}>
              Select a month and click "Load Report" to view the leaderboard
            </div>
          )}
        </div>
      )}

      {/* Feedback Report Tab */}
      {activeTab === 'feedback' && (
        <div className="feedback-report-section">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '1.5rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <label style={{ color: '#6ee7b7' }}>Select Month:</label>
              <input
                type="month"
                value={feedbackMonth}
                onChange={(e) => setFeedbackMonth(e.target.value)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '8px',
                  padding: '0.5rem',
                  color: '#fff'
                }}
              />
              <button 
                onClick={() => fetchFeedbackReport(feedbackMonth)}
                className="cta-btn"
                disabled={loadingFeedback}
                style={{ padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)' }}
              >
                {loadingFeedback ? 'Loading...' : 'Load Report'}
              </button>
            </div>
            <button
              onClick={exportFeedbackCSV}
              className="cta-btn"
              disabled={!feedbackReport || feedbackReport.feedback.length === 0}
              style={{ fontSize: '0.85rem' }}
            >
              📥 Export CSV
            </button>
          </div>

          {feedbackReport && (
            <>
              {/* Summary Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  background: 'rgba(16, 185, 129, 0.15)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#10b981' }}>
                    {feedbackReport.total_feedback}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6ee7b7' }}>Total Submissions</div>
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '1rem'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#a5b4fc', marginBottom: '0.5rem' }}>Rating Breakdown</div>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                    <span>😍 {feedbackReport.rating_breakdown.love || 0}</span>
                    <span>👍 {feedbackReport.rating_breakdown.like || 0}</span>
                    <span>😐 {feedbackReport.rating_breakdown.okay || 0}</span>
                    <span>👎 {feedbackReport.rating_breakdown.skip || 0}</span>
                  </div>
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '1rem'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#a5b4fc', marginBottom: '0.5rem' }}>By Channel</div>
                  <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                    {Object.entries(feedbackReport.channel_breakdown).map(([channel, count]) => (
                      <div key={channel}>{channel}: {count}</div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Top Genres */}
              {Object.keys(feedbackReport.genre_preferences).length > 0 && (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '1rem',
                  marginBottom: '1.5rem'
                }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#6ee7b7' }}>🎵 Top Genre Preferences</h4>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {Object.entries(feedbackReport.genre_preferences).slice(0, 10).map(([genre, count]) => (
                      <span key={genre} style={{
                        background: 'rgba(16, 185, 129, 0.2)',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '16px',
                        fontSize: '0.85rem',
                        color: '#6ee7b7'
                      }}>
                        {genre} ({count})
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Feedback Table */}
              <div className="table-container">
                <h4 style={{ margin: '0 0 1rem 0', color: '#6ee7b7' }}>💬 All Feedback - {feedbackReport.month}</h4>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Username</th>
                      <th>Channel</th>
                      <th>Song</th>
                      <th>Rating</th>
                      <th>Comment</th>
                      <th>Fav Genre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feedbackReport.feedback.map((fb, index) => (
                      <tr key={fb.id || fb.submitted_at || `feedback-${index}`}>
                        <td style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          {new Date(fb.submitted_at).toLocaleDateString()}
                        </td>
                        <td style={{ color: '#a5b4fc' }}>{fb.username || 'N/A'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{fb.channel}</td>
                        <td style={{ fontSize: '0.85rem' }}>
                          <div>{fb.song_title}</div>
                          <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{fb.song_artist}</div>
                        </td>
                        <td>
                          {fb.rating === 'love' && '😍'}
                          {fb.rating === 'like' && '👍'}
                          {fb.rating === 'okay' && '😐'}
                          {fb.rating === 'skip' && '👎'}
                        </td>
                        <td style={{ 
                          maxWidth: '200px', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.85rem',
                          color: '#94a3b8'
                        }} title={fb.comment}>
                          {fb.comment || '-'}
                        </td>
                        <td style={{ fontSize: '0.85rem', color: '#6ee7b7' }}>
                          {fb.favorite_genre || '-'}
                        </td>
                      </tr>
                    ))}
                    {feedbackReport.feedback.length === 0 && (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                          No feedback submissions for this month yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!feedbackReport && !loadingFeedback && (
            <div style={{ textAlign: 'center', color: '#64748b', padding: '3rem' }}>
              Select a month and click "Load Report" to view listener feedback
            </div>
          )}
        </div>
      )}

      {/* Artwork Cleanup Tab */}
      {activeTab === 'artwork' && (
        <div className="admin-section">
          <h2>🖼️ Jukebox Artwork Cleanup</h2>
          <p style={{ color: '#94a3b8', marginBottom: '1.5rem' }}>
            Identify and fix Jukebox songs with missing or broken album artwork URLs.
            This tool helps clean up legacy Google Drive URLs that no longer work.
          </p>

          {!artworkAudit ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <button 
                onClick={() => fetchArtworkAudit(false)}
                className="action-btn"
                style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)', marginRight: '1rem' }}
              >
                🔍 Quick Scan
              </button>
              <button 
                onClick={() => fetchArtworkAudit(true)}
                className="action-btn"
                style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)' }}
              >
                🔬 Deep Verify (slower)
              </button>
              <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '1rem' }}>
                Quick Scan checks URL formats. Deep Verify actually tests if each image loads.
              </p>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  background: 'rgba(16, 185, 129, 0.15)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#10b981' }}>
                    {artworkAudit.summary.healthy}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6ee7b7' }}>✅ Healthy</div>
                </div>
                <div style={{
                  background: 'rgba(249, 115, 22, 0.15)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#f97316' }}>
                    {artworkAudit.summary.broken_b2 || 0}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#fdba74' }}>🔗 Broken B2</div>
                </div>
                <div style={{
                  background: 'rgba(239, 68, 68, 0.15)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#ef4444' }}>
                    {artworkAudit.summary.google_drive}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#fca5a5' }}>🔗 Google Drive</div>
                </div>
                <div style={{
                  background: 'rgba(251, 191, 36, 0.15)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#fbbf24' }}>
                    {artworkAudit.summary.empty}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#fcd34d' }}>⚠️ Empty</div>
                </div>
                <div style={{
                  background: 'rgba(139, 92, 246, 0.15)',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#8b5cf6' }}>
                    {artworkAudit.summary.needs_fix}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#a5b4fc' }}>🔧 Needs Fix</div>
                </div>
              </div>

              <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button 
                  onClick={() => fetchArtworkAudit(false)}
                  className="action-btn"
                  style={{ background: '#3b82f6' }}
                >
                  🔄 Quick Refresh
                </button>
                <button 
                  onClick={() => fetchArtworkAudit(true)}
                  className="action-btn"
                  style={{ background: '#8b5cf6' }}
                >
                  🔬 Deep Verify
                </button>
                {artworkAudit.summary.verified_urls && (
                  <span style={{ color: '#10b981', fontSize: '0.85rem', alignSelf: 'center' }}>
                    ✅ URLs verified
                  </span>
                )}
              </div>

              {/* Songs needing fixes */}
              {artworkAudit.summary.needs_fix > 0 ? (
                <div className="table-container">
                  <h4 style={{ margin: '0 0 1rem 0', color: '#ef4444' }}>
                    🔧 Songs Needing Artwork Fix ({artworkAudit.summary.needs_fix})
                  </h4>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Artist</th>
                        <th>Title</th>
                        <th>Issue</th>
                        <th>Current URL</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...(artworkAudit.songs.broken_b2 || []), ...artworkAudit.songs.google_drive, ...artworkAudit.songs.empty, ...artworkAudit.songs.other].map((song) => (
                        <tr key={song.id}>
                          <td style={{ fontWeight: '500' }}>{song.artist}</td>
                          <td>{song.title}</td>
                          <td>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              background: !song.album_art_url ? 'rgba(251, 191, 36, 0.2)' :
                                         song.album_art_url.includes('google') ? 'rgba(239, 68, 68, 0.2)' :
                                         song.album_art_url.includes('backblaze') ? 'rgba(249, 115, 22, 0.2)' :
                                         'rgba(139, 92, 246, 0.2)',
                              color: !song.album_art_url ? '#fbbf24' :
                                     song.album_art_url.includes('google') ? '#ef4444' :
                                     song.album_art_url.includes('backblaze') ? '#f97316' :
                                     '#a5b4fc'
                            }}>
                              {!song.album_art_url ? 'Empty' :
                               song.album_art_url.includes('google') ? 'Google Drive' :
                               song.album_art_url.includes('backblaze') ? 'Broken B2' :
                               'Unknown'}
                            </span>
                          </td>
                          <td style={{ 
                            maxWidth: '200px', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            fontSize: '0.75rem',
                            color: '#64748b'
                          }}>
                            {song.album_art_url || '(none)'}
                          </td>
                          <td>
                            <label 
                              style={{
                                display: 'inline-block',
                                padding: '0.5rem 1rem',
                                background: artworkUploading === song.id ? '#64748b' : '#10b981',
                                color: 'white',
                                borderRadius: '6px',
                                cursor: artworkUploading === song.id ? 'wait' : 'pointer',
                                fontSize: '0.85rem'
                              }}
                            >
                              {artworkUploading === song.id ? '⏳ Uploading...' : '📤 Upload Art'}
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                disabled={artworkUploading === song.id}
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleArtworkUpload(song.id, e.target.files[0]);
                                  }
                                }}
                              />
                            </label>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(16, 185, 129, 0.3)'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
                  <div style={{ color: '#10b981', fontWeight: '600' }}>All artwork is healthy!</div>
                  <div style={{ color: '#6ee7b7', fontSize: '0.9rem' }}>
                    All {artworkAudit.summary.total} Jukebox songs have valid Backblaze B2 artwork URLs.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

    </div>
  );
};

// Schedule Page
const Schedule = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/calendar/events`);
      setEvents(response.data.events || []);
      setAuthRequired(response.data.auth_required || false);
      setMessage(response.data.message || '');
    } catch (error) {
      console.error('Failed to fetch calendar events:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const calculateDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diff = (endDate - startDate) / (1000 * 60); // minutes
    const hours = Math.floor(diff / 60);
    const mins = Math.floor(diff % 60);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className="schedule-page">
      <div className="page-header">
        <h1>📅 Show Schedule</h1>
        <p>Check out what's playing on AMS Radio</p>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading schedule...</div>
      ) : authRequired ? (
        <div className="auth-required-message">
          <div className="info-box">
            <h3>🔐 Calendar Connection Required</h3>
            <p>{message || 'The admin needs to connect their Google Calendar to display the schedule.'}</p>
            {user && user.role === 'admin' && (
              <button 
                className="cta-btn"
                onClick={() => navigate('/admin/google-setup')}
                style={{ marginTop: '1rem' }}
              >
                Connect Calendar Now
              </button>
            )}
            <p className="info-note">
              This is a one-time setup. Once connected, your show schedule will appear here automatically.
            </p>
          </div>
        </div>
      ) : events.length > 0 ? (
        <div className="schedule-grid">
          {events.map((event) => (
            <div key={event.id} className="schedule-card">
              <div className="schedule-card-header">
                <h3>{event.summary}</h3>
                <span className="schedule-duration">
                  {calculateDuration(event.start, event.end)}
                </span>
              </div>
              <div className="schedule-card-body">
                <div className="schedule-time">
                  <span className="schedule-icon">🕒</span>
                  <span>{formatDateTime(event.start)}</span>
                </div>
                {event.description && (
                  <p className="schedule-description">{event.description}</p>
                )}
                {event.location && (
                  <div className="schedule-location">
                    <span className="schedule-icon">📍</span>
                    <span>{event.location}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <p>No upcoming shows scheduled at this time</p>
          <p className="empty-note">Check back soon for updates!</p>
        </div>
      )}
    </div>
  );
};

// Google Setup Page for Admin
const GoogleSetup = () => {
  const navigate = useNavigate();
  const [connecting, setConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  useEffect(() => {
    // Check if we're returning from OAuth
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    
    if (code) {
      handleCallback(code);
    } else {
      checkConnectionStatus();
    }
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/calendar/events`);
      if (response.data.auth_required) {
        setConnectionStatus('not_connected');
      } else {
        setConnectionStatus('connected');
      }
    } catch (error) {
      setConnectionStatus('not_connected');
    }
  };

  const handleCallback = async (code) => {
    try {
      setConnecting(true);
      await axios.get(`${API}/auth/google/callback?code=${code}`);
      toast.success('Google Calendar connected successfully!');
      setConnectionStatus('connected');
      // Clean URL
      window.history.replaceState({}, '', '/admin/google-setup');
    } catch (error) {
      toast.error('Failed to connect Google Calendar');
      setConnectionStatus('not_connected');
    } finally {
      setConnecting(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const response = await axios.get(`${API}/auth/google/login`);
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate Google connection');
      setConnecting(false);
    }
  };

  return (
    <div className="google-setup-page">
      <div className="page-header">
        <h1>🔗 Google Calendar Setup</h1>
        <p>Connect your Google Calendar to display show schedules</p>
      </div>

      <div className="setup-container">
        {connectionStatus === 'connected' ? (
          <div className="success-card">
            <div className="success-icon">✅</div>
            <h2>Calendar Connected!</h2>
            <p>Your Google Calendar is now connected and events will appear on the Schedule page.</p>
            <div className="setup-actions">
              <button className="cta-btn" onClick={() => navigate('/schedule')}>
                View Schedule
              </button>
              <button className="secondary-btn" onClick={() => navigate('/admin')}>
                Back to Admin Panel
              </button>
            </div>
          </div>
        ) : connectionStatus === 'not_connected' ? (
          <div className="setup-card">
            <h2>Connect Google Calendar</h2>
            <div className="setup-info">
              <p>To display your radio show schedule, you need to connect your Google account.</p>
              <ul className="setup-steps">
                <li>✓ Click "Connect Google Calendar" below</li>
                <li>✓ Sign in with your Google account</li>
                <li>✓ Grant access to your calendar</li>
                <li>✓ You'll be redirected back here automatically</li>
              </ul>
              <div className="info-note">
                <strong>Note:</strong> This is a one-time setup. Your calendar ID is already configured: 
                <code>ab288f7492ab7080e742185ef50767c20dd4f99109233ca89604ec300d37f8ae@group.calendar.google.com</code>
              </div>
            </div>
            <button 
              className="cta-btn large-btn" 
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? 'Redirecting to Google...' : '🔗 Connect Google Calendar'}
            </button>
          </div>
        ) : (
          <div className="loading-spinner">Checking connection status...</div>
        )}
      </div>
    </div>
  );
};

// Artist Submit Track Page
const ArtistSubmit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showJukeboxModal, setShowJukeboxModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    artist_name: '',
    email: '',
    genre: '',
    album: '',
    description: '',
    isrc: '',
    iswc: '',
    is_cover_song: false,
    licensing_confirmed: false,
    opt_in_jukebox: false,
    agree_to_terms: false,
    agree_to_artist_agreement: false
  });
  const [files, setFiles] = useState({
    mp3_file: null,
    album_art: null,
    preview_clip: null
  });

  if (!user || (user.role !== 'artist' && user.role !== 'admin')) {
    return <Navigate to="/" />;
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files: fileList } = e.target;
    if (fileList && fileList[0]) {
      setFiles(prev => ({
        ...prev,
        [name]: fileList[0]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!files.mp3_file) {
      toast.error('MP3 file is required');
      return;
    }

    if (!files.album_art) {
      toast.error('Album artwork is required');
      return;
    }

    // Check if cover song + Jukebox (not allowed)
    if (formData.is_cover_song && formData.opt_in_jukebox) {
      toast.error('Cover songs cannot be submitted to Jukebox. Please uncheck the Jukebox option or submit an original song.');
      return;
    }

    // Check if cover song and licensing not confirmed
    if (formData.is_cover_song && !formData.licensing_confirmed) {
      toast.error('Please confirm that you have secured all necessary licenses for this cover song');
      return;
    }

    // Check if opted into Jukebox and hasn't agreed to terms
    if (formData.opt_in_jukebox && !formData.agree_to_terms) {
      toast.error('Please agree to the Jukebox terms and conditions');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('artist_name', formData.artist_name);
      submitData.append('genre', formData.genre);
      submitData.append('album', formData.album);
      submitData.append('description', formData.description);
      submitData.append('isrc', formData.isrc);
      submitData.append('iswc', formData.iswc);
      submitData.append('is_cover_song', formData.is_cover_song.toString());
      submitData.append('licensing_confirmed', formData.licensing_confirmed.toString());
      submitData.append('opt_in_jukebox', formData.opt_in_jukebox.toString());
      submitData.append('mp3_file', files.mp3_file);
      
      if (files.album_art) {
        submitData.append('album_art', files.album_art);
      }
      if (files.preview_clip) {
        submitData.append('preview_clip', files.preview_clip);
      }

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/artist/submit-track`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      toast.success('Track submitted successfully! Radio requests will be reviewed in 3-5 days.');
      navigate('/artist');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit track');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="artist-submit-page">
      <div className="page-header">
        <h1>🎵 Submit Track for Radio Play</h1>
        <p>Share your music with AMS Radio listeners</p>
      </div>

      <form onSubmit={handleSubmit} className="submit-form">
        {/* Cover Song Section */}
        <div className="form-section cover-song-section">
          <h3>🎸 Original vs Cover Song</h3>
          
          <div className="cover-song-question">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_cover_song"
                checked={formData.is_cover_song}
                onChange={handleInputChange}
              />
              <span>This is a cover song (not original content)</span>
            </label>
          </div>

          {formData.is_cover_song && (
            <div className="licensing-confirmation">
              <p className="warning-text" style={{color: '#ff6b6b', marginBottom: '1rem'}}>
                ⚠️ <strong>Important:</strong> Cover songs require proper licensing and cannot be submitted to Jukebox.
              </p>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="licensing_confirmed"
                  checked={formData.licensing_confirmed}
                  onChange={handleInputChange}
                  required={formData.is_cover_song}
                />
                <span>
                  <strong>I certify that I have secured all necessary rights and licenses 
                  (including mechanical licenses for cover songs) for the music submitted to AMS Radio</strong>
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Jukebox Opt-in Section */}
        <div className="form-section jukebox-section">
          <h3>📻 Jukebox Availability</h3>
          <p className="section-description">
            Opt your track into Jukebox for listeners to purchase with tokens
            {formData.is_cover_song && (
              <span style={{color: '#ff6b6b', display: 'block', marginTop: '0.5rem'}}>
                ⚠️ Not available for cover songs
              </span>
            )}
          </p>
          
          <button 
            type="button"
            onClick={() => setShowJukeboxModal(true)}
            style={{
              background: 'none',
              border: 'none',
              color: '#667eea',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '1.1rem',
              fontWeight: '700',
              fontStyle: 'italic',
              padding: '0.5rem 0',
              margin: '0.75rem 0 1rem 0',
              display: 'block',
              textAlign: 'left'
            }}
          >
            ℹ️ What is Jukebox? (Click here to learn more)
          </button>
          
          <div className="jukebox-opt-in">
            <input
              type="checkbox"
              id="opt_in_jukebox"
              name="opt_in_jukebox"
              checked={formData.opt_in_jukebox}
              onChange={handleInputChange}
              disabled={formData.is_cover_song}
            />
            <label htmlFor="opt_in_jukebox" style={{opacity: formData.is_cover_song ? 0.5 : 1}}>
              Yes, opt this track into Jukebox
            </label>
          </div>

          {/* Terms Agreement - Only shown if opted in */}
          {formData.opt_in_jukebox && (
            <div className="terms-agreement">
              <div className="terms-checkbox">
                <input
                  type="checkbox"
                  id="agree_to_terms"
                  name="agree_to_terms"
                  checked={formData.agree_to_terms}
                  onChange={handleInputChange}
                  required
                />
                <label htmlFor="agree_to_terms">
                  I have read and understand the Jukebox terms, and I confirm that:
                </label>
              </div>
              <ul className="terms-list">
                <li>✓ I earn $0.25 per token when listeners purchase my song</li>
                <li>✓ I earn $0.02 per star when listeners "show love" to my song (50 stars = $1.00)</li>
                <li>✓ Listeners receive stars with token packs and can star multiple songs</li>
                <li>✓ This is a one-time purchase model (like physical sales)</li>
                <li>✓ Listeners can download and keep the MP3 file</li>
                <li>✓ There is no streaming revenue for repeat listens</li>
                <li>✓ Payouts are processed via Stripe Connect</li>
                <li className="original-content-notice">
                  <strong>✓ This submission is 100% ORIGINAL content - NO COVER SONGS</strong>
                  <br />
                  <small style={{ color: '#ff6b6b', fontWeight: 'normal' }}>
                    Cover songs can be submitted for regular radio play but are NOT eligible for Jukebox due to additional licensing requirements
                  </small>
                </li>
              </ul>
            </div>
          )}
        </div>

        <div className="form-section">
          <h3>Track Information</h3>
          
          <div className="form-group">
            <label>Song Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              placeholder="Enter song title"
            />
          </div>

          <div className="form-group">
            <label>Artist Name *</label>
            <input
              type="text"
              name="artist_name"
              value={formData.artist_name}
              onChange={handleInputChange}
              required
              placeholder="Enter artist name"
            />
          </div>

          <div className="form-group">
            <label>Genre *</label>
            <input
              type="text"
              name="genre"
              value={formData.genre}
              onChange={handleInputChange}
              required
              placeholder="e.g. Rock, Alternative, Indie (separate multiple genres with commas)"
            />
            <small style={{ color: '#888', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem' }}>
              Enter one or more genres separated by commas
            </small>
          </div>

          <div className="form-group">
            <label>Album (Optional)</label>
            <input
              type="text"
              name="album"
              value={formData.album}
              onChange={handleInputChange}
              placeholder="Enter album name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="isrc">ISRC Code (Optional)</label>
            <input
              type="text"
              id="isrc"
              name="isrc"
              value={formData.isrc}
              onChange={handleInputChange}
              placeholder="e.g., USRC17607839"
              data-testid="artist-form-isrc"
            />
            <small style={{ color: '#888', fontSize: '0.9em' }}>International Standard Recording Code - for radio royalty reporting</small>
          </div>

          <div className="form-group">
            <label htmlFor="iswc">ISWC Code (Optional)</label>
            <input
              type="text"
              id="iswc"
              name="iswc"
              value={formData.iswc}
              onChange={handleInputChange}
              placeholder="e.g., T-345246800-1"
              data-testid="artist-form-iswc"
            />
            <small style={{ color: '#888', fontSize: '0.9em' }}>International Standard Musical Work Code - for international publishing</small>
          </div>

          <div className="form-group">
            <label>Description (Optional)</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Tell us about your track..."
              rows="4"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Upload Files</h3>
          
          <div className="form-group">
            <label>MP3 File *</label>
            <input
              type="file"
              name="mp3_file"
              accept="audio/mpeg,audio/mp3"
              onChange={handleFileChange}
              required
            />
            {files.mp3_file && (
              <span className="file-name">✓ {files.mp3_file.name}</span>
            )}
          </div>

          <div className="form-group">
            <label>Album Art (Required)</label>
            <input
              type="file"
              name="album_art"
              accept="image/*"
              onChange={handleFileChange}
              required
            />
            {files.album_art && (
              <span className="file-name">✓ {files.album_art.name}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="preview_clip">30-Second Preview Clip (Optional)</label>
            <input
              type="file"
              id="preview_clip"
              name="preview_clip"
              accept="audio/*"
              onChange={handleFileChange}
            />
            {files.preview_clip && (
              <span className="file-name">✓ {files.preview_clip.name}</span>
            )}
            <small style={{ color: '#888', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem' }}>
              Upload a 30-second clip for Jukebox previews (recommended if opted into Jukebox)
            </small>
          </div>
        </div>

        <div className="disclaimer-section">
          <p className="disclaimer-text">
            <strong>Note:</strong> Tracks in radio rotation are periodically curated and may be removed based on available server space, track age, listener engagement, and playlist diversity needs. Tracks opted into Jukebox will remain available in Jukebox until an artist chooses to remove them (this is managed in Artist Dashboard).
          </p>
          <p className="disclaimer-text" style={{marginTop: '1rem', color: '#ff6b6b'}}>
            <strong>⚠️ AI-Generated Music Policy:</strong> All submissions are reviewed for AI-generated or AI-prompted content. AI-generated music is not accepted for radio play or Jukebox. Submissions found to contain AI-generated content will be rejected.
          </p>
        </div>

        {/* MusoSoup Affiliate Banner */}
        <div style={{ margin: '30px 0', textAlign: 'center' }}>
          <a 
            href="https://app.musosoup.com/submit/AMSradio" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ display: 'inline-block' }}
          >
            <img 
              src="https://audiomiragestudios.wordpress.com/wp-content/uploads/2024/03/image.png" 
              alt="MusoSoup - Submit Your Music"
              style={{ 
                maxWidth: '100%', 
                height: 'auto', 
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'transform 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            />
          </a>
        </div>

        <div className="form-actions">
          <button type="button" className="secondary-btn" onClick={() => navigate('/artist')}>
            Cancel
          </button>
          <button type="submit" className="cta-btn" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Track'}
          </button>
        </div>
      </form>

      {/* What is Jukebox Modal */}
      {showJukeboxModal && (
        <div className="modal-overlay" onClick={() => setShowJukeboxModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📻 What is Jukebox?</h2>
              <button className="modal-close" onClick={() => setShowJukeboxModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <h3>A New Way to Monetize Your Music</h3>
              <p>
                Jukebox is a unique feature that allows listeners to purchase and own your music directly. 
                When you opt-in to Jukebox, your track becomes available for listeners to add to their 
                personal collection using tokens.
              </p>
              
              <h3>How It Works:</h3>
              <ul>
                <li><strong>Token-Based Sales:</strong> Listeners purchase tokens and use them to add your songs to their jukebox</li>
                <li><strong>Direct Earnings:</strong> You earn <strong>$0.25 per token</strong> when someone purchases your song, plus <strong>$0.02 per star</strong> when listeners "like" your track</li>
                <li><strong>Physical Sales Model:</strong> Once a listener buys your track, it belongs to them - just like an MP3 download</li>
                <li><strong>No Streaming Revenue:</strong> There's no revenue from repeat listens - it's a one-time purchase model</li>
                <li><strong>Full Ownership:</strong> Listeners can download and keep the MP3 file permanently</li>
              </ul>

              <h3>Benefits for Artists:</h3>
              <ul>
                <li><strong>3 Ways to Monetize:</strong> Earn from radio play on The Mother Ship, from Stars when listeners bookmark your song for later purchase, and from Tokens when your music is purchased</li>
                <li>Simple, transparent earnings ($0.25/token + $0.02/star)</li>
                <li>Request payouts via Stripe Connect once you reach $2.50 minimum (we cover transaction fees)</li>
                <li>Increased exposure on AMS Radio</li>
              </ul>

              <p className="modal-note">
                <strong>Important:</strong> Opting in to Jukebox is optional. You can choose to submit tracks 
                for radio play only without making them available for purchase.
              </p>
            </div>
            <div className="modal-footer">
              <button className="cta-btn" onClick={() => setShowJukeboxModal(false)}>
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const PublicSubmit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [showJukeboxPrompt, setShowJukeboxPrompt] = useState(false);
  const [showJukeboxModal, setShowJukeboxModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    artist_name: '',
    email: '',
    genre: '',
    album: '',
    description: '',
    isrc: '',
    iswc: '',
    is_cover_song: false,
    licensing_confirmed: false,
    opt_in_jukebox: false,
    opt_in_pirate_ship: false,
    agree_to_terms: false,
    agree_to_artist_agreement: false
  });
  const [files, setFiles] = useState({
    mp3_file: null,
    album_art: null,
    preview_clip: null
  });

  // Restore form data from session storage after login
  useEffect(() => {
    const savedData = sessionStorage.getItem('pending_submission');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      setFormData(parsed);
      sessionStorage.removeItem('pending_submission');
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const { name, files: fileList } = e.target;
    if (fileList && fileList[0]) {
      setFiles(prev => ({
        ...prev,
        [name]: fileList[0]
      }));
    }
  };

  const handleJukeboxChange = (e) => {
    const checked = e.target.checked;
    if (checked && !user) {
      // Save form data and show prompt
      setShowJukeboxPrompt(true);
    } else {
      setFormData(prev => ({ ...prev, opt_in_jukebox: checked }));
    }
  };

  const handleLoginForJukebox = () => {
    // Save form data to session storage
    sessionStorage.setItem('pending_submission', JSON.stringify(formData));
    sessionStorage.setItem('return_to_submit', 'true');
    navigate('/login', { state: { from: '/submit' } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!files.mp3_file) {
      toast.error('MP3 file is required');
      return;
    }

    if (!files.album_art) {
      toast.error('Album artwork is required');
      return;
    }

    // If opted into Jukebox, must be logged in
    if (formData.opt_in_jukebox && !user) {
      setShowJukeboxPrompt(true);
      return;
    }

    // Check if cover song + Jukebox (not allowed)
    if (formData.is_cover_song && formData.opt_in_jukebox) {
      toast.error('Cover songs cannot be submitted to Jukebox. Please uncheck the Jukebox option or submit an original song.');
      return;
    }

    // Check if cover song and licensing not confirmed
    if (formData.is_cover_song && !formData.licensing_confirmed) {
      toast.error('Please confirm that you have secured all necessary licenses for this cover song');
      return;
    }

    // Check if opted into Jukebox and hasn't agreed to terms
    if (formData.opt_in_jukebox && !formData.agree_to_terms) {
      toast.error('Please agree to the Jukebox terms and conditions');
      return;
    }

    // Check if agreed to artist agreement
    if (!formData.agree_to_artist_agreement) {
      toast.error('Please agree to the Artist End User Agreement to submit your track');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('artist_name', formData.artist_name);
      submitData.append('genre', formData.genre);
      submitData.append('album', formData.album);
      submitData.append('description', formData.description);
      submitData.append('isrc', formData.isrc);
      submitData.append('iswc', formData.iswc);
      submitData.append('is_cover_song', formData.is_cover_song.toString());
      submitData.append('licensing_confirmed', formData.licensing_confirmed.toString());
      submitData.append('opt_in_jukebox', formData.opt_in_jukebox.toString());
      submitData.append('opt_in_pirate_ship', formData.opt_in_pirate_ship.toString());
      submitData.append('mp3_file', files.mp3_file);
      
      // Add email for public submissions (required by backend)
      if (formData.email) {
        submitData.append('email', formData.email);
      }
      
      if (files.album_art) {
        submitData.append('album_art', files.album_art);
      }
      if (files.preview_clip) {
        submitData.append('preview_clip', files.preview_clip);
      }

      const endpoint = user ? '/artist/submit-track' : '/public/submit-track';
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'multipart/form-data'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.post(`${API}${endpoint}`, submitData, { headers });

      toast.success('Track submitted successfully! Radio requests will be reviewed in 3-5 days.');
      navigate('/');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit track');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="artist-submit-page">
      <div className="page-header">
        <h1>🎵 Submit Track for Radio Play</h1>
        <p>Share your music with AMS Radio listeners</p>
      </div>

      {showJukeboxPrompt && (
        <div className="modal-overlay" onClick={() => setShowJukeboxPrompt(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Jukebox Requires Account</h3>
            <p>To make your track available in the Jukebox, you'll need to create an account or log in.</p>
            <div className="modal-actions">
              <button onClick={handleLoginForJukebox} className="primary-btn">
                Login / Sign Up
              </button>
              <button onClick={() => {
                setShowJukeboxPrompt(false);
                setFormData(prev => ({ ...prev, opt_in_jukebox: false }));
              }} className="secondary-btn">
                Continue without Jukebox
              </button>
            </div>
          </div>
        </div>
      )}

      {showJukeboxModal && (
        <div className="modal-overlay" onClick={() => setShowJukeboxModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📻 What is Jukebox?</h2>
              <button className="modal-close" onClick={() => setShowJukeboxModal(false)}>×</button>
            </div>

            <div className="jukebox-info">
              <p className="intro-text">
                Jukebox is our artist monetization platform where listeners can purchase your tracks using tokens. Here's how it works:
              </p>

              <div className="info-section">
                <h4>💰 How You Earn</h4>
                <ul>
                  <li><strong>Direct Earnings:</strong> You earn <strong>$0.25 per token</strong> when someone purchases your song</li>
                  <li><strong>Star Love:</strong> You earn <strong>$0.02 per star</strong> when listeners "like" your track (cashable at 50+ stars)</li>
                  <li><strong>Radio Play:</strong> Your track gets submitted for radio rotation on The Mother Ship</li>
                </ul>
              </div>

              <div className="info-section">
                <h4>🎵 Artist Benefits</h4>
                <ul>
                  <li><strong>3 Ways to Monetize:</strong> Earn from radio play, Stars (bookmarks), and Token purchases</li>
                  <li>Control your catalog - add or remove tracks anytime</li>
                  <li>Track your earnings in real-time</li>
                  <li>Cash out via Stripe once you reach $2.50 minimum (we cover fees)</li>
                </ul>
              </div>

              <div className="info-section">
                <h4>📻 How It Works for Listeners</h4>
                <ul>
                  <li>Listeners buy token packages to spend in the Jukebox</li>
                  <li>They browse your catalog and purchase tracks they love</li>
                  <li>Purchased tracks play instantly and get added to their personal jukebox</li>
                  <li>They can also send you stars to show extra support</li>
                </ul>
              </div>
            </div>

            <div className="modal-footer">
              <button className="cta-btn" onClick={() => setShowJukeboxModal(false)}>
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="submit-form">
        <div className="form-section jukebox-section">
          <h3>📻 Jukebox Availability</h3>
          <p className="jukebox-login-message">
            Making your track available in Jukebox requires an account with AMS Radio. If you do not have an account yet, you can <a href="/login" className="jukebox-login-link">login</a> to create an account.
          </p>
        </div>

        <div className="form-section">
          <h3>Track Information</h3>
          <div className="form-group">
            <label htmlFor="title">Track Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="artist_name">Artist Name *</label>
            <input
              type="text"
              id="artist_name"
              name="artist_name"
              value={formData.artist_name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your@email.com"
              required
            />
            <small style={{color: '#888', fontSize: '0.9em'}}>We'll contact you about your submission status</small>
          </div>

          <div className="form-group">
            <label htmlFor="genre">Genre *</label>
            <input
              type="text"
              id="genre"
              name="genre"
              value={formData.genre}
              onChange={handleInputChange}
              placeholder="e.g., Rock, Pop, Hip-Hop"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="album">Album (Optional)</label>
            <input
              type="text"
              id="album"
              name="album"
              value={formData.album}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="isrc">ISRC Code (Optional)</label>
            <input
              type="text"
              id="isrc"
              name="isrc"
              value={formData.isrc}
              onChange={handleInputChange}
              placeholder="e.g., USRC17607839"
            />
            <small style={{color: '#888', fontSize: '0.9em'}}>International Standard Recording Code - for radio royalty reporting</small>
          </div>

          <div className="form-group">
            <label htmlFor="iswc">ISWC Code (Optional)</label>
            <input
              type="text"
              id="iswc"
              name="iswc"
              value={formData.iswc}
              onChange={handleInputChange}
              placeholder="e.g., T-345246800-1"
            />
            <small style={{color: '#888', fontSize: '0.9em'}}>International Standard Musical Work Code - for international publishing</small>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (Optional)</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              placeholder="Tell us about your track..."
            />
          </div>
        </div>

        {/* Cover Song Section */}
        <div className="form-section cover-song-section">
          <h3>🎸 Original vs Cover Song</h3>
          
          <div className="cover-song-question">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_cover_song"
                checked={formData.is_cover_song}
                onChange={handleInputChange}
              />
              <span>This is a cover song (not original content)</span>
            </label>
          </div>

          {formData.is_cover_song && (
            <div className="licensing-confirmation">
              <p className="warning-text" style={{color: '#ff6b6b', marginBottom: '1rem'}}>
                ⚠️ <strong>Important:</strong> Cover songs require proper licensing.
              </p>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="licensing_confirmed"
                  checked={formData.licensing_confirmed}
                  onChange={handleInputChange}
                  required={formData.is_cover_song}
                />
                <span>
                  <strong>I certify that I have secured all necessary rights and licenses 
                  (including mechanical licenses for cover songs) for the music submitted to AMS Radio</strong>
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="form-section">
          <h3>Upload Files</h3>
          
          <div className="form-group">
            <label htmlFor="mp3_file">MP3 File *</label>
            <input
              type="file"
              id="mp3_file"
              name="mp3_file"
              accept=".mp3,audio/mpeg"
              onChange={handleFileChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="album_art">Album Artwork *</label>
            <input
              type="file"
              id="album_art"
              name="album_art"
              accept="image/*"
              onChange={handleFileChange}
              required
            />
          </div>

        </div>

        <div className="disclaimer-section">
          <p className="disclaimer-text">
            <strong>Note:</strong> Tracks in radio rotation are periodically curated and may be removed based on available server space, track age, listener engagement, and playlist diversity needs. Tracks opted into Jukebox will remain available in Jukebox until an artist chooses to remove them (this is managed in Artist Dashboard).
          </p>
          <p className="disclaimer-text" style={{marginTop: '1rem', color: '#ff6b6b'}}>
            <strong>⚠️ AI-Generated Music Policy:</strong> All submissions are reviewed for AI-generated or AI-prompted content. AI-generated music is not accepted for radio play or Jukebox. Submissions found to contain AI-generated content will be rejected.
          </p>
        </div>

        {/* MusoSoup Affiliate Banner */}
        <div style={{ margin: '30px 0', textAlign: 'center' }}>
          <a 
            href="https://app.musosoup.com/submit/AMSradio" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ display: 'inline-block' }}
          >
            <img 
              src="https://audiomiragestudios.wordpress.com/wp-content/uploads/2024/03/image.png" 
              alt="MusoSoup - Submit Your Music"
              style={{ 
                maxWidth: '100%', 
                height: 'auto', 
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'transform 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            />
          </a>
        </div>

        {/* Artist Agreement Checkbox */}
        <div className="agreement-checkbox-section">
          <label className="agreement-checkbox-label">
            <input
              type="checkbox"
              name="agree_to_artist_agreement"
              checked={formData.agree_to_artist_agreement}
              onChange={handleInputChange}
              required
            />
            <span>
              By submitting, I agree to the{' '}
              <button 
                type="button"
                className="agreement-link"
                onClick={() => window.open('/artist-agreement', '_blank')}
              >
                Artist End User Agreement
              </button>
            </span>
          </label>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/')} className="cancel-btn">
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="submit-btn">
            {submitting ? 'Submitting...' : 'Submit Track'}
          </button>
        </div>
      </form>
    </div>
  );
};


// Navigation
const Navigation = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="navbar" data-testid="navbar">
      <div className="nav-brand" onClick={() => navigate('/')} data-testid="nav-brand">
        📻 AMS RADIO
      </div>
      <div className="nav-links">
        <button data-testid="nav-home" onClick={() => navigate('/')}>Listen</button>
        <button onClick={() => navigate('/podcasts')}>Podcasts</button>
        <button onClick={() => navigate('/how-it-works')}>How It Works</button>
        <button onClick={() => navigate(user && (user.role === 'artist' || user.role === 'admin') ? '/artist' : '/submit')}>Submit for Radio Play</button>
        <button onClick={() => window.open('https://audiomiragestudios.wordpress.com', '_blank', 'noopener,noreferrer')}>Blog</button>
        {user && (
          <>
            <button data-testid="nav-songs" onClick={() => navigate('/songs')}>Jukebox</button>
            <button data-testid="nav-jukebox" onClick={() => navigate('/jukebox')}>My Jukebox</button>
            <button data-testid="nav-account" onClick={() => navigate('/account')}>Account</button>
            {(user.role === 'artist' || user.role === 'admin') && (
              <button data-testid="nav-artist" onClick={() => navigate('/artist')}>Artist Dashboard</button>
            )}
            {user.role === 'admin' && (
              <button data-testid="nav-admin" onClick={() => navigate('/admin')}>Admin</button>
            )}
            <button data-testid="nav-logout" onClick={logout}>Logout</button>
          </>
        )}
        {!user && (
          <button data-testid="nav-login" onClick={() => navigate('/login')}>Login</button>
        )}
      </div>
    </nav>
  );
};

// Artist Profile Page
const ArtistProfile = () => {
  const { artistId } = useParams();
  const [artist, setArtist] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starredSongs, setStarredSongs] = useState(new Set());
  const [starringInProgress, setStarringInProgress] = useState(null);
  const [playingSongId, setPlayingSongId] = useState(null);
  const { user, refetchUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchArtistProfile();
    if (user) {
      fetchStarredSongs();
    }
  }, [artistId, user]);

  const fetchArtistProfile = async () => {
    try {
      const response = await axios.get(`${API}/artists/${artistId}`);
      setArtist(response.data.artist);
      setSongs(response.data.songs);
    } catch (error) {
      toast.error('Failed to load artist profile');
    }
    setLoading(false);
  };

  const fetchStarredSongs = async () => {
    try {
      const response = await axios.get(`${API}/user/starred-songs`);
      const starredIds = new Set(response.data.map(song => song.id));
      setStarredSongs(starredIds);
    } catch (error) {
      console.error('Failed to load starred songs');
    }
  };

  const handleStarSong = async (songId) => {
    if (!user) {
      toast.error('Please log in to show love for songs');
      navigate('/login');
      return;
    }

    if (starringInProgress === songId) return;

    setStarringInProgress(songId);
    try {
      const isStarred = starredSongs.has(songId);
      
      if (isStarred) {
        await axios.delete(`${API}/songs/${songId}/star`);
        setStarredSongs(prev => {
          const newSet = new Set(prev);
          newSet.delete(songId);
          return newSet;
        });
        toast.success('Removed from Loved Songs');
      } else {
        const response = await axios.post(`${API}/songs/${songId}/star`);
        setStarredSongs(prev => new Set([...prev, songId]));
        toast.success(`⭐ Loved! (${response.data.stars_remaining} stars remaining)`);
      }
      
      refetchUser();
      await fetchArtistProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to star song');
    } finally {
      setStarringInProgress(null);
    }
  };

  const handlePreviewPlay = (songId, audioElement) => {
    if (playingSongId === songId) {
      audioElement.pause();
      setPlayingSongId(null);
    } else {
      if (playingSongId) {
        const prevAudio = document.getElementById(`audio-artist-${playingSongId}`);
        if (prevAudio) prevAudio.pause();
      }
      
      audioElement.currentTime = 0;
      audioElement.play();
      setPlayingSongId(songId);
      
      const timeCheckInterval = setInterval(() => {
        if (audioElement.currentTime >= 30) {
          audioElement.pause();
          audioElement.currentTime = 0;
          setPlayingSongId(null);
          clearInterval(timeCheckInterval);
        }
      }, 100);
      
      audioElement.addEventListener('pause', () => clearInterval(timeCheckInterval), { once: true });
      audioElement.addEventListener('ended', () => clearInterval(timeCheckInterval), { once: true });
    }
  };

  const handlePurchase = async (songId) => {
    if (!user) {
      toast.error('Please log in to purchase songs');
      navigate('/login');
      return;
    }

    try {
      await axios.post(`${API}/user/purchase-song`, { song_id: songId });
      toast.success('🎵 Song added to your Jukebox!');
      refetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purchase song');
    }
  };

  if (loading) {
    return <div className="loading-spinner">Loading artist profile...</div>;
  }

  if (!artist) {
    return (
      <div className="page-container">
        <div className="error-message">Artist not found</div>
        <button onClick={() => navigate('/songs')} className="back-btn">← Back to Browse</button>
      </div>
    );
  }

  return (
    <div className="page-container artist-profile-page">
      <button onClick={() => navigate('/songs')} className="back-btn">← Back to Browse</button>
      
      {/* Artist Header */}
      <div className="artist-header">
        <div 
          className="artist-avatar-large"
          style={{
            backgroundImage: artist.profile_picture_url ? `url(${artist.profile_picture_url})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {!artist.profile_picture_url && (artist.artist_name?.charAt(0).toUpperCase() || '🎤')}
        </div>
        <div className="artist-info-large">
          <h1>{artist.artist_name}</h1>
          {artist.bio && <p className="artist-bio">{artist.bio}</p>}
          {artist.artist_link && (
            <a href={artist.artist_link} target="_blank" rel="noopener noreferrer" className="artist-link-btn">
              🔗 Find Me Here
            </a>
          )}
        </div>
      </div>

      {/* Songs Section */}
      <div className="artist-songs-section">
        <h2>Songs by {artist.artist_name}</h2>
        {songs.length === 0 ? (
          <p className="no-songs">No songs available yet</p>
        ) : (
          <div className="songs-grid">
            {songs.map(song => (
              <div key={song.id} className="song-card">
                <div className="song-details">
                  <h3>{song.title}</h3>
                  <p className="song-artist">{song.artist}</p>
                  {song.album && <p className="song-album">📀 {song.album}</p>}
                  {song.genre && <span className="genre-badge">{song.genre}</span>}
                  
                  {/* Preview Audio */}
                  {song.preview_url && (
                    <div className="preview-section">
                      <audio 
                        id={`audio-artist-${song.id}`}
                        src={song.preview_url}
                        onEnded={() => setPlayingSongId(null)}
                      />
                      <button
                        className="preview-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          const audio = document.getElementById(`audio-artist-${song.id}`);
                          handlePreviewPlay(song.id, audio);
                        }}
                      >
                        {playingSongId === song.id ? '⏸ Pause Preview' : '▶ Play 30s Preview'}
                      </button>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="song-actions">
                    <div className="star-section">
                      <button
                        className={`star-btn ${starredSongs.has(song.id) ? 'starred' : ''}`}
                        onClick={() => handleStarSong(song.id)}
                        disabled={starringInProgress === song.id}
                        title={starredSongs.has(song.id) ? 'Remove from Loved Songs' : 'Show Love (1 star)'}
                      >
                        {starringInProgress === song.id ? '...' : starredSongs.has(song.id) ? '❤️' : '🤍'}
                      </button>
                    </div>
                    
                    <button 
                      className="purchase-btn" 
                      onClick={() => handlePurchase(song.id)}
                    >
                      🎵 Add to Jukebox ({song.token_cost} token{song.token_cost !== 1 ? 's' : ''})
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Mixtape Viewer Page (Public)
const MixtapeViewer = () => {
  const { code } = useParams();
  const { user, refetchUser } = useAuth();
  const navigate = useNavigate();
  const [mixtape, setMixtape] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playingPreview, setPlayingPreview] = useState(null);

  useEffect(() => {
    fetchMixtape();
  }, [code]);

  const fetchMixtape = async () => {
    try {
      const response = await axios.get(`${API}/mixtapes/${code}`);
      setMixtape(response.data.mixtape);
    } catch (error) {
      toast.error('Mix tape not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSong = async (songId) => {
    if (!user) {
      toast.info('Please log in to add songs to your Jukebox');
      navigate('/login');
      return;
    }

    try {
      await axios.post(`${API}/user/purchase-song`, { song_id: songId });
      toast.success('🎵 Song added to your Jukebox!');
      refetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add song');
    }
  };

  const handlePreviewPlay = (songId, audioElement) => {
    if (playingPreview === songId) {
      audioElement.pause();
      setPlayingPreview(null);
    } else {
      if (playingPreview) {
        const prevAudio = document.getElementById(`preview-${playingPreview}`);
        if (prevAudio) prevAudio.pause();
      }
      
      audioElement.currentTime = 0;
      audioElement.play();
      setPlayingPreview(songId);
      
      // Enforce 30-second limit
      const timeCheckInterval = setInterval(() => {
        if (audioElement.currentTime >= 30) {
          audioElement.pause();
          audioElement.currentTime = 0;
          setPlayingPreview(null);
          clearInterval(timeCheckInterval);
        }
      }, 100);
      
      audioElement.addEventListener('pause', () => clearInterval(timeCheckInterval), { once: true });
      audioElement.addEventListener('ended', () => clearInterval(timeCheckInterval), { once: true });
    }
  };

  if (loading) {
    return <div className="loading-spinner">Loading mix tape...</div>;
  }

  if (!mixtape) {
    return <div className="error-message">Mix tape not found</div>;
  }

  return (
    <div className="mixtape-viewer-page" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1 style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🎵 {mixtape.playlist_name}</h1>
        <p style={{ fontSize: '1.25rem', color: '#9ca3af' }}>
          Mixed by <strong style={{ color: '#8b5cf6' }}>{mixtape.creator_name}</strong>
        </p>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
          {mixtape.view_count} {mixtape.view_count === 1 ? 'view' : 'views'}
        </p>
      </div>

      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#9ca3af' }}>
          🎧 Enjoy 30-second previews • {user ? 'Add songs to your Jukebox' : 'Log in to add songs to your Jukebox'}
        </p>
      </div>

      <div className="songs-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
        {mixtape.songs.map((song) => (
          <div key={song.id} className="song-card" style={{
            background: '#1f2937',
            borderRadius: '12px',
            padding: '1.5rem',
            border: song.available ? '1px solid #374151' : '1px solid #991b1b'
          }}>
            {song.available ? (
              <>
                <h3 style={{ marginBottom: '0.5rem' }}>{song.title}</h3>
                <p style={{ color: '#9ca3af', marginBottom: '1rem' }}>{song.artist}</p>
                
                {(song.preview_url || song.audio_url) && (
                  <div style={{ marginBottom: '1rem' }}>
                    <audio
                      id={`preview-${song.id}`}
                      src={song.preview_url || song.audio_url}
                      preload="none"
                      onEnded={() => setPlayingPreview(null)}
                    />
                    <button
                      onClick={(e) => {
                        const audio = document.getElementById(`preview-${song.id}`);
                        handlePreviewPlay(song.id, audio);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: playingPreview === song.id ? '#dc2626' : '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        marginBottom: '0.5rem'
                      }}
                    >
                      {playingPreview === song.id ? '⏸️ Pause' : '▶️ 30s Preview'}
                    </button>
                  </div>
                )}

                <button
                  onClick={() => handleAddSong(song.id)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  💎 Add to My Jukebox ({song.token_cost} {song.token_cost === 1 ? 'token' : 'tokens'})
                </button>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                <p style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🚫</p>
                <p>No longer available</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {!user && (
        <div style={{ 
          marginTop: '3rem', 
          padding: '2rem', 
          background: 'rgba(139, 92, 246, 0.1)', 
          borderRadius: '12px',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          textAlign: 'center'
        }}>
          <h3 style={{ marginBottom: '1rem' }}>Want to add these songs to your own collection?</h3>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '0.75rem 2rem',
              background: '#8b5cf6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1.1rem'
            }}
          >
            Sign Up / Log In
          </button>
        </div>
      )}
    </div>
  );
};

const Podcasts = () => {
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedShows, setExpandedShows] = useState({});
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const audioRefs = useState({})[0];

  useEffect(() => {
    fetchPodcasts();
  }, []);

  const fetchPodcasts = async () => {
    try {
      const response = await axios.get(`${API}/azurecast/podcasts`);
      if (response.data.success) {
        setPodcasts(response.data.podcasts);
      }
    } catch (error) {
      console.error('Failed to fetch podcasts:', error);
      toast.error('Failed to load podcasts');
    } finally {
      setLoading(false);
    }
  };

  const toggleShow = (showId) => {
    setExpandedShows(prev => ({
      ...prev,
      [showId]: !prev[showId]
    }));
  };

  const handlePlayPause = (episodeId) => {
    if (currentlyPlaying && currentlyPlaying !== episodeId) {
      const currentAudio = audioRefs[currentlyPlaying];
      if (currentAudio) {
        currentAudio.pause();
      }
    }

    const audio = audioRefs[episodeId];
    if (audio) {
      if (currentlyPlaying === episodeId) {
        audio.pause();
        setCurrentlyPlaying(null);
      } else {
        audio.play();
        setCurrentlyPlaying(episodeId);
      }
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="loading-spinner">Loading podcasts...</div>;
  }

  if (podcasts.length === 0) {
    return (
      <div className="podcasts-page">
        <h1>Podcasts</h1>
          <p style={{ textAlign: 'center', color: '#9ca3af', marginTop: '2rem' }}>
          No podcasts available at this time.
        </p>
      </div>
    );
  }

  return (
    <div className="podcasts-page">
      <h1>Podcasts</h1>
      
      <div className="podcast-shows-list">
        {podcasts.map((podcast) => (
          <div key={podcast.id} className="podcast-show-card">
            {/* Collapsed Show Header */}
            <div 
              className="podcast-show-header"
              onClick={() => toggleShow(podcast.id)}
            >
              <div className="podcast-show-icon">
                🎙️
              </div>
              <div className="podcast-show-info">
                <h3 className="podcast-show-title">{podcast.title}</h3>
                <p className="podcast-show-description">
                  {podcast.description?.substring(0, 80)}{podcast.description?.length > 80 ? '...' : ''}
                </p>
                <div className="podcast-show-meta">
                  <span className="episode-count">{podcast.episodes.length} episode{podcast.episodes.length !== 1 ? 's' : ''}</span>
                  {podcast.link && (
                    <a 
                      href={podcast.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="podcast-website-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      🔗 Website
                    </a>
                  )}
                </div>
              </div>
              <div className={`podcast-expand-icon ${expandedShows[podcast.id] ? 'expanded' : ''}`}>
                ▼
              </div>
            </div>

            {/* Expanded Episodes List */}
            {expandedShows[podcast.id] && (
              <div className="podcast-episodes-list">
                {podcast.episodes.map((episode) => (
                  <div key={episode.id} className="podcast-episode-item">
                    <div className="episode-info">
                      <h4 className="episode-title">{episode.title}</h4>
                      <p className="episode-meta">
                        {formatDate(episode.publish_at)} • {episode.media?.length_text || ''}
                      </p>
                    </div>
                    <div className="episode-controls">
                      <button 
                        className="episode-play-btn"
                        onClick={() => handlePlayPause(episode.id)}
                      >
                        {currentlyPlaying === episode.id ? '⏸️' : '▶️'}
                      </button>
                      <audio
                        ref={(el) => {
                          if (el) audioRefs[episode.id] = el;
                        }}
                        src={episode.links?.download}
                        onEnded={() => setCurrentlyPlaying(null)}
                        onPause={() => {
                          if (currentlyPlaying === episode.id) setCurrentlyPlaying(null);
                        }}
                        onPlay={() => setCurrentlyPlaying(episode.id)}
                        preload="none"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};



const VerificationBanner = () => {
  const { user } = useAuth();
  const [isResending, setIsResending] = useState(false);

  // Don't show banner if user is not logged in or already verified
  if (!user || user.is_verified) {
    return null;
  }

  const handleResendEmail = async () => {
    setIsResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
      color: '#1f2937',
      padding: '1rem',
      textAlign: 'center',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
    }}>
      <p style={{ margin: 0, fontWeight: 600 }}>
        ⚠️ Please verify your email address to access all features.
        <button
          onClick={handleResendEmail}
          disabled={isResending}
          style={{
            marginLeft: '1rem',
            padding: '0.4rem 1rem',
            background: '#ffffff',
            border: 'none',
            borderRadius: '6px',
            fontWeight: 600,
            cursor: isResending ? 'not-allowed' : 'pointer',
            opacity: isResending ? 0.7 : 1
          }}
        >
          {isResending ? 'Sending...' : 'Resend Email'}
        </button>
      </p>
      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', opacity: 0.9 }}>
        Check your spam folder if you don't see the email. Having issues? {' '}
        <a 
          href="https://audiomiragestudios.wordpress.com/contact-us/" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#1f2937', fontWeight: 600, textDecoration: 'underline' }}
        >
          Contact us
        </a>
      </p>
    </div>
  );
};

// Artist Agreement Page
const ArtistAgreement = () => {
  const navigate = useNavigate();
  
  return (
    <div className="artist-agreement-page" data-testid="artist-agreement-page">
      <div className="agreement-container">
        <div className="agreement-header">
          <h1>Artist End User Agreement</h1>
          <p className="effective-date">Effective Date: January 1, 2026</p>
        </div>

        <div className="agreement-content">
          <section className="agreement-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to AMS Radio. This Artist End User Agreement ("Agreement") establishes the terms and conditions 
              governing your use of our platform and services. By submitting music to AMS Radio, you ("Artist" or "you") 
              agree to be bound by this Agreement.
            </p>
            <p>
              AMS Radio is an independent internet radio platform dedicated to showcasing independent artists and providing 
              listeners with curated music experiences. We operate multiple channels, each with distinct purposes and 
              submission requirements.
            </p>
          </section>

          <section className="agreement-section">
            <h2>2. AMS Radio Channels</h2>
            
            <div className="channel-info">
              <h3>2.1 The Mother Ship</h3>
              <p>
                Our flagship channel featuring a diverse rotation of independent music. Tracks submitted for radio play 
                are reviewed and, if approved, added to rotation on The Mother Ship. Radio play submissions are periodically 
                curated based on available server space, track age, listener engagement, and playlist diversity needs.
              </p>
            </div>

            <div className="channel-info">
              <h3>2.2 Third-Party Partner Channels</h3>
              <p>
                AMS Radio may feature additional channels operated by third-party partners. 
                These channels maintain their own submission processes, guidelines, and agreements separate from AMS Radio. 
                Submitting music to AMS Radio does <strong>not</strong> automatically include your music on partner channels. 
                Artists interested in third-party channels must submit directly to those platforms according to their 
                respective requirements.
              </p>
            </div>
          </section>

          <section className="agreement-section">
            <h2>3. Jukebox Program</h2>
            <p>
              Jukebox is our artist monetization platform. Participation is <strong>entirely optional</strong>—you may 
              submit tracks for radio play only without participating in Jukebox.
            </p>
            
            <div className="subsection">
              <h3>3.1 How Jukebox Works</h3>
              <ul>
                <li><strong>Token-Based Sales:</strong> Listeners purchase tokens and use them to add your songs to their personal jukebox collection.</li>
                <li><strong>Direct Earnings:</strong> You earn <strong>$0.25 per token</strong> when someone purchases your song, plus <strong>$0.02 per star</strong> when listeners "show love" to your track.</li>
                <li><strong>Physical Sales Model:</strong> Once a listener purchases your track, it belongs to them—similar to an MP3 download. Listeners can download and keep the file permanently.</li>
                <li><strong>One-Time Purchase:</strong> There is no streaming revenue for repeat listens. This is a one-time purchase model.</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>3.2 Artist Earnings</h3>
              <ul>
                <li>Earn from radio play on The Mother Ship</li>
                <li>Earn from Stars when listeners bookmark your song for later purchase</li>
                <li>Earn from Tokens when your music is purchased</li>
                <li>Simple, transparent earnings: $0.25/token + $0.02/star</li>
                <li>Request payouts via Stripe Connect once you reach a $2.50 minimum balance (AMS Radio covers all transaction fees)</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>3.3 Jukebox Eligibility</h3>
              <p>
                <strong>Only original content is eligible for Jukebox.</strong> Cover songs may be submitted for regular 
                radio play but are NOT eligible for Jukebox due to additional licensing requirements. By opting a track 
                into Jukebox, you certify that it is 100% original content.
              </p>
            </div>
          </section>

          <section className="agreement-section">
            <h2>4. Artist Rights and Control</h2>
            <p>
              We believe artists should maintain control over their work. Under this Agreement:
            </p>
            <ul>
              <li><strong>You retain full ownership</strong> of your music at all times. Submitting to AMS Radio does not transfer any ownership rights.</li>
              <li><strong>You control your Jukebox catalog.</strong> You may add or remove tracks from Jukebox at any time through your Artist Dashboard.</li>
              <li><strong>You may request removal.</strong> You may request that any of your tracks be removed from radio rotation entirely by contacting us or managing through your Artist Dashboard.</li>
              <li><strong>You manage your catalog as you see fit.</strong> All participation in monetization features is voluntary and can be adjusted at any time.</li>
            </ul>
          </section>

          <section className="agreement-section">
            <h2>5. Artist Responsibilities</h2>
            <p>By submitting music to AMS Radio, you represent and warrant that:</p>
            <ul>
              <li><strong>Ownership and Rights:</strong> You own or have secured all necessary rights, licenses, permissions, and copyrights for the music you submit. This includes performance rights, mechanical rights, and any other applicable rights.</li>
              <li><strong>Cover Songs:</strong> If submitting a cover song, you have obtained all required licenses (including mechanical licenses) and understand that cover songs are not eligible for Jukebox.</li>
              <li><strong>Original Content:</strong> Tracks opted into Jukebox are 100% original content created by you.</li>
              <li><strong>Accurate Information:</strong> All metadata and information provided with your submission is accurate and complete.</li>
              <li><strong>No AI-Generated Content:</strong> Your submission does not contain AI-generated or AI-prompted music. All submissions are reviewed for AI content, and AI-generated music will be rejected.</li>
              <li><strong>Legal Compliance:</strong> Your submission complies with all applicable laws and does not infringe on any third-party rights.</li>
            </ul>
          </section>

          <section className="agreement-section">
            <h2>6. Platform Rights</h2>
            <p>AMS Radio reserves the right to:</p>
            <ul>
              <li>Review, approve, or reject any submission at our sole discretion.</li>
              <li>Remove tracks from rotation based on content curation needs, server capacity, or policy violations.</li>
              <li>Suspend or terminate access for violations of this Agreement.</li>
              <li>Modify the terms of this Agreement with reasonable notice to artists.</li>
              <li>Add new channels, features, or services, which may have separate terms and conditions.</li>
            </ul>
          </section>

          <section className="agreement-section">
            <h2>7. Content Guidelines</h2>
            <ul>
              <li>Submissions must not contain illegal content or promote illegal activities.</li>
              <li>Submissions must not contain hate speech, explicit violence, or discriminatory content.</li>
              <li>AI-generated music is not accepted for radio play or Jukebox.</li>
              <li>All content must comply with applicable copyright and intellectual property laws.</li>
            </ul>
          </section>

          <section className="agreement-section">
            <h2>8. Limitation of Liability</h2>
            <p>
              AMS Radio provides its services "as is" and makes no warranties regarding uninterrupted service or 
              guaranteed exposure. We are not liable for any indirect, incidental, or consequential damages arising 
              from your use of our platform.
            </p>
          </section>

          <section className="agreement-section">
            <h2>9. Contact Information</h2>
            <p>
              For questions about this Agreement, removal requests, or other inquiries, please contact us at:{' '}
              <a href="https://audiomiragestudios.wordpress.com/contact-us/" target="_blank" rel="noopener noreferrer">
                audiomiragestudios.wordpress.com/contact-us
              </a>
            </p>
          </section>

          <section className="agreement-section agreement-footer-note">
            <p>
              By submitting music to AMS Radio, you acknowledge that you have read, understood, and agree to be bound 
              by this Artist End User Agreement.
            </p>
          </section>
        </div>

        <div className="agreement-actions">
          <button className="cta-btn" onClick={() => navigate(-1)}>
            ← Return to Previous Page
          </button>
          <button className="secondary-btn" onClick={() => navigate('/submit')}>
            Submit Music
          </button>
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicy = () => {
  const navigate = useNavigate();
  
  return (
    <div className="artist-agreement-page" data-testid="privacy-policy-page">
      <div className="agreement-container">
        <div className="agreement-header">
          <h1>Privacy Policy</h1>
          <p className="effective-date">Effective Date: January 1, 2026</p>
        </div>

        <div className="agreement-content">
          <section className="agreement-section">
            <h2>1. Introduction</h2>
            <p>
              AMS Radio ("we," "us," or "our") respects your privacy and is committed to protecting your personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
              platform and services.
            </p>
            <p>
              By using AMS Radio, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section className="agreement-section">
            <h2>2. Information We Collect</h2>
            
            <div className="subsection">
              <h3>2.1 Account Information</h3>
              <p>When you create an account, we collect:</p>
              <ul>
                <li>Email address</li>
                <li>Password (stored securely using industry-standard encryption)</li>
                <li>Username/Listener Tag (optional display name)</li>
                <li>Account type (listener, artist, or admin)</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>2.2 Artist Information</h3>
              <p>If you register as an artist, we additionally collect:</p>
              <ul>
                <li>Artist/band name</li>
                <li>Biography and profile information</li>
                <li>Social media links (optional)</li>
                <li>Payment information via Stripe Connect for payouts</li>
                <li>Submitted music files and associated metadata</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>2.3 Listening & Activity Data</h3>
              <p>To provide our services and loyalty program, we collect:</p>
              <ul>
                <li>Listening time and channel preferences</li>
                <li>Songs you've starred or purchased</li>
                <li>Feedback survey responses (ratings, comments, genre preferences)</li>
                <li>Loyalty points and tier status</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>2.4 Transaction Data</h3>
              <p>When you make purchases, we collect:</p>
              <ul>
                <li>Token and song purchase history</li>
                <li>Payment method details (processed securely by Stripe)</li>
                <li>Transaction timestamps and amounts</li>
              </ul>
            </div>

            <div className="subsection">
              <h3>2.5 Technical Data</h3>
              <p>We automatically collect certain technical information:</p>
              <ul>
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Access times and dates</li>
              </ul>
            </div>
          </section>

          <section className="agreement-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li><strong>Provide our services:</strong> Stream radio, process purchases, manage your jukebox collection</li>
              <li><strong>Process payments:</strong> Handle token purchases and artist payouts</li>
              <li><strong>Operate the loyalty program:</strong> Track listening time, award points, determine tier status</li>
              <li><strong>Communicate with you:</strong> Send account notifications, submission updates, and service announcements</li>
              <li><strong>Improve our platform:</strong> Analyze usage patterns to enhance user experience</li>
              <li><strong>Ensure security:</strong> Detect and prevent fraud or unauthorized access</li>
              <li><strong>Comply with legal obligations:</strong> Meet regulatory and legal requirements</li>
            </ul>
          </section>

          <section className="agreement-section">
            <h2>4. Third-Party Services</h2>
            <p>
              We work with trusted third-party services to operate our platform. These services have access to 
              your information only as necessary to perform their functions:
            </p>
            
            <div className="channel-info">
              <h3>Payment Processing</h3>
              <p>
                <strong>Stripe:</strong> Handles all payment processing and artist payouts. Stripe's use of your 
                information is governed by their{' '}
                <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </p>
            </div>

            <div className="channel-info">
              <h3>Email Services</h3>
              <p>
                <strong>Resend:</strong> Sends transactional emails (account verification, submission updates, password resets).
              </p>
            </div>

            <div className="channel-info">
              <h3>Streaming Services</h3>
              <p>
                <strong>Live365 & AzuraCast:</strong> Power our radio streaming. These services may collect their own 
                analytics data when you listen to streams.
              </p>
            </div>

            <div className="channel-info">
              <h3>File Storage</h3>
              <p>
                <strong>Backblaze B2:</strong> Securely stores uploaded music files and artwork.
              </p>
            </div>
          </section>

          <section className="agreement-section">
            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information, including:
            </p>
            <ul>
              <li>Encryption of passwords and sensitive data</li>
              <li>Secure HTTPS connections for all data transmission</li>
              <li>Regular security assessments</li>
              <li>Limited access to personal data on a need-to-know basis</li>
            </ul>
            <p>
              While we strive to protect your information, no method of transmission over the internet is 100% secure. 
              We cannot guarantee absolute security.
            </p>
          </section>

          <section className="agreement-section">
            <h2>6. Data Retention</h2>
            <p>We retain your information for as long as:</p>
            <ul>
              <li>Your account is active</li>
              <li>Needed to provide you with our services</li>
              <li>Required to comply with legal obligations</li>
              <li>Necessary to resolve disputes and enforce agreements</li>
            </ul>
            <p>
              If you delete your account, we will remove your personal information within a reasonable timeframe, 
              except where retention is required by law or for legitimate business purposes.
            </p>
          </section>

          <section className="agreement-section">
            <h2>7. Your Rights</h2>
            <p>Depending on your location, you may have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your account and personal data</li>
              <li><strong>Portability:</strong> Request your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from non-essential communications</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us using the information provided below.
            </p>
          </section>

          <section className="agreement-section">
            <h2>8. Cookies</h2>
            <p>
              We use essential cookies and local storage to maintain your login session and remember your preferences. 
              We do not use tracking cookies for advertising purposes.
            </p>
          </section>

          <section className="agreement-section">
            <h2>9. Children's Privacy</h2>
            <p>
              AMS Radio is not intended for children under 13 years of age. We do not knowingly collect personal 
              information from children under 13. If you believe we have collected information from a child under 13, 
              please contact us immediately.
            </p>
          </section>

          <section className="agreement-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by 
              posting the new policy on this page and updating the "Effective Date" above. Your continued use of 
              AMS Radio after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="agreement-section">
            <h2>11. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us at:{' '}
              <a href="https://audiomiragestudios.wordpress.com/contact-us/" target="_blank" rel="noopener noreferrer">
                audiomiragestudios.wordpress.com/contact-us
              </a>
            </p>
          </section>

          <section className="agreement-section agreement-footer-note">
            <p>
              By using AMS Radio, you acknowledge that you have read and understood this Privacy Policy.
            </p>
          </section>
        </div>

        <div className="agreement-actions">
          <button className="cta-btn" onClick={() => navigate(-1)}>
            ← Return to Previous Page
          </button>
        </div>
      </div>
    </div>
  );
};

// Site Footer
const SiteFooter = () => {
  const navigate = useNavigate();
  
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="footer-content">
        <span className="copyright">© 2026 AMS Radio</span>
        <div className="footer-links">
          <button onClick={() => navigate('/artist-agreement')} className="footer-link">
            Artist Agreement
          </button>
          <span className="footer-divider">•</span>
          <button onClick={() => navigate('/privacy-policy')} className="footer-link">
            Privacy Policy
          </button>
          <span className="footer-divider">•</span>
          <button 
            onClick={() => window.open('https://audiomiragestudios.wordpress.com/contact-us/', '_blank')}
            className="footer-link"
          >
            Contact Us
          </button>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Navigation />
          <VerificationBanner />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<ChannelPage />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/submit" element={<PublicSubmit />} />
              <Route path="/channel/:channelId" element={<ChannelPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/verify-email" element={<EmailVerification />} />
              <Route path="/songs" element={<Songs />} />
              <Route path="/artist/:artistId" element={<ArtistProfile />} />
              <Route path="/jukebox" element={<ProtectedRoute><Jukebox /></ProtectedRoute>} />
              <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
              <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
              <Route path="/artist" element={<ProtectedRoute><ArtistDashboard /></ProtectedRoute>} />
              <Route path="/artist/submit" element={<ProtectedRoute><ArtistSubmit /></ProtectedRoute>} />
              <Route path="/schedule" element={<Schedule />} />
              <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminPanel /></ProtectedRoute>} />
              <Route path="/admin/google-setup" element={<ProtectedRoute requireAdmin><GoogleSetup /></ProtectedRoute>} />
              <Route path="/podcasts" element={<Podcasts />} />
              <Route path="/mixtape/:code" element={<MixtapeViewer />} />
              <Route path="/artist-agreement" element={<ArtistAgreement />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
          <SiteFooter />
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;